import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@components/ui/Card";
import Button from "@components/ui/Button";
import { CheckCircle, CheckCircle2, Camera, Palette, Package, ArrowLeft, ArrowRight, Lock, Clock, ChevronDown, ChevronUp, Eye, Sparkles, BookOpen, X, Gift } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@contexts/AuthContext";

// Free trial module - accessible without purchase
const FREE_TRIAL_MODULE_ID = "natural-light";

// Photography Fundamentals Training Program - Love & Photos

// Helper to parse **bold**, *italic*, ~gray~ text, __underline__, and [links](url) in content
const parseBoldText = (text) => {
  if (!text) return text;

  // First handle markdown links [text](url)
  const linkRegex = /(\[[^\]]+\]\([^)]+\))/g;
  const linkParts = text.split(linkRegex);

  return linkParts.map((linkPart, linkIndex) => {
    // Check if this is a link
    const linkMatch = linkPart.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={`link-${linkIndex}`}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
        >
          {linkMatch[1]}
        </a>
      );
    }

    // Handle larger text (##text##)
    const largerParts = linkPart.split(/(##[^#]+##)/g);
    return largerParts.map((largerPart, largerIndex) => {
      if (largerPart.startsWith('##') && largerPart.endsWith('##')) {
        return <span key={`larger-${linkIndex}-${largerIndex}`} className="text-xl font-semibold">{largerPart.slice(2, -2)}</span>;
      }

      // Handle maroon text ({{text}})
      const maroonParts = largerPart.split(/(\{\{[^}]+\}\})/g);
      return maroonParts.map((maroonPart, maroonIndex) => {
        if (maroonPart.startsWith('{{') && maroonPart.endsWith('}}')) {
          return <span key={`maroon-${linkIndex}-${largerIndex}-${maroonIndex}`} style={{ color: '#FF7F50' }} className="font-semibold">{maroonPart.slice(2, -2)}</span>;
        }

        // Handle gray text (~text~)
        const grayParts = maroonPart.split(/(~[^~]+~)/g);
        return grayParts.map((grayPart, grayIndex) => {
          if (grayPart.startsWith('~') && grayPart.endsWith('~')) {
            return <span key={`gray-${linkIndex}-${largerIndex}-${maroonIndex}-${grayIndex}`} className="text-gray-400">{grayPart.slice(1, -1)}</span>;
          }

      // Helper to parse bold and italic
      const parseBoldItalic = (text, keyPrefix) => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, index) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={`${keyPrefix}-${index}`}>{part.slice(2, -2)}</strong>;
          }
          // Then handle italics (*text*) - but not ** which is bold
          const italicParts = part.split(/(\*[^*]+\*)/g);
          return italicParts.map((iPart, iIndex) => {
            if (iPart.startsWith('*') && iPart.endsWith('*') && !iPart.startsWith('**')) {
              return <em key={`${keyPrefix}-${index}-${iIndex}`}>{iPart.slice(1, -1)}</em>;
            }
            return iPart;
          });
        });
      };

      // Handle underline (__text__)
      const underlineParts = grayPart.split(/(__[^_]+__)/g);
      return underlineParts.map((uPart, uIndex) => {
        if (uPart.startsWith('__') && uPart.endsWith('__')) {
          const innerContent = uPart.slice(2, -2);
          return <span key={`underline-${linkIndex}-${grayIndex}-${uIndex}`} className="underline">{parseBoldItalic(innerContent, `underline-inner-${linkIndex}-${grayIndex}-${uIndex}`)}</span>;
        }

        // Handle bold (**text**)
        return parseBoldItalic(uPart, `${linkIndex}-${largerIndex}-${maroonIndex}-${grayIndex}-${uIndex}`);
      });
    });
    });
    });
  });
};

// Confetti celebration component
const Confetti = ({ show }) => {
  const particles = useMemo(() =>
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 1 + Math.random() * 1,
      color: ['#fe395f', '#ff6b89', '#ffd700', '#ff69b4', '#00bcd4'][Math.floor(Math.random() * 5)],
      size: 4 + Math.random() * 8,
    })),
  []);

  if (!show) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{
            x: `${p.x}vw`,
            y: -20,
            rotate: 0,
            opacity: 1
          }}
          animate={{
            y: '100vh',
            rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
            opacity: 0
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'linear'
          }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          }}
        />
      ))}
    </div>
  );
};

// Lightbox component for viewing images full screen
const Lightbox = ({ images, currentIndex, onIndexChange, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onIndexChange(currentIndex - 1);
      }
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        onIndexChange(currentIndex + 1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, currentIndex, images.length, onIndexChange]);

  if (!images || images.length === 0 || currentIndex < 0) return null;

  const currentImage = images[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-8"
      onClick={onClose}
    >
      {/* Close button - always visible with dark background */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white/90 hover:text-white hover:bg-black/70 transition-colors z-20"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full text-white/90 text-sm z-20">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Previous button */}
      {hasPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(currentIndex - 1);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white/90 hover:text-white hover:bg-black/70 transition-colors z-20"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      )}

      {/* Next button */}
      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(currentIndex + 1);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white/90 hover:text-white hover:bg-black/70 transition-colors z-20"
        >
          <ArrowRight className="w-6 h-6" />
        </button>
      )}

      {/* Image container - limited size to ensure X button visibility */}
      <motion.img
        key={currentImage}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        src={currentImage}
        alt=""
        className="max-w-[85vw] max-h-[85vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  );
};

// Interactive Settings Card - reveals camera settings on click (only if settings exist)
const SettingsCard = ({ image }) => {
  const [revealed, setRevealed] = useState(false);

  if (!image?.src) return null;

  // Check if image has actual settings data
  const hasSettings = image.settings?.iso && image.settings?.aperture && image.settings?.shutter;

  // If no settings, render as simple image
  if (!hasSettings) {
    return (
      <div className="rounded-xl overflow-hidden shadow-lg">
        <img
          src={image.src}
          alt={image.alt || ""}
          className="w-full"
          loading="lazy"
        />
        {image.settingsCaption && (
          <div className="bg-gray-50 px-3 py-2 text-center">
            <p className="text-sm text-[#606b7c]">{image.settingsCaption}</p>
          </div>
        )}
      </div>
    );
  }

  // Interactive version with settings reveal
  return (
    <motion.div
      className="relative rounded-xl overflow-hidden shadow-lg cursor-pointer group"
      onClick={() => setRevealed(!revealed)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <img
        src={image.src}
        alt={image.alt || ""}
        className="w-full"
        loading="lazy"
      />

      {/* Tap to reveal hint */}
      {!revealed && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-4">
          <div className="flex items-center gap-2 text-white text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full">
            <Eye className="w-4 h-4" />
            Tap to reveal settings
          </div>
        </div>
      )}

      {/* Settings overlay */}
      <AnimatePresence>
        {revealed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center p-4"
          >
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="text-white/60 text-xs uppercase tracking-wide">ISO</div>
                <div className="text-white text-xl font-bold">{image.settings.iso}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="text-white/60 text-xs uppercase tracking-wide">Aperture</div>
                <div className="text-white text-xl font-bold">{image.settings.aperture}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="text-white/60 text-xs uppercase tracking-wide">Shutter</div>
                <div className="text-white text-xl font-bold">{image.settings.shutter}</div>
              </div>
            </div>
            {image.settingsCaption && (
              <p className="text-white/80 text-sm mt-4 text-center">{image.settingsCaption}</p>
            )}
            <p className="text-white/50 text-xs mt-3">Tap to hide</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Section Card - collapsible section with checkpoint, sequential unlock, and transitions
const SectionCard = ({ section, isCompleted, onComplete, isExpanded, onToggle, isLocked, isCurrent, stepNumber, totalSteps, showTransition, onTransitionDismiss, onImageClick, proTip }) => {
  return (
    <div className="space-y-3">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border rounded-xl overflow-hidden transition-all ${
          isLocked
            ? "border-gray-200 bg-gray-50 opacity-60"
            : isCompleted
              ? "border-black bg-white"
              : isCurrent
                ? "border-black bg-white shadow-lg"
                : "border-black bg-white"
        }`}
      >
        {/* Section Header - clickable to expand/collapse */}
        <button
          onClick={isLocked ? undefined : onToggle}
          disabled={isLocked}
          className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
            isLocked ? "cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              isLocked
                ? "bg-gray-200 text-gray-400"
                : isCompleted
                  ? "bg-[#fe395f] text-white"
                  : isCurrent
                    ? "bg-[#fe395f]/20 text-[#fe395f] ring-2 ring-[#fe395f]"
                    : "border-2 border-gray-300 text-gray-500"
            }`}>
              {isLocked ? (
                <Lock className="w-4 h-4" />
              ) : isCompleted ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                stepNumber
              )}
            </div>
            <div>
              <h3 className={`font-semibold ${
                isLocked
                  ? "text-gray-400"
                  : isCompleted
                    ? "text-[#fe395f]"
                    : isCurrent
                      ? "text-gray-900"
                      : "text-gray-700"
              }`}>
                {parseBoldText(section.title)}
              </h3>
              {isCurrent && !isCompleted && (
                <span className="text-xs text-[#fe395f] font-medium">Current step</span>
              )}
            </div>
          </div>
          {!isLocked && (
            isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )
          )}
        </button>

        {/* Section Content */}
        <AnimatePresence>
          {isExpanded && !isLocked && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className={`px-4 pb-4 pt-2 border-t border-gray-100 ${section.darkBackground ? 'bg-[#2a2a2a] rounded-b-xl' : ''}`}>
                {/* Top callout - renders before everything */}
                {section.topCallout && (
                  <div className={`rounded-xl py-4 px-5 mb-4`} style={{
                    backgroundColor: section.topCallout.borderOnly ? 'transparent' : (section.topCallout.backgroundColor || '#4a4a4a'),
                    border: section.topCallout.borderOnly ? `2px solid ${section.topCallout.backgroundColor || '#4a4a4a'}` : 'none'
                  }}>
                    <div className="flex items-start gap-3">
                      {section.topCallout.icon && (
                        <span className={`text-xl ${section.topCallout.borderOnly ? '' : 'text-white'}`} style={section.topCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                          {section.topCallout.icon}
                        </span>
                      )}
                      <div className="flex-1">
                        {section.topCallout.title && (
                          <p className={`font-semibold mb-2 ${section.topCallout.borderOnly ? '' : 'text-white'}`} style={section.topCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.topCallout.title}
                          </p>
                        )}
                        {section.topCallout.textLines && (
                          <div className={`space-y-1 ${section.topCallout.borderOnly ? '' : 'text-white'}`} style={section.topCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.topCallout.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content above grid - for imagesFirst sections */}
                {section.imagesFirst && section.contentAboveGrid && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAboveGrid.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Gallery images FIRST if imagesFirst is true */}
                {section.imagesFirst && section.galleryImages && section.galleryImages.length > 0 && (
                  <div className={`grid gap-4 mb-4 ${
                    section.galleryImages.some(img => img.span) ? 'md:grid-cols-6' :
                    section.galleryColumns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                  }`}>
                    {section.galleryImages.map((img, idx) => (
                      <div key={idx} className={`${img.stackWith ? "space-y-4" : ""} ${
                        img.span === 6 ? 'md:col-span-6' :
                        img.span === 3 ? 'md:col-span-3' :
                        img.span === 2 ? 'md:col-span-2' : ''
                      } ${
                        img.colStart === 4 ? 'md:col-start-4' : ''
                      }`}>
                        <div>
                          <img
                            src={img.src}
                            alt={img.alt || ""}
                            className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy"
                            onClick={() => onImageClick?.(img.src)}
                          />
                          {img.caption && (
                            <div className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                              {img.caption.split('\n\n').map((paragraph, pIdx) => (
                                <p key={pIdx} className="mb-2 last:mb-0">
                                  {paragraph.split('\n').map((line, idx) => (
                                    <span key={idx}>
                                      {parseBoldText(line)}
                                      {idx < paragraph.split('\n').length - 1 && <br />}
                                    </span>
                                  ))}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        {img.stackWith && (
                          <div className="w-full">
                            <img
                              src={img.stackWith.src}
                              alt={img.stackWith.alt || ""}
                              className={`w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity ${img.stackWith.aspectRatio ? 'object-cover' : ''}`}
                              style={img.stackWith.aspectRatio ? { aspectRatio: img.stackWith.aspectRatio } : {}}
                              loading="lazy"
                              onClick={() => onImageClick?.(img.stackWith.src)}
                            />
                            {img.stackWith.caption && (
                              <p className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                                {parseBoldText(img.stackWith.caption)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Callout box - render FIRST if there's sideByeSideImage */}
                {section.callout && section.sideByeSideImage?.src && (
                  <div className={`rounded-xl py-4 px-5 ${section.callout.smallMargin ? 'mb-2' : 'mb-4'}`} style={{
                    backgroundColor: section.callout.color || '#4a4a4a'
                  }}>
                    <div className="flex items-start gap-3">
                      <span className="text-white text-xl">{section.callout.icon || '✨'}</span>
                      <div className="flex-1">
                        {section.callout.title && (
                          <p className="text-white font-semibold mb-2">{section.callout.title}</p>
                        )}
                        {section.callout.textLines && (
                          <div className="text-white space-y-2">
                            {section.callout.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Side-by-side layout: image left, content right */}
                {section.sideByeSideImage?.src && (
                  <div className="grid md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <img
                        src={section.sideByeSideImage.src}
                        alt={section.sideByeSideImage.alt || ""}
                        className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        onClick={() => onImageClick?.(section.sideByeSideImage.src)}
                      />
                    </div>
                    <div className={`leading-normal ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                      {section.content.split('\n\n').map((paragraph, pIdx) => (
                        <p key={pIdx} className="mb-3 last:mb-0">
                          {paragraph.split('\n').map((line, idx) => (
                            <span key={idx}>
                              {parseBoldText(line)}
                              {idx < paragraph.split('\n').length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Callout box - render FIRST if there's contentLeftImageRight */}
                {section.callout && section.contentLeftImageRight?.src && (
                  <div className={`rounded-xl py-4 px-5 ${section.callout.smallMargin ? 'mb-2' : 'mb-4'}`} style={{
                    ...(section.callout.borderOnly ? {
                      backgroundColor: 'transparent',
                      border: `2px solid ${section.callout.color || '#4a4a4a'}`
                    } : {
                      backgroundColor: section.callout.color
                        ? section.callout.color
                        : section.callout.title?.toLowerCase().includes('dynamic') || section.callout.title?.toLowerCase().includes('trick')
                          ? '#5c3d3d'
                          : section.callout.title?.toLowerCase().includes('tip')
                            ? '#4a3f3f'
                            : '#4a4a4a'
                    })
                  }}>
                    <div className="flex items-start gap-3">
                      <span className="text-white text-xl">
                        {section.callout.icon
                          ? section.callout.icon
                          : section.callout.title?.toLowerCase().includes('dynamic') || section.callout.title?.toLowerCase().includes('trick')
                            ? '⚡'
                            : section.callout.title?.toLowerCase().includes('tip')
                              ? '💡'
                              : '✨'}
                      </span>
                      <div className="flex-1">
                        {section.callout.title && (
                          <p className="text-white font-semibold mb-2">{section.callout.title}</p>
                        )}
                        {section.callout.textLines && (
                          <div className="text-white space-y-2">
                            {section.callout.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content after callout (when using contentLeftImageRight) */}
                {section.content && section.contentLeftImageRight?.src && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.content.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Side-by-side layout: content left, image right */}
                {section.contentLeftImageRight?.src && (
                  <div className="grid md:grid-cols-2 gap-6 mb-4 items-start">
                    <div className={`leading-normal text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                      {section.contentLeftImageRight.content?.split('\n\n').map((paragraph, pIdx) => (
                        <p key={pIdx} className="mb-3 last:mb-0">
                          {paragraph.split('\n').map((line, idx) => (
                            <span key={idx}>
                              {parseBoldText(line)}
                              {idx < paragraph.split('\n').length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      ))}
                    </div>
                    <div>
                      <img
                        src={section.contentLeftImageRight.src}
                        alt={section.contentLeftImageRight.alt || ""}
                        className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        onClick={() => onImageClick?.(section.contentLeftImageRight.src)}
                      />
                    </div>
                  </div>
                )}

                {/* Image above content */}
                {section.imageAboveContent && (
                  <div className="mb-4">
                    <img
                      src={section.imageAboveContent}
                      alt={section.imageAboveContentAlt || ""}
                      className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                      onClick={() => onImageClick?.(section.imageAboveContent)}
                    />
                  </div>
                )}

                {/* Section content (regular - only if no side-by-side and not in grid and not contentLeftImageRight) */}
                {section.content && !section.sideByeSideImage && !section.contentInGrid && !section.contentLeftImageRight?.src && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.content.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => {
                          // Check for ### heading
                          if (line.startsWith('### ')) {
                            return (
                              <span key={idx} className="block text-lg font-semibold mt-3 mb-1">
                                {parseBoldText(line.slice(4))}
                                {idx < paragraph.split('\n').length - 1 && <br />}
                              </span>
                            );
                          }
                          return (
                            <span key={idx}>
                              {parseBoldText(line)}
                              {idx < paragraph.split('\n').length - 1 && <br />}
                            </span>
                          );
                        })}
                      </p>
                    ))}
                  </div>
                )}

                {/* Highlighted/accent text */}
                {section.highlightedText && (
                  <p className="mb-4 text-base" style={{ color: '#e8837b' }}>
                    {parseBoldText(section.highlightedText)}
                  </p>
                )}

                {/* Callout box (skip if already rendered above contentLeftImageRight or sideByeSideImage) */}
                {section.callout && !section.callout.inGrid && !section.contentLeftImageRight?.src && !section.sideByeSideImage?.src && (
                  <div className={`rounded-xl py-4 px-5 ${section.callout.smallMargin ? 'mb-2' : 'mb-4'}`} style={{
                    ...(section.callout.borderOnly ? {
                      backgroundColor: 'transparent',
                      border: `2px solid ${section.callout.color || '#4a4a4a'}`
                    } : {
                      backgroundColor: section.callout.color
                        ? section.callout.color
                        : section.callout.title?.toLowerCase().includes('dynamic') || section.callout.title?.toLowerCase().includes('trick')
                          ? '#5c3d3d'
                          : section.callout.title?.toLowerCase().includes('tip')
                            ? '#4a3f3f'
                            : '#4a4a4a'
                    })
                  }}>
                    <div className="flex items-start gap-3">
                      <span className="text-white text-xl">
                        {section.callout.icon
                          ? section.callout.icon
                          : section.callout.title?.toLowerCase().includes('dynamic') || section.callout.title?.toLowerCase().includes('trick')
                            ? '⚡'
                            : section.callout.title?.toLowerCase().includes('tip')
                              ? '💡'
                              : '✨'}
                      </span>
                      <div className="flex-1">
                        {section.callout.title && (
                          <p className="text-white font-semibold mb-2">{section.callout.title}</p>
                        )}
                        {section.callout.textLines && (
                          <div className="text-white space-y-2">
                            {section.callout.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                        {section.callout.preformattedText && (
                          <pre className="text-white font-mono text-sm whitespace-pre leading-relaxed">
                            {section.callout.preformattedText}
                          </pre>
                        )}
                        {section.callout.items && (
                          <ul className="text-white space-y-1">
                            {section.callout.items.map((item, idx) => (
                              <li key={idx}>• {parseBoldText(item)}</li>
                            ))}
                          </ul>
                        )}
                        {section.callout.quote && (
                          <div className="border-l-4 border-[#606b7c] pl-4 mt-3">
                            <p className="text-white italic text-base leading-relaxed">
                              {parseBoldText(section.callout.quote)}
                            </p>
                          </div>
                        )}
                        {section.callout.footer && (
                          <p className="text-white font-bold">{section.callout.footer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Multiple callouts (callouts array) */}
                {section.callouts && section.callouts.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {section.callouts.map((calloutItem, idx) => (
                      <div key={idx} className="rounded-xl py-4 px-5" style={{ backgroundColor: calloutItem.color || '#4a4a4a' }}>
                        <div className="flex items-start gap-3">
                          {calloutItem.icon && (
                            <span className="text-white text-xl">{calloutItem.icon}</span>
                          )}
                          <div className="flex-1">
                            {calloutItem.title && (
                              <p className="text-white font-semibold mb-2">{calloutItem.title}</p>
                            )}
                            {calloutItem.textLines && (
                              <div className="text-white space-y-1">
                                {calloutItem.textLines.map((line, lineIdx) => (
                                  <p key={lineIdx}>{parseBoldText(line)}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Callout left, Image right side-by-side layout */}
                {section.calloutLeftImageRight && (
                  <div className="grid md:grid-cols-2 gap-6 mb-4 items-start">
                    <div className={`rounded-xl py-4 px-5`} style={{
                      backgroundColor: section.calloutLeftImageRight.callout?.borderOnly ? 'transparent' : (section.calloutLeftImageRight.callout?.color || '#5c3d3d'),
                      border: section.calloutLeftImageRight.callout?.borderOnly ? `2px solid ${section.calloutLeftImageRight.callout?.color || '#4a4a4a'}` : 'none'
                    }}>
                      <div className="flex items-start gap-3">
                        <span className={`text-xl ${section.calloutLeftImageRight.callout?.borderOnly ? 'text-white' : 'text-white'}`}>
                          {section.calloutLeftImageRight.callout?.icon || '💡'}
                        </span>
                        <div className="flex-1">
                          {section.calloutLeftImageRight.callout?.title && (
                            <p className="text-white font-semibold mb-2">{section.calloutLeftImageRight.callout.title}</p>
                          )}
                          {section.calloutLeftImageRight.callout?.textLines && (
                            <div className="text-white space-y-2">
                              {section.calloutLeftImageRight.callout.textLines.map((line, idx) => (
                                <p key={idx}>{parseBoldText(line)}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <img
                        src={section.calloutLeftImageRight.image?.src}
                        alt={section.calloutLeftImageRight.image?.alt || ""}
                        className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        onClick={() => onImageClick?.(section.calloutLeftImageRight.image?.src)}
                      />
                    </div>
                  </div>
                )}

                {/* Content after callout (before images) */}
                {section.contentAfterCallout && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAfterCallout.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Single image */}
                {section.image?.src && (
                  <div className="mb-4">
                    <img
                      src={section.image.src}
                      alt={section.image.alt || ""}
                      className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                      onClick={() => onImageClick?.(section.image.src)}
                    />
                    {section.image.caption && (
                      <p className="text-sm text-[#606b7c] text-center mt-2 italic">
                        {section.image.caption}
                      </p>
                    )}
                  </div>
                )}

                {/* Table layout */}
                {section.table && (
                  <div className="overflow-x-auto mb-4">
                    <table className={`w-full border-collapse border ${section.darkBackground ? 'border-gray-500' : 'border-gray-300'}`}>
                      <thead>
                        <tr className={`border-b ${section.darkBackground ? 'border-gray-500 bg-[#3a3a3a]' : 'border-gray-300 bg-gray-100'}`}>
                          {section.table.headers.map((header, idx) => (
                            <th key={idx} className={`text-left py-3 px-4 font-semibold ${section.darkBackground ? 'text-white' : 'text-gray-700'} ${idx < section.table.headers.length - 1 ? (section.darkBackground ? 'border-r border-gray-500' : 'border-r border-gray-300') : ''}`}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className={`border-b ${section.darkBackground ? 'border-gray-500' : 'border-gray-200'}`}>
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className={`py-3 px-4 ${section.darkBackground ? 'text-white' : 'text-gray-600'} ${cellIdx < row.length - 1 ? (section.darkBackground ? 'border-r border-gray-500' : 'border-r border-gray-300') : ''}`}>
                                {parseBoldText(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Content after table */}
                {section.contentAfterTable && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAfterTable.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Hero layout: quote left, image right */}
                {section.quote && section.heroImage?.src && (
                  <div className="grid md:grid-cols-2 gap-8 mb-6 items-start">
                    <div className="border-l-4 border-white pl-4">
                      <p className={`italic text-lg leading-relaxed ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                        {section.quote.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < section.quote.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    </div>
                    <div>
                      <img
                        src={section.heroImage.src}
                        alt={section.heroImage.alt || ""}
                        className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                        loading="lazy"
                        onClick={() => onImageClick?.(section.heroImage.src)}
                      />
                    </div>
                  </div>
                )}

                {/* Blockquote (standard - only if no heroImage) */}
                {section.quote && !section.heroImage && (
                  <div className="border-l-4 border-[#606b7c] pl-4 mb-4">
                    <p className={`italic text-base leading-relaxed ${section.darkBackground ? 'text-white' : 'text-black'} ${section.quoteHighlighted ? 'bg-[#4a4a4a] px-2 py-1 rounded' : ''}`}>
                      {section.quote.split('\n').map((line, idx) => (
                        <span key={idx}>
                          {parseBoldText(line)}
                          {idx < section.quote.split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </p>
                  </div>
                )}

                {/* Content after quote */}
                {section.contentAfterQuote && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAfterQuote.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Content above grid - only for non-imagesFirst sections (imagesFirst handled earlier) */}
                {section.contentAboveGrid && !section.imagesFirst && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAboveGrid.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* External links with images */}
                {section.externalLinks && section.externalLinks.length > 0 && (
                  <div className="grid gap-4 mb-4 md:grid-cols-2">
                    {section.externalLinks.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group"
                      >
                        <div className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                          {link.image && (
                            <img
                              src={link.image}
                              alt={link.title}
                              className="w-full h-40 object-cover"
                              loading="lazy"
                            />
                          )}
                          <div className={`p-3 ${section.darkBackground ? 'bg-gray-800' : 'bg-gray-100'}`}>
                            <p className={`font-medium group-hover:underline ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                              {link.title}
                            </p>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {/* Gallery images AFTER content (default) */}
                {!section.imagesFirst && section.galleryImages && section.galleryImages.length > 0 && (
                  <div className={`grid gap-4 mb-4 ${section.imagesLeftInGrid || section.callout?.inGrid ? 'items-start' : ''} ${
                    section.galleryColumns === 4 ? 'md:grid-cols-4' :
                    section.galleryColumns === 2 ? 'md:grid-cols-2' :
                    section.galleryImages.some(img => img.span) || section.galleryText || section.callout?.inGrid || section.contentInGrid ? 'md:grid-cols-6' :
                    'md:grid-cols-3'
                  }`}>
                    {/* Content in grid (beside images) */}
                    {section.contentInGrid && section.content && (
                      <div className="md:col-span-3 flex items-start" style={section.imagesLeftInGrid ? { order: 2 } : {}}>
                        <div className={`leading-normal text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                          {section.content.split('\n\n').map((paragraph, pIdx) => (
                            <p key={pIdx} className="mb-2 last:mb-0">
                              {paragraph.split('\n').map((line, idx) => {
                                // Check for ### heading
                                if (line.startsWith('### ')) {
                                  return (
                                    <span key={idx} className="block text-lg font-semibold mt-3 mb-1">
                                      {parseBoldText(line.slice(4))}
                                      {idx < paragraph.split('\n').length - 1 && <br />}
                                    </span>
                                  );
                                }
                                return (
                                  <span key={idx}>
                                    {parseBoldText(line)}
                                    {idx < paragraph.split('\n').length - 1 && <br />}
                                  </span>
                                );
                              })}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    {section.galleryImages.map((img, idx) => (
                      <div key={idx} style={section.imagesLeftInGrid ? { order: 1 } : {}} className={`${img.stackWith ? "space-y-4" : ""} ${
                        img.span === 6 ? 'md:col-span-6' :
                        img.span === 5 ? 'md:col-span-5' :
                        img.span === 4 ? 'md:col-span-4' :
                        img.span === 3 ? 'md:col-span-3' :
                        img.span === 2 ? 'md:col-span-2' : ''
                      } ${
                        img.colStart === 5 ? 'md:col-start-5' :
                        img.colStart === 4 ? 'md:col-start-4' :
                        img.colStart === 3 ? 'md:col-start-3' :
                        img.offset === 2 ? 'md:col-start-2' :
                        img.offset === 1 ? 'md:col-start-2' : ''
                      }`}>
                        <div className="w-full">
                          <img
                            src={img.src}
                            alt={img.alt || ""}
                            className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy"
                            onClick={() => onImageClick?.(img.src)}
                          />
                          {img.caption && (
                            <div className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                              {img.caption.split('\n\n').map((paragraph, pIdx) => (
                                <p key={pIdx} className="mb-2 last:mb-0">
                                  {paragraph.split('\n').map((line, idx) => (
                                    <span key={idx}>
                                      {parseBoldText(line)}
                                      {idx < paragraph.split('\n').length - 1 && <br />}
                                    </span>
                                  ))}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        {img.stackWith && (
                          <div className="w-full">
                            <img
                              src={img.stackWith.src}
                              alt={img.stackWith.alt || ""}
                              className={`w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity ${img.stackWith.aspectRatio ? 'object-cover' : ''}`}
                              style={img.stackWith.aspectRatio ? { aspectRatio: img.stackWith.aspectRatio } : {}}
                              loading="lazy"
                              onClick={() => onImageClick?.(img.stackWith.src)}
                            />
                            {img.stackWith.caption && (
                              <p className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                                {parseBoldText(img.stackWith.caption)}
                              </p>
                            )}
                          </div>
                        )}
                        {img.calloutBelow && (
                          <div className="w-full mt-4 rounded-xl py-4 px-5" style={{ backgroundColor: img.calloutBelow.color || '#4a4a4a' }}>
                            <div className="flex items-start gap-3">
                              <span className="text-xl text-white">{img.calloutBelow.icon || '✨'}</span>
                              <div className="flex-1">
                                {img.calloutBelow.title && (
                                  <p className="font-semibold text-white mb-2">{img.calloutBelow.title}</p>
                                )}
                                {img.calloutBelow.textLines && (
                                  <div className="space-y-2 text-white">
                                    {img.calloutBelow.textLines.map((line, idx) => (
                                      <p key={idx}>{parseBoldText(line)}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {section.galleryText && (
                      <div className={`flex items-center ${
                        section.galleryText.span === 3 ? 'md:col-span-3' :
                        section.galleryText.span === 2 ? 'md:col-span-2' : 'md:col-span-2'
                      }`}>
                        <p className={`text-base italic leading-relaxed ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                          {parseBoldText(section.galleryText.content)}
                        </p>
                      </div>
                    )}
                    {/* Callout in grid (beside images) */}
                    {section.callout?.inGrid && (
                      <div className={`md:col-span-3 md:row-start-1 flex items-start ${section.callout.rightSide ? 'md:col-start-4' : ''}`}>
                        <div className="rounded-xl py-4 px-5 w-full" style={{
                          ...(section.callout.borderOnly ? {
                            backgroundColor: 'transparent',
                            border: `2px solid ${section.callout.color || '#4a4a4a'}`
                          } : {
                            backgroundColor: section.callout.color || '#4a4a4a'
                          })
                        }}>
                          <div className="flex items-start gap-3">
                            <span className={`text-xl ${section.callout.borderOnly ? '' : 'text-white'}`} style={section.callout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                              {section.callout.icon || '✨'}
                            </span>
                            <div className="flex-1">
                              {section.callout.title && (
                                <p className={`font-semibold mb-2 ${section.callout.borderOnly ? '' : 'text-white'}`} style={section.callout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>{section.callout.title}</p>
                              )}
                              {section.callout.textLines && (
                                <div className={`space-y-2 ${section.callout.borderOnly ? '' : 'text-white'}`} style={section.callout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                                  {section.callout.textLines.map((line, idx) => (
                                    <p key={idx}>{parseBoldText(line)}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Content AFTER images */}
                {section.contentAfter && (
                  <div
                    className={`leading-normal mb-4 text-base ${section.contentAfterColor ? '' : (section.darkBackground ? 'text-white' : 'text-black')}`}
                    style={section.contentAfterColor ? { color: section.contentAfterColor } : {}}
                  >
                    {section.contentAfter.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Blockquote with left border - renders before galleryImagesAfter */}
                {section.blockquote && (
                  <div className="grid md:grid-cols-6 gap-4 mb-4 items-start">
                    <div className={`${section.blockquoteImage ? 'md:col-span-3' : 'md:col-span-6'} pl-4 py-3 rounded-r`} style={{ borderLeft: '3px solid #888', backgroundColor: section.blockquoteTransparent ? 'transparent' : '#3a3a3a' }}>
                      <p className="text-lg text-white">
                        {parseBoldText(section.blockquote)}
                      </p>
                    </div>
                    {section.blockquoteImage && (
                      <div className="md:col-span-3">
                        <img
                          src={section.blockquoteImage.src}
                          alt={section.blockquoteImage.alt || ""}
                          className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                          loading="lazy"
                          onClick={() => onImageClick?.(section.blockquoteImage.src)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Gallery images AFTER contentAfter (galleryImagesAfter) */}
                {section.galleryImagesAfter && section.galleryImagesAfter.length > 0 && (
                  <div className={`grid gap-4 mb-4 ${
                    section.galleryImagesAfter.some(img => img.span) ? 'md:grid-cols-6' :
                    section.galleryColumns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                  }`}>
                    {section.galleryImagesAfter.map((img, idx) => (
                      <div key={idx} className={`${img.stackWith ? "space-y-4" : ""} ${
                        img.span === 6 ? 'md:col-span-6' :
                        img.span === 5 ? 'md:col-span-5' :
                        img.span === 4 ? 'md:col-span-4' :
                        img.span === 3 ? 'md:col-span-3' :
                        img.span === 2 ? 'md:col-span-2' : ''
                      } ${
                        img.colStart === 4 ? 'md:col-start-4' : ''
                      }`}>
                        <div>
                          <img
                            src={img.src}
                            alt={img.alt || ""}
                            className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy"
                            onClick={() => onImageClick?.(img.src)}
                          />
                          {img.caption && (
                            <p className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                              {parseBoldText(img.caption)}
                            </p>
                          )}
                        </div>
                        {img.stackWith && (
                          <div className="w-full">
                            <img
                              src={img.stackWith.src}
                              alt={img.stackWith.alt || ""}
                              className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                              onClick={() => onImageClick?.(img.stackWith.src)}
                            />
                            {img.stackWith.caption && (
                              <p className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                                {parseBoldText(img.stackWith.caption)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Content AFTER images (contentAfterImages) */}
                {section.contentAfterImages && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAfterImages.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => {
                          // Check for ### heading
                          if (line.startsWith('### ')) {
                            return (
                              <span key={idx} className="block text-lg font-semibold mt-3 mb-1">
                                {parseBoldText(line.slice(4))}
                                {idx < paragraph.split('\n').length - 1 && <br />}
                              </span>
                            );
                          }
                          return (
                            <span key={idx}>
                              {parseBoldText(line)}
                              {idx < paragraph.split('\n').length - 1 && <br />}
                            </span>
                          );
                        })}
                      </p>
                    ))}
                  </div>
                )}

                {/* Callout AFTER images (calloutAfterImages) */}
                {section.calloutAfterImages && (
                  <div className={`rounded-xl py-4 px-5 mb-4`} style={{
                    backgroundColor: section.calloutAfterImages.borderOnly ? 'transparent' : (section.calloutAfterImages.backgroundColor || '#4a4a4a'),
                    border: section.calloutAfterImages.borderOnly ? `2px solid ${section.calloutAfterImages.backgroundColor || '#4a4a4a'}` : 'none'
                  }}>
                    <div className="flex items-start gap-3">
                      {section.calloutAfterImages.icon && (
                        <span className={`text-xl ${section.calloutAfterImages.borderOnly ? '' : 'text-white'}`} style={section.calloutAfterImages.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                          {section.calloutAfterImages.icon}
                        </span>
                      )}
                      <div className="flex-1">
                        {section.calloutAfterImages.title && (
                          <p className={`font-semibold mb-2 ${section.calloutAfterImages.borderOnly ? '' : 'text-white'}`} style={section.calloutAfterImages.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.calloutAfterImages.title}
                          </p>
                        )}
                        {section.calloutAfterImages.items && (
                          <ul className={`space-y-1 ${section.calloutAfterImages.borderOnly ? '' : 'text-white'}`} style={section.calloutAfterImages.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.calloutAfterImages.items.map((item, idx) => (
                              <li key={idx}>• {parseBoldText(item)}</li>
                            ))}
                          </ul>
                        )}
                        {section.calloutAfterImages.textLines && (
                          <div className={`space-y-1 ${section.calloutAfterImages.borderOnly ? '' : 'text-white'}`} style={section.calloutAfterImages.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.calloutAfterImages.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content before additional images */}
                {section.contentBeforeAdditionalImages && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentBeforeAdditionalImages.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Additional images (additionalImages) */}
                {section.additionalImages && section.additionalImages.length > 0 && (
                  <div className={`grid gap-4 mb-4 ${
                    section.additionalImages.some(img => img.span) ? 'md:grid-cols-6' :
                    section.galleryColumns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                  }`}>
                    {section.additionalImages.map((img, idx) => (
                      <div key={idx} className={`${img.stackWith ? "space-y-4" : ""} ${
                        img.span === 6 ? 'md:col-span-6' :
                        img.span === 3 ? 'md:col-span-3' :
                        img.span === 2 ? 'md:col-span-2' : ''
                      }`}>
                        <img
                          src={img.src}
                          alt={img.alt || ""}
                          className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                          loading="lazy"
                          onClick={() => onImageClick?.(img.src)}
                        />
                        {img.caption && (
                          <p className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                            {parseBoldText(img.caption)}
                          </p>
                        )}
                        {img.stackWith && (
                          <div className="w-full">
                            <img
                              src={img.stackWith.src}
                              alt={img.stackWith.alt || ""}
                              className={`w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity ${img.stackWith.aspectRatio ? 'object-cover' : ''}`}
                              style={img.stackWith.aspectRatio ? { aspectRatio: img.stackWith.aspectRatio } : {}}
                              loading="lazy"
                              onClick={() => onImageClick?.(img.stackWith.src)}
                            />
                            {img.stackWith.caption && (
                              <p className={`text-sm mt-2 ${section.darkBackground ? 'text-white' : 'text-[#606b7c]'}`}>
                                {parseBoldText(img.stackWith.caption)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Additional callout (additionalCallout) */}
                {section.additionalCallout && (
                  <div
                    className={`rounded-xl py-4 px-5 mb-4 ${section.additionalCallout.smallMargin ? 'mb-2' : 'mb-4'}`}
                    style={{
                      backgroundColor: section.additionalCallout.borderOnly ? 'transparent' : (section.additionalCallout.backgroundColor || '#4a4a4a'),
                      border: section.additionalCallout.borderOnly ? `1px solid ${section.additionalCallout.backgroundColor || '#4a4a4a'}` : 'none'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {section.additionalCallout.icon && (
                        <span className={`text-xl ${section.additionalCallout.borderOnly ? '' : 'text-white'}`} style={section.additionalCallout.borderOnly ? { color: section.additionalCallout.backgroundColor || '#4a4a4a' } : {}}>
                          {section.additionalCallout.icon}
                        </span>
                      )}
                      <div className="flex-1">
                        {section.additionalCallout.title && (
                          <p className={`font-semibold mb-2 ${section.additionalCallout.borderOnly ? '' : 'text-white'}`} style={section.additionalCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.additionalCallout.title}
                          </p>
                        )}
                        {section.additionalCallout.textLines && (
                          <div className={`space-y-1 ${section.additionalCallout.borderOnly ? '' : 'text-white'}`} style={section.additionalCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.additionalCallout.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content after additional callout */}
                {section.contentAfterAdditionalCallout && (
                  <div className={`leading-normal mb-4 text-base ${section.darkBackground ? 'text-white' : 'text-black'}`}>
                    {section.contentAfterAdditionalCallout.split('\n\n').map((paragraph, pIdx) => (
                      <p key={pIdx} className="mb-2 last:mb-0">
                        {paragraph.split('\n').map((line, idx) => (
                          <span key={idx}>
                            {parseBoldText(line)}
                            {idx < paragraph.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    ))}
                  </div>
                )}

                {/* Third callout */}
                {section.thirdCallout && (
                  <div
                    className={`rounded-xl py-4 px-5 mb-4`}
                    style={{
                      backgroundColor: section.thirdCallout.borderOnly ? 'transparent' : (section.thirdCallout.backgroundColor || '#4a4a4a'),
                      border: section.thirdCallout.borderOnly ? `2px solid ${section.thirdCallout.borderColor || section.thirdCallout.backgroundColor || '#4a4a4a'}` : 'none'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {section.thirdCallout.icon && (
                        <span className={`text-xl`} style={{ color: section.thirdCallout.iconColor || (section.thirdCallout.borderOnly ? (section.darkBackground ? 'white' : 'black') : 'white') }}>
                          {section.thirdCallout.icon}
                        </span>
                      )}
                      <div className="flex-1">
                        {section.thirdCallout.title && (
                          <p className={`font-semibold mb-2 ${section.thirdCallout.borderOnly ? '' : 'text-white'}`} style={section.thirdCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.thirdCallout.title}
                          </p>
                        )}
                        {section.thirdCallout.textLines && (
                          <div className={`space-y-1 ${section.thirdCallout.borderOnly ? '' : 'text-white'}`} style={section.thirdCallout.borderOnly ? { color: section.darkBackground ? 'white' : 'black' } : {}}>
                            {section.thirdCallout.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* External link */}
                {section.link && (
                  <a
                    href={section.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mb-4 text-base underline hover:opacity-80"
                    style={{ color: '#e57373' }}
                  >
                    {section.link.text}
                  </a>
                )}

                {/* Pro Tip - only in last section */}
                {proTip && (
                  <div className="mt-4 mb-4 rounded-xl py-4 px-5" style={{ backgroundColor: proTip.color || '#4a4a4a' }}>
                    <div className="flex items-start gap-3">
                      <span className="text-xl text-white">{proTip.icon || '✨'}</span>
                      <div className="flex-1">
                        {proTip.textLines ? (
                          <div className="space-y-2 text-white">
                            {proTip.textLines.map((line, idx) => (
                              <p key={idx}>{parseBoldText(line)}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-white whitespace-pre-line">{proTip}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Checkpoint button */}
                {!isCompleted && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onComplete();
                    }}
                    className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 mt-2"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {stepNumber === totalSteps ? "Complete Lesson" : "Continue to Next Step"}
                  </Button>
                )}

                {isCompleted && (
                  <div className="flex items-center justify-center gap-2 text-[#fe395f] py-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Transition Message - shows after completing this section */}
      <AnimatePresence>
        {showTransition && section.transitionTo && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-gradient-to-r from-[#fef2f4] to-white border border-[#fe395f]/20 rounded-xl p-4"
          >
            <p className="text-[#606b7c] italic text-center">
              {section.transitionTo}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Legacy content renderer for modules without sections
const ContentRenderer = ({ content, images, onImageClick }) => {
  if (!content) return null;

  const parts = content.split(/(\{\{IMAGE:[^}]+\}\})/g);

  return (
    <div className="space-y-6">
      {parts.map((part, index) => {
        const imageMatch = part.match(/\{\{IMAGE:([^}]+)\}\}/);

        if (imageMatch) {
          const imageId = imageMatch[1];
          const imageData = images?.[imageId];

          if (!imageData?.src) {
            return (
              <div
                key={index}
                className="bg-[#fef2f4] rounded-xl p-6 text-center border-2 border-dashed border-[#fe395f]/30"
              >
                <Camera className="w-8 h-8 text-[#fe395f]/50 mx-auto mb-2" />
                <p className="text-sm text-[#606b7c]">Image: {imageId}</p>
              </div>
            );
          }

          return (
            <div key={index} className="my-6">
              <img
                src={imageData.src}
                alt={imageData.alt || ""}
                className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                loading="lazy"
                onClick={() => onImageClick?.(imageData.src)}
              />
              {imageData.caption && (
                <p className="text-sm text-[#606b7c] text-center mt-2 italic">
                  {imageData.caption}
                </p>
              )}
            </div>
          );
        }

        if (part.trim()) {
          return (
            <p key={index} className="text-lg text-[#606b7c] leading-relaxed whitespace-pre-line">
              {part}
            </p>
          );
        }

        return null;
      })}
    </div>
  );
};

const trainingStructure = {
  technical: {
    id: "technical",
    icon: Camera,
    title: "TECHNICAL",
    emoji: "📸",
    description: "How your camera sees the world — exposure, light, color, lenses, and all the tools that shape your images.",
    modules: [
      {
        id: "exposure-triangle",
        title: "EXPOSURE TRIANGLE — MASTERING LIGHT & BALANCE IN WEDDING PHOTOGRAPHY",
        sections: [
          {
            id: "intro",
            title: "What It Is",
            darkBackground: true,
            sideByeSideImage: {
              src: "/training/exposure-triangle/25.11.2025%2014.39.09%20378%2024.jpg",
              alt: "Wedding couple in beautiful light"
            },
            content: `The **exposure triangle** is the relationship between **ISO, Shutter Speed, and Aperture**.

These three settings control how much light enters your camera — and how your image *feels*.

You can think of it simply: ISO, Shutter, and Aperture are three doors to the same light. Open one, and you may need to close the others.`,
            transitionTo: null,
          },
          {
            id: "iso",
            title: "1. ISO — Light Sensitivity",
            darkBackground: true,
            callout: {
              textLines: [
                `• Controls how sensitive your sensor is to light.`,
                `• **Low ISO (100–400)** → clean image, rich colors.`,
                `• **High ISO (800–3200+)** → brighter but adds *noise* (grainy texture).`,
                ``,
                `💬 *Remember:* ISO doesn't add light — it just **amplifies** what's already there.`,
                ``,
                `Too high = more light, but also more digital noise.`,
                ``,
                `**Wedding Tip:** Keep ISO as low as possible, raise it *only* when shutter or aperture can't go further.`
              ],
              color: "#4a4a4a"
            },
            galleryImagesAfter: [
              {
                src: "/training/exposure-triangle/02.12.2025%2020.11.22%20316%20309.jpg",
                alt: "ISO comparison example",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "shutter",
            title: "2. Shutter Speed — Time & Motion",
            darkBackground: true,
            callout: {
              textLines: [
                `• Determines how long your sensor is exposed to light.`,
                `• **Fast shutter (1/250–1/800s):** freezes motion (perfect for walking down the aisle).`,
                `• **Slow shutter (1/60–1/125s):** adds motion blur — use carefully, only for creative shots or static scenes.`,
                ``,
                `💬 *Rule of thumb:* Never go below **1/200s** for handheld people shots.`,
                ``,
                `Otherwise, you risk blur — especially in emotional, moving moments.`,
                ``,
                `**Wedding Tip:** In Manual mode, set shutter first for the type of movement you want.`
              ],
              color: "#4a4a4a"
            },
            galleryImagesAfter: [
              {
                src: "/training/exposure-triangle/02.12.2025%2021.14.08%20130%20391.jpg",
                alt: "Shutter speed motion example",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "aperture",
            title: "3. Aperture — Depth & Emotion",
            darkBackground: true,
            callout: {
              textLines: [
                `• Controls how *wide* your lens opens.`,
                `• **Wide aperture (f/1.4–f/2.8):** shallow depth, dreamy background, perfect for portraits.`,
                `• **Narrow aperture (f/4–f/8):** more depth, ideal for group shots or décor.`,
                ``,
                `💬 *Think of it as emotional control:*`,
                ``,
                `Wider aperture = intimacy and softness.`,
                ``,
                `Narrower = clarity and storytelling context.`
              ],
              color: "#4a4a4a"
            },
            galleryImagesAfter: [
              {
                src: "/training/exposure-triangle/02.12.2025%2021.41.10%20507%20443.jpg",
                alt: "Aperture depth of field example",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "together",
            title: "⚖️ How They Work Together",
            darkBackground: true,
            content: `**Example — Indoor ceremony, dim light:**

• Aperture: f/1.8 (to let in light and isolate subjects)
• Shutter: 1/250s (to avoid blur)
• ISO: 1600 (to balance exposure)

**Example — Outdoor sunny portraits:**

• Aperture: f/2.0
• Shutter: 1/2000s
• ISO: 100`,
            highlightedText: `💬 *The key:* when you change one, adjust at least one of the others to keep proper exposure.`,
            transitionTo: null,
          },
          {
            id: "bonus",
            title: "Bonus: Creative Balancing Tricks",
            darkBackground: true,
            content: `• **Use exposure compensation** (+/–) even in Manual with Auto ISO to fine-tune.

• **Lock exposure** when the light doesn't change (for consistent tones across a series).

• **Don't chase the meter** — rely on skin tones and histogram instead.

• **Neutral Density (ND) filter** allows you to shoot wide open in bright light — essential for creamy background outdoors.`,
            transitionTo: null,
          },
          {
            id: "hidden",
            title: "Hidden Details Few Mention",
            darkBackground: true,
            content: `1. ISO affects *dynamic range* — the higher you go, the fewer details in highlights.

2. Shutter affects *flash sync* — avoid going above 1/200–1/250s with external flash unless your camera supports HSS.

3. Aperture affects *sharpness zones* — most lenses are sharpest around f/2.8–f/5.6, not wide open.

4. Balancing all three in **Manual mode** gives consistency for editing and color grading later.`,
            transitionTo: null,
          },
          {
            id: "formula",
            title: "Quick Wedding Formula",
            darkBackground: true,
            content: `• **Preparation room:** ISO 400–800 / f/2.0 / 1/250s

• **Ceremony (indoor):** ISO 1600–3200 / f/1.8 / 1/250s

• **Reception dancing:** ISO 3200+ / f/2.8 / 1/250s / with flash

• **Outdoor portraits:** ISO 100 / f/2.0 / 1/2000s`,
            transitionTo: null,
          },
        ],
        // Reference images - add settings: { iso, aperture, shutter } to enable tap-to-reveal
        referenceColumns: 6,
        referenceImages: [
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2022.59.25.png",
            alt: "Reference diagram 1",
            settingsCaption: "Preparation room with window light",
            span: 3
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2022.55.24.png",
            alt: "Reference diagram 2",
            settingsCaption: "Indoor ceremony",
            span: 3
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2022.59.39.png",
            alt: "Reference diagram 3",
            settingsCaption: "Outdoor portraits",
            span: 2
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2022.57.44.png",
            alt: "Reference diagram 4",
            settingsCaption: "Reception dancing",
            span: 2
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2022.55.03.png",
            alt: "Reference diagram 5",
            settingsCaption: "Detail shots",
            span: 2
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2023.00.56.png",
            alt: "Reference diagram 6",
            settingsCaption: "Low light moment",
            span: 3
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2023.00.39.png",
            alt: "Reference diagram 7",
            settingsCaption: "Group shot outdoors",
            span: 3
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2023.02.27.png",
            alt: "Reference diagram 8",
            settingsCaption: "First dance",
            span: 6
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2022.57.12.png",
            alt: "Reference diagram 9",
            settingsCaption: "Golden hour portrait",
            span: 2
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2023.02.05.png",
            alt: "Reference diagram 10",
            settingsCaption: "Walking down the aisle",
            span: 2
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2023.00.28.png",
            alt: "Reference diagram 11",
            settingsCaption: "Emotional moment",
            span: 2
          },
          {
            src: "/training/exposure-triangle/Screenshot%202025-12-02%20at%2023.03.26.png",
            alt: "Reference diagram 12",
            settingsCaption: "Sparkler exit",
            span: 6
          },
        ],
      },
      {
        id: "exposure-triangle-real-life",
        title: "Exposure Triangle in Real Life — Mastering Light, Emotion & Control",
        estimatedMinutes: 8,
        hook: "It's the emotional language of your camera. Each side changes how the photo feels as much as how it looks.",
        sections: [
          {
            id: "intro",
            title: "What It Really Means",
            darkBackground: true,
            blockquote: `The "exposure triangle" — ISO, Shutter Speed, and Aperture — isn't just a chart from a photography manual. It's the emotional language of your camera. Each side changes how the photo feels as much as how it looks.`,
            galleryImagesAfter: [
              {
                src: "/training/exposure-triangle-real-life/Screenshot%202025-12-02%20at%2023.11.11.png",
                alt: "Exposure triangle diagram",
                span: 6
              }
            ],
            contentAfterImages: `When you truly understand the exposure triangle, you stop "fixing exposure" and start creating atmosphere — that's the difference between a picture and a photograph.`,
            calloutAfterImages: {
              icon: "💬",
              textLines: [`"The camera doesn't see light. It sees choices."`],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "emotion",
            title: "🎭 How Each Affects Emotion (and the Final Look)",
            darkBackground: true,
            content: `##Aperture## = mood *and* depth of field
• **f/1.4** → dreamy and intimate, background melts away, soft focus around the subject
• **f/5.6** → storytelling balance, both faces in focus, context visible
• **f/11** → crisp and controlled, every detail sharp — great for architecture or group portraits

##Shutter Speed## = rhythm *and* motion clarity
• **Fast (1/500 or higher)** → freezes laughter, dancing, confetti — every gesture razor-sharp
• **Slow (1/30 or lower)** → brings motion blur and softness, perfect for emotional hugs or a bride twirling; creates a feeling of movement and nostalgia

##ISO## = texture *and* purity
• **Low (100–400)** → clean and timeless, ideal for natural daylight portraits
• **High (1600–3200+)** → visible grain and cinematic energy; adds realism but can reduce dynamic range`,
            callout: {
              icon: "⚡",
              textLines: [
                `**Pro Tip: Prioritize What Matters**`,
                `There's no "correct" exposure, only **intentional exposure**.`,
                `Ask yourself: *"What feeling am I chasing — clarity, drama, or tenderness?"*`,
                `That question decides which side of the triangle you'll bend.`
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "hidden-truths",
            title: "🔍 Hidden Truths Nobody Tells You",
            darkBackground: true,
            content: `**1. The Triangle is Not Equal**
In low light, ISO becomes your safety net — not your villain.
In harsh sun, aperture becomes your paintbrush.

**2. Expose for skin tones**, not white dresses or dark suits
Wedding photos live or die by how skin looks.

**3. Every camera has its bias**
• **Sony** sensors love shadow recovery
• **Canon** gives warmth to midtones
• **Nikon** preserves highlight detail
• **Leica** builds emotion through color roll-off, not perfection

**4. RAW is essential**
Exposure mistakes are forgivable; missed emotion isn't.

**5. Try "The Exposure Triangle Game"**`,
            calloutAfterImages: {
              icon: "📸",
              textLines: [`Spend one day locking two settings, adjusting only one.`],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "summary",
            title: "✨ Summary",
            darkBackground: true,
            callout: {
              textLines: [
                `Exposure is balance between light and soul.`,
                ``,
                `Once you stop fearing ISO, shutter, and aperture, you begin composing **feelings** instead of numbers.`
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
        ],
        referenceImages: [],
      },
      {
        id: "iso",
        title: "ISO — Light, Noise & Balance",
        estimatedMinutes: 10,
        hook: "Understanding ISO for clean, emotive wedding photography",
        sections: [
          {
            id: "what-iso-is",
            title: "What ISO Really Is",
            darkBackground: true,
            content: `ISO defines how sensitive your sensor is to light.

**It simply adds or removes light in the image.**

Higher ISO → brighter photo, but more **digital noise** (grain and color distortion).`,
            galleryImagesAfter: [
              {
                src: "/training/iso/02.12.2025%2020.11.22%20316%20309.jpg",
                alt: "ISO example photo",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "noise-examples",
            title: "Noise Comparison",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.18.04.png",
                alt: "Noise ISO 100",
                caption: "Noise IS0 100"
              },
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.17.42.png",
                alt: "Noise ISO 200",
                caption: "Noise ISO 200"
              },
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.15.47.png",
                alt: "Noise ISO 400",
                caption: "Noise ISO 400"
              },
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.16.07.png",
                alt: "Noise ISO 800",
                caption: "Noise ISO 800"
              },
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.16.27.png",
                alt: "Noise ISO 1600",
                caption: "Noise ISO 1600"
              },
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.16.41.png",
                alt: "Noise ISO 3200",
                caption: "Noise ISO 3200"
              },
              {
                src: "/training/iso/Screenshot%202025-12-02%20at%2020.17.02.png",
                alt: "Noise ISO 6400",
                caption: "Noise ISO 6400"
              },
            ],
            transitionTo: null,
          },
          {
            id: "what-is-noise",
            title: "What Is Noise (and Why It Matters)",
            darkBackground: true,
            content: `When you raise ISO, the sensor amplifies both signal and interference.

That interference shows as:
• small colored specks in shadows (purple or green)
• grainy skin texture
• loss of contrast and details

🎞️ In wedding photography, noise is most visible on dark suits, shadowy corners, or first dance scenes in dim light.`,
            galleryImages: [
              {
                src: "/training/iso/Screenshot%202025-11-28%20at%2020.19.42.png",
                alt: "High ISO noise example - dim room",
                caption: "Because the room is extremely dim, the camera pushed ISO very high. This creates heavy noise across the entire frame, muddy shadows, and a noticeable loss of sharpness. A classic example of how high ISO destroys detail in low light."
              },
              {
                src: "/training/iso/Screenshot%202025-11-28%20at%2020.20.49.png",
                alt: "High ISO noise example - chaotic lighting",
                caption: "The lighting is chaotic and weak, so ISO goes up to compensate. Noise is clearly visible on skin and clothing, colors lose clarity, and fine details become mushy. Situations like this always benefit from flash or stronger light sources.",
                stackWith: {
                  src: "/training/iso/Screenshot%202025-11-28%20at%2020.21.51.png",
                  alt: "High ISO noise example - chaotic lighting 2",
                  caption: "The lighting is chaotic and weak, so ISO goes up to compensate. Noise is clearly visible on skin and clothing, colors lose clarity, and fine details become mushy. Situations like this always benefit from flash or stronger light sources."
                }
              },
              {
                src: "/training/iso/Screenshot%202025-11-28%20at%2020.21.28.png",
                alt: "High ISO noise example - household lamp",
                caption: "The only light comes from a warm household lamp → the camera increases ISO → grain becomes visible, especially in darker areas. You can also see color noise (yellow-green speckles), which is common with high ISO under artificial lighting."
              },
            ],
            transitionTo: null,
          },
          {
            id: "simple-rule",
            title: "💡 Simple Rule",
            darkBackground: true,
            content: `**ISO = simulated light.**

• Bright environment → low ISO
• Dark environment → higher ISO`,
            transitionTo: null,
          },
          {
            id: "wedding-settings",
            title: "Typical ISO Settings for Weddings",
            darkBackground: true,
            table: {
              headers: ["Scene", "ISO", "Shutter", "Aperture", "Mode"],
              rows: [
                ["Morning prep near window", "100–400", "1/250", "f/2.0", "Manual"],
                ["Indoor ceremony or church", "800–1600", "1/200", "f/1.8", "Manual"],
                ["Coctail hour", "400–800", "1/250", "f/5.6", "Manual"],
                ["Dancing", "100–400", "1/500", "f/4", "Manual"],
                ["Sparklers or fireworks", "1600–3200", "1/250", "f/2.8", "Manual"],
              ]
            },
            transitionTo: null,
          },
          {
            id: "ettr",
            title: "Pro Tip — \"Expose to the Right\" (ETTR)",
            darkBackground: true,
            content: `If you shoot in **RAW**, slightly overexpose the image (move the histogram to the right).

You'll get cleaner shadows and smoother tones when correcting exposure in post.

📷 Always shoot in **RAW** — it preserves details and reduces visible noise later.`,
            transitionTo: null,
          },
          {
            id: "hidden-tips",
            title: "Few People Tell You:",
            darkBackground: true,
            content: `1. **Auto ISO with a cap (e.g. 3200)** helps when the light changes quickly (dances, fireworks).

2. **Dual Gain Sensors** (Sony, Canon R5, Nikon Z6 II) have "sweet ISO points" — like 800 or 3200 — with minimal noise.

3. **Disable in-camera noise reduction** — it destroys fine textures. Handle noise in post.

4. **High ISO reduces dynamic range** — less tonal depth between shadows and highlights.`,
            image: {
              src: "/training/iso/file_00000000845471f4b4cf8a5af0111594%20(1).jpg.png",
              alt: "ISO diagram"
            },
            transitionTo: null,
          },
          {
            id: "iso-combination",
            title: "⚙️ ISO in Combination",
            darkBackground: true,
            content: `• To freeze motion, increase ISO instead of lowering shutter speed.
• Keep ISO as low as possible when light allows.
• In backlight — ISO 200–400 and slight overexposure help preserve faces.`,
            transitionTo: null,
          },
          {
            id: "core-logic",
            title: "Core Logic",
            darkBackground: true,
            content: `1. Start with the lowest ISO that gives correct exposure.

2. When it's too dark, open the aperture first, then raise ISO.

3. Keep your shutter **at least 1/250** to avoid motion blur.`,
            galleryImages: [
              {
                src: "/training/iso/Screenshot%202025-11-28%20at%2020.32.37.png",
                alt: "Core logic example"
              },
              {
                src: "/training/iso/Screenshot%202025-11-28%20at%2020.32.52.png",
                alt: "Core logic example 2"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "Summary",
            darkBackground: true,
            content: `ISO isn't the enemy — it's light control in your hands.

A little noise is worth it if it means capturing emotion.

Shoot RAW, know your limits, and trust your exposure.`,
            transitionTo: null,
          },
        ],
        referenceImages: [
          {
            src: "/training/iso/Screenshot%202025-12-02%20at%2023.15.29.png",
            alt: "Reference image 1"
          },
          {
            src: "/training/iso/Screenshot%202025-12-02%20at%2023.14.43.png",
            alt: "Reference image 2"
          },
          {
            src: "/training/iso/Screenshot%202025-12-02%20at%2023.15.56.png",
            alt: "Reference image 3"
          },
        ],
      },
      {
        id: "shutter",
        title: "Shutter (Shutter Speed)",
        estimatedMinutes: 8,
        hook: "Freeze motion or create intentional blur",
        sections: [
          {
            id: "what-shutter-is",
            title: "What shutter speed really is",
            darkBackground: true,
            content: `Shutter speed is the amount of time your camera's shutter stays open, letting light hit the sensor.
Simply put: **shutter speed is how long your camera "sees" the world.**

• Shorter = less light, but sharper motion.
• Longer = more light, but moving subjects get blurred.`,
            galleryImagesAfter: [
              {
                src: "/training/shutter/02.12.2025%2021.14.08%20130%20391.jpg",
                alt: "Shutter speed comparison",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "shutter-examples",
            title: "Shutter speed examples",
            darkBackground: true,
            galleryImages: [
              {
                src: "/training/shutter/Screenshot%202025-12-02%20at%2021.17.33.png",
                alt: "Shutter speed 1/1000",
                caption: "Shutter speed 1/1000"
              },
              {
                src: "/training/shutter/Screenshot%202025-12-02%20at%2021.17.07.png",
                alt: "Shutter speed 1/15",
                caption: "Shutter speed 1/15"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "plain-words",
            title: "In plain words",
            darkBackground: true,
            content: `**Shutter controls motion in your frame.**
If ISO defines how much light you let in, then **shutter defines how long that light stays.**
It's one of the three pillars of exposure — together with ISO and aperture.`,
            transitionTo: null,
          },
          {
            id: "wedding-settings",
            title: "⚙️ Typical settings for wedding photography (Manual Mode)",
            darkBackground: true,
            table: {
              headers: ["Situation", "Recommended Shutter Speed", "Comment"],
              rows: [
                ["Bride walking down the aisle", "1/250–1/400", "movement — needs to freeze action"],
                ["Dancing, laughter, hugs", "1/320–1/500", "captures emotion without blur"],
                ["The kiss or static pose", "1/250–1/500", "crisp and clean"],
                ["Portraits in good light", "1/250", "solid baseline"],
                ["Details (rings, bouquet)", "1/200–1/250", "controlled sharpness"],
                ["Moving elements (veil, petals, hair)", "1/500 or higher", "freezes motion beautifully"],
                ["Creative blur (dance motion, lights)", "1/30–1/60", "only intentionally, with tripod"],
              ]
            },
            transitionTo: null,
          },
          {
            id: "critical-rule",
            title: "Critical rule",
            darkBackground: true,
            callout: {
              icon: "‼️",
              textLines: [
                `**Never go below 1/250 when shooting handheld.**`,
                `Even the tiniest hand movement will cause motion blur — especially during fast, emotional wedding scenes.`
              ],
              color: "#5c3d3d"
            },
            galleryImagesAfter: [
              {
                src: "/training/shutter/Screenshot%202025-12-03%20at%2000.47.49.png",
                alt: "Example of incorrect shutter speed",
                span: 6
              }
            ],
            contentAfterImages: `And in this photo, it's a full combo.
Besides the incorrect shutter speed, the white balance and ISO are also wrong — plus the lens is dirty.`,
            transitionTo: null,
          },
          {
            id: "little-known",
            title: "Little-known but important details",
            darkBackground: true,
            content: `• **Flash sync speed:** most cameras have a limit around 1/200–1/250 for flash sync. Going faster creates a dark band across the frame.

• **Shutter + LED flicker** — see "LED Banding – What It Is and How to Fix It" in the Lighting Troubleshooting guide.

• **Rolling shutter:** mirrorless cameras can distort fast motion with long exposures (like waving hands looking "bent").

• **Cinematic motion:** slightly slower speeds (1/50–1/80) can add natural flow and warmth when the camera is stable.

• **Motion background trick:** move your camera along with the subject while using a slower shutter (1/60–1/100) — the background blurs beautifully, emphasizing speed and energy.`,
            transitionTo: null,
          },
          {
            id: "practical-advice",
            title: "Practical advice",
            darkBackground: true,
            content: `• **Always shoot in Manual mode (M).**
   - Start from 1/250 as your base.
   - Adjust only when you're aiming for a creative effect.

• **If it's dark:** raise ISO or open your aperture — don't drop your shutter speed below safe range.

• **Stability rule:** use at least 1/250.`,
            transitionTo: null,
          },
          {
            id: "wedding-application",
            title: "In wedding photography",
            darkBackground: true,
            content: `Proper shutter speed helps you:

• Freeze raw emotions — laughter, tears, hugs, movement.
• {{PRO}} Add soul and energy through intentional blur.
• Capture life's rhythm without losing technical quality.`,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "In short",
            darkBackground: true,
            content: `Shutter is the rhythm of your image.

It decides whether the moment stands still — or breathes with motion.`,
            transitionTo: null,
          },
        ],
        referenceImages: [
          {
            src: "/training/shutter/Screenshot%202025-12-03%20at%2000.58.48.png",
            alt: "Reference image 1"
          },
          {
            src: "/training/shutter/Screenshot%202025-12-03%20at%2000.57.41.png",
            alt: "Reference image 2"
          },
          {
            src: "/training/shutter/Screenshot%202025-12-03%20at%2001.18.57.png",
            alt: "Reference image 3"
          },
          {
            src: "/training/shutter/Screenshot%202025-12-03%20at%2001.16.35.png",
            alt: "Reference image 4"
          },
        ],
        referenceColumns: 2,
      },
      {
        id: "aperture",
        title: "Aperture (Depth of Field)",
        estimatedMinutes: 8,
        hook: "How much of your world stays in focus",
        sections: [
          {
            id: "what-aperture-is",
            title: "What aperture really is",
            darkBackground: true,
            content: `Aperture is the opening inside your lens that controls **how much light** passes through to the sensor — and how much of your image appears **sharp or blurry**.

It's measured in **f-stops (f/1.4, f/2.8, f/4, etc.)** — and here's the twist: **The smaller the f-number → the larger the opening → the blurrier the background.**`,
            galleryImagesAfter: [
              {
                src: "/training/aperture/02.12.2025%2021.41.10%20507%20443.jpg.png",
                alt: "Aperture diagram",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "plain-words",
            title: "💡 In plain words",
            darkBackground: true,
            callout: {
              textLines: [
                `Aperture defines **depth of field** — how deep the focus runs in your image.`,
                ``,
                `• **Wide open (f/1.4–f/2.8):** dreamy portraits, creamy bokeh, focus only on the subject.`,
                `• **Medium (f/3.5–f/5.6):** more balanced — face and background both readable.`,
                `• **Narrow (f/8–f/16):** landscapes or group shots where everything needs to stay sharp.`,
                ``,
                `Aperture also affects exposure: **a wider aperture = more light.**`
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "wedding-settings",
            title: "⚙️ Typical settings for wedding photography (Manual Mode)",
            darkBackground: true,
            table: {
              headers: ["Scene", "Aperture", "Why"],
              rows: [
                ["Bride portraits / details", "f/1.8–f/2.2", "soft background, emotional focus"],
                ["Couple shots", "f/2.2–f/2.8", "both faces sharp, still creamy"],
                ["Group photos", "f/4.5–f/7.1", "ensures everyone in focus"],
                ["Venue / decor shots", "f/5.6–f/8", "clarity and depth"],
                ["Rings, flat lays", "f/2.2–f/4", "balance between detail and softness"],
                ["Outdoor portraits in sunlight", "f/2.8–f/4", "keeps control over highlights"],
              ]
            },
            transitionTo: null,
          },
          {
            id: "pro-secrets",
            title: "Things pros rarely tell you",
            darkBackground: true,
            content: `1. **Every lens has a "sweet spot".**
Usually around **f/2.8–f/4** — sharpest image, minimal vignetting.

2. **Aperture changes the mood.**
• f/1.4–f/2: emotional, intimate, romantic.
• f/4–f/8: storytelling, documentary, context-rich.

3. **Background compression** depends on focal length, not aperture — but together they shape the "look" of your photo.

4. **Light falloff:** wide apertures brighten the subject's face more than background, creating natural separation without artificial lighting.`,
            transitionTo: null,
          },
          {
            id: "eyes-first",
            title: "Quick Tip — \"Eyes first\"",
            darkBackground: true,
            content: `When shooting wide open (f/1.4–f/2), always focus on the **eyes** — especially if your subject turns slightly.

At such depth, even eyelashes can fall out of focus.`,
            transitionTo: null,
          },
          {
            id: "practical-logic",
            title: "Practical logic",
            darkBackground: true,
            content: `1. Start as wide as possible (f/1.8–f/2.2) for portraits.

2. If too much of the image blurs — close down slightly (f/2.8–f/3.2).

3. For groups — go f/4.5 or more.

4. When light is limited — open aperture first, then raise ISO.

5. Always shoot **in RAW** — you'll recover highlights and adjust focus falloff more flexibly.`,
            transitionTo: null,
          },
          {
            id: "wedding-storytelling",
            title: "🎞️ In wedding storytelling",
            darkBackground: true,
            content: `Aperture shapes not only sharpness but **emotion**:

• A bride alone at f/1.8 feels intimate.
• The whole family at f/5.6 feels connected and complete.
• The same scene, two apertures — two different emotions.`,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "In short",
            darkBackground: true,
            content: `Aperture is the soul of your frame.

It decides what the viewer sees — and what remains a beautiful blur.`,
            transitionTo: null,
          },
        ],
        proTip: {
          icon: "✨",
          textLines: [
            `Pro styling tip: keep backgrounds simple when shooting wide open so bokeh shapes read cleanly; add leading lines for context at f/4–f/8.`,
            ``,
            `90% of the wedding is typically captured between f/2.2–f/5.6.`
          ],
          color: "#4a4a4a"
        },
        referenceImages: [
          {
            src: "/training/aperture/Screenshot%202025-12-03%20at%2002.34.24.png",
            alt: "f/1.8–f/2.2 soft background",
            caption: "f/1.8–f/2.2 soft background"
          },
          {
            src: "/training/aperture/Screenshot%202025-12-03%20at%2002.39.11.png",
            alt: "f/2.2–f/2.8 still creamy",
            caption: "f/2.2–f/2.8 still creamy"
          },
          {
            src: "/training/aperture/Screenshot%202025-12-03%20at%2002.36.38.png",
            alt: "f/4.5–f/7.1 ensures everyone in focus",
            caption: "f/4.5–f/7.1 ensures everyone in focus"
          },
          {
            src: "/training/aperture/Screenshot%202025-12-03%20at%2002.38.30.png",
            alt: "f/4.5–f/7.1 ensures everyone in focus",
            caption: "f/4.5–f/7.1 ensures everyone in focus"
          },
        ],
        referenceColumns: 2,
      },
      {
        id: "white-balance",
        title: "White Balance — Mastering Color Accuracy in Mixed Light",
        estimatedMinutes: 10,
        sections: [
          {
            id: "what-it-is",
            title: "🔍 What It Is",
            darkBackground: true,
            content: `White Balance (WB) tells your camera *what "true white" looks like* under the current lighting conditions.

Different light sources (sun, tungsten, LED, etc.) have different color temperatures — measured in **Kelvin (K)**.

Your WB setting corrects these temperature shifts so whites stay neutral, not yellow or blue.`,
            image: {
              src: "/training/white-balance/02.12.2025%2022.44.03%20199%20505.jpg",
              alt: "White balance example"
            },
            transitionTo: null,
          },
          {
            id: "why-it-matters",
            title: "💡 Why It Matters",
            darkBackground: true,
            content: `When you shoot weddings, lighting can shift constantly — from warm golden sunsets to cool fluorescent halls.

Accurate WB ensures consistent skin tones and clean whites (especially crucial for the bride's dress and décor).

If your WB is off, you'll spend *hours* fixing colors in post.`,
            galleryImages: [
              {
                src: "/training/white-balance/Screenshot%202025-11-29%20at%2021.04.09.png",
                alt: "WB too cool - blue cast",
                caption: "The white balance is extremely cool (around 3000K or lower), giving the entire scene a strong blue cast and making the skin tones look unnatural."
              },
              {
                src: "/training/white-balance/Screenshot%202025-11-29%20at%2021.04.29.png",
                alt: "WB slightly cool - green tint",
                caption: "The white balance is slightly cool (around 3800–4200K), leaning toward green, which makes the skin look pale and muted."
              },
              {
                src: "/training/white-balance/Screenshot%202025-11-29%20at%2021.05.03.png",
                alt: "WB too warm - yellow/orange",
                caption: "The white balance is too warm (around 5200–6000K+), shifting the image toward yellow/orange and affecting the accuracy of the skin tones."
              },
              {
                src: "/training/white-balance/Screenshot%202025-11-29%20at%2021.05.17.png",
                alt: "WB correct - natural tones",
                caption: "The white balance is correctly adjusted (roughly 4600–5000K), giving natural skin tones and accurate, clean colors."
              },
            ],
            galleryColumns: 4,
            contentAfterImages: `{{*WB values shown in the examples are approximate and represent how the camera corrected the mixed lighting in that moment, not the actual physical color temperature of the light sources.}}`,
            transitionTo: null,
          },
          {
            id: "kelvin-scale",
            title: "Color Temperature Scale (Kelvin)",
            darkBackground: true,
            table: {
              headers: ["Kelvin", "Look", "Common Sources"],
              rows: [
                ["2000–3500K", "Very warm, orange", "Candles, tungsten bulbs"],
                ["4000–5000K", "Neutral", "Daylight, flash"],
                ["5500–6500K", "Slightly cool", "Cloudy, shade"],
                ["7000K+", "Very cool, blue", "Blue shade, overcast, late twilight"],
              ]
            },
            transitionTo: null,
          },
          {
            id: "kelvin-explained",
            title: "A short and clear explanation of why Kelvin values can seem confusing:",
            darkBackground: true,
            content: `**Kelvin in lighting chart describes the temperature of the actual light** (whether the environment is warm or cool).

*Kelvin in your camera's White Balance is a compensation* — the camera adjusts in the opposite direction to neutralize that light.

So:

*Warm light (around 3000K)* → the camera needs a **higher WB value** (4500–5500K) to cool it down.

*Cool light (around 7000K)* → the camera needs a **lower WB value** (3000–4000K) to warm it up.

That's why the same numbers can appear opposite depending on the context —

one scale describes the **light itself**, and the other describes how the camera **corrects** it.`,
            galleryImages: [
              {
                src: "/training/white-balance/Screenshot%202025-11-28%20at%2020.06.55.png",
                alt: "WB comparison - dramatic difference",
                caption: "Here we can see how dramatically the same scene changes when the white balance is set incorrectly."
              },
              {
                src: "/training/white-balance/Screenshot%202025-11-28%20at%2020.06.42.png",
                alt: "WB comparison - obvious improvement",
                caption: "Even though the white balance isn't perfectly accurate here, the difference is still huge. It's obvious which version looks better, right?"
              },
              {
                src: "/training/white-balance/Screenshot%202025-12-03%20at%2000.12.44.png",
                alt: "WB example 1"
              },
              {
                src: "/training/white-balance/Screenshot%202025-12-03%20at%2000.12.57.png",
                alt: "WB example 2"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "wedding-scenarios",
            title: "⚙️ Typical Wedding Scenarios (White Balance)",
            darkBackground: true,
            content: `**Outdoor ceremony (golden hour)**
• **Setting:** Manual WB
• **WB:** **4800–5800K**
• **Why:** Balances the warm golden-hour light while keeping skin tones natural (not overly orange).

**Outdoor ceremony (midday sun)**
• **Setting:** Manual WB
• **WB:** **5200–6000K**
• **Why:** Matches daylight color temperature and keeps colors accurate.

**Indoor venue with LED lighting**
• **Setting:** Manual WB
• **WB:** **4000–5000K (starting point)**
• **Why:** LEDs vary widely; start around neutral and adjust by eye to avoid green/magenta shifts.
• **Tip:** If LEDs cast strong color (green/purple), adjust tint as well — not just Kelvin.

**Indoor venue with warm tungsten light**
• **Setting:** Manual WB
• **WB:** **2800–3400K**
• **Why:** Tungsten light is very warm; lower Kelvin neutralizes the orange cast.

**Evening party with fairy lights / ambient warm decor**
• **Setting:** Manual WB
• **WB:** **3000–3800K**
• **Why:** Keeps ambience warm but prevents skin from becoming too orange.

**Flash photography (reception / dancing)**
• **Setting:** Flash WB
• **WB:** **5200–5600K**
• **Why:** Flash is calibrated to daylight; these values keep skin tones natural even in mixed lighting.

**Mixed light (LED + daylight + practical lamps)**
• **Setting:** Manual WB
• **WB:** **4200–5000K (starting point)**
• **Why:** Mixed lighting shifts constantly; a neutral midpoint gives the cleanest result.
• **Tip:** Turn off strong colored lights if possible — they confuse the camera and WB.`,
            transitionTo: null,
          },
          {
            id: "pro-tips",
            title: "🔧 Pro Tips Nobody Talks About",
            darkBackground: true,
            content: `1. **Custom WB = Magic.** Use a grey card or white napkin before the ceremony to set exact WB for that space.

2. **Don't chase warmth in-camera.** A slightly cool base WB gives more flexibility in post.

3. **Avoid Auto WB for consistency.** It constantly adjusts — your shots will vary even within one series.

4. **Save WB presets for each venue type.** Many modern cameras let you store custom Kelvin values — use that!

5. **Flash and ambient light don't always match.** Use a **CTO gel** on your flash to match warm indoor lights.`,
            transitionTo: null,
          },
        ],
        referenceImages: [
          {
            src: "/training/white-balance/Screenshot%202025-12-03%20at%2002.49.24.png",
            alt: "White balance example 1"
          },
          {
            src: "/training/white-balance/Screenshot%202025-12-03%20at%2002.49.05.png",
            alt: "White balance example 2"
          },
          {
            src: "/training/white-balance/Screenshot%202025-12-03%20at%2002.49.44.png",
            alt: "White balance example 3"
          },
          {
            src: "/training/white-balance/Screenshot%202025-12-03%20at%2002.50.10.png",
            alt: "White balance example 4"
          },
          {
            src: "/training/white-balance/Screenshot%202025-12-03%20at%2002.52.05.png",
            alt: "White balance example 5",
            span: 2
          },
          {
            src: "/training/white-balance/Screenshot%202025-12-03%20at%2002.52.49.png",
            alt: "White balance example 6",
            span: 2
          },
        ],
        referenceColumns: 4,
      },
      {
        id: "focus",
        title: "FOCUS — Capturing Sharp Emotion and Movement",
        estimatedMinutes: 10,
        headerImage: {
          src: "/training/focus/Screenshot%202025-11-28%20at%2021.07.29.png",
          alt: "Focus module header"
        },
        sections: [
          {
            id: "what-it-is",
            title: "What It Is",
            darkBackground: true,
            content: `Focus determines *what part of your image is perfectly sharp*.

In weddings, focus isn't just technical — it's emotional. The look between the couple, a tear, a ring — if it's out of focus, the moment is gone.`,
            image: {
              src: "/training/focus/02.12.2025%2022.00.48%20716%20464.jpg",
              alt: "Focus example"
            },
            transitionTo: null,
          },
          {
            id: "why-it-matters",
            title: "💡 Why It Matters",
            darkBackground: true,
            content: `Weddings move fast. You can't ask the couple to repeat the kiss because your camera focused on the background.

Understanding **AF modes, focus points, and timing** makes the difference between a blurry photo and a timeless shot.`,
            galleryImages: [
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2020.28.30.png",
                alt: "Out of focus moment",
                caption: "{{In this photo, an important moment is lost due to the shot being out of focus.}}"
              },
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2020.33.51.png",
                alt: "Wrong focus point",
                caption: "{{In this photo, the focus is completely in the wrong place.}}"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "focus-modes",
            title: "Key Focus Modes",
            darkBackground: true,
            content: `• **AF-S / One-Shot AF:** Locks focus when you half-press the shutter — great for portraits or still shots.

• **AF-C / AI-Servo AF:** Continuously tracks moving subjects — perfect for walking down the aisle, dancing, or running kids.

• **Manual Focus:** Use rarely, but essential for detail shots (rings, textures, close-ups with shallow depth).`,
            galleryImages: [
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2021.59.21.png",
                alt: "Focus mode example 1"
              },
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2021.59.33.png",
                alt: "Focus mode example 2"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "af-points",
            title: "AF Points",
            darkBackground: true,
            content: `• **Single Point AF:** Maximum control — focus on eyes or faces.

• **Zone / Dynamic AF:** Camera chooses focus within a set area — useful for couples moving together.

• **Wide / Auto AF:** Camera guesses — avoid it for key moments, it often picks the wrong subject.`,
            transitionTo: null,
          },
          {
            id: "wedding-scenarios",
            title: "Wedding Scenarios",
            darkBackground: true,
            content: `**Portrait session**

• **Mode:** Manual Exposure + AF-S
• **Focus point:** On the nearest eye
• **Tip:** Recompose only slightly — or move focus point manually to avoid focus shift.`,
            galleryImages: [
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2021.49.44.png",
                alt: "Portrait focus example 1"
              },
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2022.04.00.png",
                alt: "Portrait focus example 2"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "first-dance",
            title: "First dance or walking down the aisle",
            darkBackground: true,
            content: `• **Mode:** Manual Exposure + AF-C
• **Focus area:** Wide or Zone
• **Shutter speed:** ≥ 1/250
• **Tip:** Keep half-press on shutter or use back-button focus for constant tracking.`,
            galleryImages: [
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2021.52.12.png",
                alt: "First dance focus example 1"
              },
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2022.04.28.png",
                alt: "First dance focus example 2"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "detail-shots",
            title: "Ring / Detail shots",
            darkBackground: true,
            content: `• **Mode:** Manual Exposure + Manual Focus
• **Lens:** Macro or prime wide open (f/1.8–f/2.8)
• **Tip:** Use live view + zoom in digitally to nail precision.`,
            galleryImages: [
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2021.46.47.png",
                alt: "Ring detail shot 1"
              },
              {
                src: "/training/focus/Screenshot%202025-11-28%20at%2021.47.39.png",
                alt: "Ring detail shot 2"
              },
            ],
            galleryColumns: 2,
            transitionTo: null,
          },
          {
            id: "pro-tips",
            title: "Pro Tips Nobody Talks About",
            darkBackground: true,
            content: `1. **Back-Button Focus** — assign AF to a rear button so you can lock or track without refocusing every shot.

2. **Eye Detection AF** — excellent for fast portraits, but test it on your camera; not all systems handle motion equally.

3. **Depth Check:** Always zoom in to check critical focus after the first few shots — better safe than sorry.

4. **Recalibrate lenses.** Even small front/back focus shifts can ruin your sharpness.

5. **Use focus peaking (if available).** Great visual aid when manual focusing during detail shots.`,
            transitionTo: null,
          },
        ],
        proTip: {
          icon: "✨",
          textLines: [
            "Pro tip: use AF-C for the majority of the day to avoid blurry photos. Practice using your focus on moving subjects before the event to understand how your camera works.",
            "Bonus Tip: Practice on friends and family! You can also build your portfolio this way."
          ],
          color: "#4a4a4a"
        },
        referenceImages: [
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.32.18.png",
            alt: "Focus reference example 1",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.35.38.png",
            alt: "Focus reference example 2",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.40.53.png",
            alt: "Focus reference example 3",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.32.44.png",
            alt: "Focus reference example 4",
            span: 3
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.42.53.png",
            alt: "Focus reference example 5",
            span: 3
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.39.17.png",
            alt: "Focus reference example 6",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.39.42.png",
            alt: "Focus reference example 7",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.44.40.png",
            alt: "Focus reference example 8",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.45.03.png",
            alt: "Focus reference example 9",
            span: 3
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.43.32.png",
            alt: "Focus reference example 10",
            span: 3
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.47.06.png",
            alt: "Focus reference example 11",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.44.19.png",
            alt: "Focus reference example 12",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.47.27.png",
            alt: "Focus reference example 13",
            span: 2
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.46.01.png",
            alt: "Focus reference example 14",
            span: 3
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.48.28.png",
            alt: "Focus reference example 15",
            span: 3
          },
          {
            src: "/training/focus/Screenshot%202025-11-29%20at%2017.40.08.png",
            alt: "Focus reference example 16",
            span: 6
          },
        ],
        referenceColumns: 6,
      },
      {
        id: "cameras-lenses",
        title: "CAMERAS & LENSES — Full-Frame vs Crop | Primes vs Zooms",
        estimatedMinutes: 8,
        headerImage: {
          src: "/training/camera-lenses/Screenshot%202025-12-03%20at%2000.55.46.png",
          alt: "Cameras and lenses header"
        },
        sections: [
          {
            id: "full-frame-vs-crop",
            title: "Full-Frame vs Crop Sensors",
            darkBackground: true,
            content: `**Full-frame cameras** (like the *Canon R5, Sony A7 IV, Nikon Z6 II, or Leica SL2*) have sensors the same size as a 35mm film frame.

They capture **more light**, offer **better dynamic range**, and produce **shallower depth of field** — that creamy background blur many love.

**Crop-sensor cameras** (APS-C) have smaller sensors, effectively *zooming in* on your image — great for wildlife or sports. They're cheaper, lighter, and still powerful. Think *Fujifilm X-T5* or *Canon R7*.`,
            callout: {
              icon: "💡",
              textLines: [
                "*Fun fact:* Many legendary photographers use full-frame cameras for the richness and depth of tones. Meanwhile, **street photographers** often prefer crop-sensor bodies — they're compact and discreet."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "primes-vs-zooms",
            title: "Primes vs Zooms",
            darkBackground: true,
            content: `**Prime lenses** (fixed focal length) are the *purest form* of photography.

They're often sharper, lighter, and let in more light (like a 50mm f/1.4). Perfect for portraits, low light, and artistic depth.

**Zoom lenses**, like 24–70mm or 70–200mm, offer flexibility. They're indispensable for travel or events where you can't move around easily.`,
            callout: {
              icon: "🕊️",
              textLines: [
                "*Henri Cartier-Bresson*, a master of decisive moments, used a **50mm prime** all his life. He believed limitation fuels creativity.",
                "On the other hand, modern creators like **Peter McKinnon** favor zooms for versatility and storytelling dynamics."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "camera-brands",
            title: "Camera Brands and Their Souls",
            darkBackground: true,
            content: `Each brand has its own philosophy:

• **Leica** — timeless craftsmanship, minimalism, and emotion. Photographers love it for the *connection between eye and hand*.

• **Sony** — innovative sensors, superb autofocus, hybrid tools for photo and video creators.

• **Canon** — warm color science, intuitive ergonomics, a classic all-rounder.

• **Nikon** — exceptional sharpness and dynamic range, beloved by portrait and landscape photographers.

• **Fujifilm** — nostalgic color profiles, beautiful design, and tactile controls.`,
            callout: {
              icon: "📚",
              textLines: [
                "*If you want to feel photography, not just do it,* try holding a **Leica M** once — it's like shaking hands with history."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "hidden-truths",
            title: "What People Don't Tell You",
            darkBackground: true,
            callouts: [
              {
                textLines: [
                  "**Sensor character** matters as much as megapixels.",
                  "A 24MP full-frame often looks richer than a 50MP crop."
                ],
                color: "#4a4a4a"
              },
              {
                textLines: [
                  "**Lens rendering** is art. Some lenses have \"soul\" — the way they handle color, flare, or bokeh."
                ],
                color: "#4a4a4a"
              },
              {
                textLines: [
                  "**The best camera is the one that inspires you.**",
                  "A cheap camera in creative hands beats a $10,000 setup without vision."
                ],
                color: "#5c3d3d"
              },
              {
                textLines: [
                  "**Experiment:** use vintage lenses with adapters. The imperfections can create beauty digital perfection can't."
                ],
                color: "#4a4a4a"
              }
            ],
            blockquote: `"Cameras are like brushes. It's not about the price — it's about the painter."`,
            blockquoteTransparent: true,
            transitionTo: null,
          },
        ],
      },
      {
        id: "filters-accessories",
        title: "Filters & Accessories — Light Shaping Tools",
        estimatedMinutes: 10,
        hook: '"Making Ordinary Light Extraordinary"',
        headerLayout: "side-by-side",
        headerImage: {
          src: "/training/filters-accessories/IMG_8116.jpg",
          alt: "Filters and accessories header"
        },
        sections: [
          {
            id: "what-they-are",
            title: "What They Are",
            darkBackground: true,
            content: `Filters and accessories help photographers **shape, control, and refine light** — before it even reaches the sensor.

They're the bridge between the technical and the creative, letting you turn harsh light into softness, or dull scenes into cinematic stories.`,
            transitionTo: null,
          },
          {
            id: "nd-filters",
            title: "ND (Neutral Density) Filters",
            darkBackground: true,
            content: `Reduce the amount of light entering the lens **without changing color**.

Perfect for **shooting wide open (f/1.4–f/2.8)** even in bright daylight — keeping that dreamy depth of field.

**Example:**
Shooting the bride outdoors at noon — use ND 3–6 stops to keep your shutter speed around 1/250 and maintain soft background blur.`,
            callout: {
              icon: "💡",
              textLines: [
                "**Pro Tip:** ND filters come in fixed (e.g., ND8) or variable forms. Variable ND saves time but can cause cross-polarization artifacts on ultra-wide lenses."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "polarizing-filter",
            title: "Polarizing Filter",
            darkBackground: true,
            content: `Reduces reflections from **glass, water, and shiny skin**, while deepening blue skies and greens.

Ideal for outdoor weddings, lake scenes, or sunny garden receptions.

Many editorial wedding photographers use CPLs to add richness and clarity without extra editing.`,
            callout: {
              icon: "⚠️",
              textLines: [
                "**Be careful indoors** — polarizers cut light, so use them mainly in natural light."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "diffusers",
            title: "Diffusers",
            darkBackground: true,
            content: `Soften harsh sunlight or flash by spreading the light evenly.

Simple white umbrellas or silk diffusers can turn noon sun into a gentle, glowing light — ideal for portraits.

Pro photographers often use scrims or even **a white bedsheet** if nothing else is available — improvisation matters.`,
            transitionTo: null,
          },
          {
            id: "reflectors",
            title: "Reflectors",
            darkBackground: true,
            content: `Bounce light back onto the subject to **fill in shadows**.

**Silver** = strong contrast
**Gold** = warm tone
**White** = neutral, soft

Perfect for backlit shots of couples.`,
            transitionTo: null,
          },
          {
            id: "flash-softboxes",
            title: "Flash & Softboxes",
            darkBackground: true,
            content: `Flashes give you control when ambient light fails — from **TTL on-camera units** to **off-camera setups** with softboxes.

Softboxes diffuse light, creating a studio feel even at a wedding.`,
            callout: {
              icon: "💡",
              textLines: [
                "**Tip:** You can practice balancing flash with ambient light so your shots don't look \"flashed.\""
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "creative-filters",
            title: "Creative & Optional Filters",
            darkBackground: true,
            content: `**UV Filter**
Mostly used today as **lens protection**. It doesn't affect colors much but saves your lens from scratches.

**Black Mist / Diffusion Filter**
A favorite of photographers like **Greg Williams** — softens highlights, smooths skin, and adds that cinematic glow.

Perfect for emotional, romantic shots — but don't overdo it.

**Graduated ND Filter**
Darkens skies while keeping the ground bright — ideal for sunset portraits or landscape-style shots.

Balances exposure between heaven and earth — literally.`,
            callout: {
              icon: "🕵️‍♀️",
              title: "A Little Secret",
              textLines: [
                "You can apply a **thin layer of vaseline** around the edges of a clear filter to create a soft-focus, dreamy effect — but this trick should **never be used for classic wedding shots** unless the couple approves in advance."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/filters-accessories/IMG_7775.jpg",
                alt: "Filter example 1"
              },
              {
                src: "/training/filters-accessories/IMG_7761.jpg",
                alt: "Filter example 2"
              },
              {
                src: "/training/filters-accessories/IMG_7732.jpg",
                alt: "Filter example 3"
              },
            ],
            transitionTo: null,
          },
          {
            id: "summary",
            title: "In Summary",
            darkBackground: true,
            blockquote: `Filters and accessories are like your light palette — subtle tools that transform the mood of the image. Mastering them separates an average photo from a timeless one.`,
            blockquoteTransparent: true,
            transitionTo: null,
          },
        ],
      },
      {
        id: "bokeh",
        title: "Bokeh & Lens Rendering",
        estimatedMinutes: 12,
        sections: [
          {
            id: "what-is-bokeh",
            title: "💡 What Actually Is Bokeh?",
            darkBackground: true,
            callout: {
              textLines: [
                "Bokeh is simply the *personality of blur*.",
                "",
                "Not how blurry the background is — but how that blur *feels*.",
                "",
                "It's the softness, the mood, the way light melts behind your subject.",
                "",
                "**A good lens doesn't just blur the background — it paints with it.**"
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/bokeh/Screenshot 2025-12-03 at 02.34.24.png",
                alt: "Bokeh example 1",
                span: 3
              },
              {
                src: "/training/bokeh/Screenshot 2025-12-03 at 02.39.11.png",
                alt: "Bokeh example 2",
                span: 3
              },
              {
                src: "/training/bokeh/Screenshot 2025-12-03 at 03.11.40.png",
                alt: "Bokeh example 3",
                span: 3
              },
              {
                src: "/training/bokeh/Screenshot 2025-12-03 at 03.16.47.png",
                alt: "Bokeh example 4",
                span: 3
              },
            ],
            transitionTo: null,
          },
          {
            id: "good-vs-awkward",
            title: "Good Bokeh vs Awkward Bokeh",
            darkBackground: true,
            content: `**Good, pretty bokeh**
• Smooth circles of light
• Soft transitions
• No harsh lines
• Background melts in layers
• Subject looks **framed**, not cut out

**Ugly, "plastic" bokeh**
• Harsh, busy patterns
• Crazy outlines around light circles
• Too-perfect blur that looks like a phone portrait mode
• Subject looks pasted onto a blurred wall`,
            transitionTo: null,
          },
          {
            id: "creamy-lenses",
            title: "Why Do Some Lenses Look Creamy & Others Look Fake?",
            darkBackground: true,
            content: `**1️⃣ Aperture shape**
More blades = round, soft bokeh
Fewer blades = polygonal shapes

**2️⃣ Vintage vs modern**
Vintage = dreamy, soft, romantic
Modern = razor sharp, sometimes too "clinical"

**3️⃣ Focal length**
85mm, 105mm, 135mm → smoother compression
35mm, 28mm → more distortion + busier bokeh

**4️⃣ Background distance**
If background is close → messy
If background is far → dreamy

*One of the most important bokeh secrets no one talks about!*`,
            callout: {
              icon: "⚡",
              textLines: [
                "**The distance behind your subject often matters more than the aperture.**"
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "bokeh-for-weddings",
            title: "Bokeh for Wedding Photography",
            darkBackground: true,
            content: `**Wedding-specific bokeh tips:**

• **For romantic portraits** → 85mm or 135mm is your best friend
• **For bride details** → 50mm gives a cleaner, neutral bokeh
• **For golden hour** → place the sun behind the couple → glowing circles
• **For messy forests** → don't shoot wide open → f/2.8 is cleaner
• **On dancefloor** → bokeh isn't everything → light placements matter more

*You can often use 35mm and still get beautiful bokeh by placing your subjects far from the background and using light cleverly.*`,
            transitionTo: null,
          },
          {
            id: "famous-lenses",
            title: "Famous Lenses Known for Delicious Bokeh",
            darkBackground: true,
            content: `**Premium bokeh lenses:**

• Canon RF 85mm f/1.2
• Sony 50mm f/1.2 GM
• Fujifilm 56mm f/1.2
• Nikon 105mm f/1.4
• Leica 50mm Noctilux
• Helios 44-2 (dreamy, swirly chaos — artistic, not for weddings unless intentional)`,
            image: {
              src: "/training/bokeh/_MG_1669.jpg",
              alt: "Bokeh example with beautiful lens rendering"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "metering-modes",
        title: "Metering Modes — The Camera Brain",
        estimatedMinutes: 12,
        hook: "Understanding how your camera decides what to expose",
        hookStyle: "callout",
        headerLayout: "side-by-side",
        headerImage: {
          src: "/training/metering-modes/03.12.2025 06.42.36 837 31.jpg",
          alt: "Metering modes header"
        },
        sections: [
          {
            id: "the-problem",
            title: "The Problem",
            darkBackground: true,
            content: `Most photographers learn ISO, Shutter, Aperture…

But almost nobody learns **how the camera decides WHAT to expose**.

And that's exactly why so many wedding photos end up:
• dark faces,
• blown-out skies,
• muddy indoor ceremonies,
• silhouettes that weren't artistic, just mistakes.

Metering = **the camera's logic system**.

It answers one question:

**"What part of this scene should I expose correctly?"**`,
            transitionTo: null,
          },
          {
            id: "evaluative-matrix",
            title: "✦ 1. Evaluative / Matrix — 'Smart mode'",
            darkBackground: true,
            content: `Camera looks at EVERYTHING and tries to be clever.

It analyses:
• faces
• background light
• contrast
• colors

Think of it like having a very experienced assistant who says:
*"Calm down, I've seen this before, I got it."*

**Best for:** weddings in motion — walking, hugging, outdoor sun, candid moments.

**Weakness:** sometimes tries too hard to balance and makes the couple darker than needed.`,
            callout: {
              icon: "💡",
              title: "Fun fact:",
              textLines: [
                "Sony cameras literally prioritise skin tones in this mode.",
                "Canon tries to protect foreground subjects.",
                "Nikon tends to protect highlights (sky, window light)."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "center-weighted",
            title: "✦ 2. Center-weighted — 'Classic logic'",
            darkBackground: true,
            content: `Camera says:
*"I'll check the whole scene… but middle is most important."*

Perfect when the couple is centered:
• vows
• ring exchange
• first kiss
• walking down the aisle

**Weakness:**
If light is coming strongly from the side, this mode gets confused.`,
            transitionTo: null,
          },
          {
            id: "spot-metering",
            title: "✦ 3. Spot — 'Sniper mode'",
            darkBackground: true,
            content: `Camera exposes ONLY the tiny spot where you focus.

Like a flashlight on a small target.

**The risk everyone forgets:**
Put spot on the bride's white dress → faces become dark.
Put spot on groom's black suit → faces overexpose.

Spot mode is powerful but unforgiving.

Once, celebrity photographer said:
*"Spot metering will expose EXACTLY what you choose — so choose carefully."*

It's perfect for dramatic moments:
• sun rays hitting only the bride's cheek
• window light in a dark room
• backlit hair in golden hour

But not stable for fast movement.`,
            transitionTo: null,
          },
          {
            id: "real-scenarios",
            title: "Real Wedding Scenarios (Easy + Practical)",
            darkBackground: true,
            callouts: [
              {
                icon: "☀️",
                title: "Outdoor sunny ceremony",
                textLines: [
                  "**Use:** Evaluative",
                  "**WHY:** balanced exposure, face detection works",
                  "**TIP:** +0.3 to +0.7 compensation to avoid dark skin tones"
                ],
                color: "#5c3d3d"
              },
              {
                icon: "🌅",
                title: "Golden hour backlight",
                textLines: [
                  "**Use:** Spot",
                  "**WHY:** you want the face to be correctly exposed, not the sky",
                  "**Effect:** background becomes dreamy + blown out (in a good way)"
                ],
                color: "#5c3d3d"
              },
              {
                icon: "💃",
                title: "First dance with spotlight",
                textLines: [
                  "**Use:** Center-weighted",
                  "**WHY:** spotlight moves, but couple stays \"center-ish\""
                ],
                color: "#5c3d3d"
              },
              {
                icon: "⛪",
                title: "Dark church",
                textLines: [
                  "**Use:** Evaluative",
                  "**WHY:** Matrix handles mixed lighting better",
                  "**TIP:** add +1 EV to avoid muddy midtones"
                ],
                color: "#5c3d3d"
              }
            ],
            transitionTo: null,
          },
          {
            id: "common-mistakes",
            title: "Mistakes EVERYONE Makes (and Nobody Warns You)",
            darkBackground: true,
            callouts: [
              {
                icon: "⚠️",
                title: "Mistake #1",
                textLines: ["Spot metering + Eye-AF = exposure jumps if focus accidentally hits eyebrow or hair."],
                color: "#4a4a4a"
              },
              {
                icon: "⚠️",
                title: "Mistake #2",
                textLines: ["Metering on WHITE → underexposed faces."],
                color: "#4a4a4a"
              },
              {
                icon: "⚠️",
                title: "Mistake #3",
                textLines: ["Metering on BLACK → blown-out faces."],
                color: "#4a4a4a"
              },
              {
                icon: "⚠️",
                title: "Mistake #4",
                textLines: ["Changing composition but forgetting metering mode is still set to Spot."],
                color: "#4a4a4a"
              }
            ],
            transitionTo: null,
          },
          {
            id: "unique-insights",
            title: "Unique Insights (Rarely Discussed but Helpful)",
            darkBackground: true,
            content: `• Bridesmaids' dresses can trick metering if they are too bright or too dark.

• In harsh midday sun, **Evaluative tends to underexpose intentionally**, assuming you will lift shadows in RAW.

• Fuji's metering is heavily midtone-biased, giving softer, film-like exposure decisions.`,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "✨ The Real Secret",
            darkBackground: true,
            callout: {
              textLines: [
                "Metering is **not about accuracy**, it's about priority.",
                "",
                "You choose what matters:",
                "• face,",
                "• highlight edge,",
                "• dress texture,",
                "• background detail.",
                "",
                "Once you choose — your exposure becomes intentional."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "histogram",
        title: "Histogram & Exposure Check — How to Read It & Avoid Blowing Highlights",
        estimatedMinutes: 14,
        sections: [
          {
            id: "what-histogram-shows",
            title: "1. What the Histogram Shows",
            darkBackground: true,
            callout: {
              icon: "⚡",
              title: "The histogram is your camera's lie detector.",
              textLines: [
                "Your screen may lie, brightness may be maxed out, your eyes may adapt to dark rooms — but the histogram will always tell the truth."
              ],
              color: "#4a4a4a"
            },
            sideByeSideImage: {
              src: "/training/histogram/file_00000000299c71f5a386e5105eb7d7da.png",
              alt: "Histogram diagram"
            },
            content: `A histogram is a **graph of light distribution** in your image:

• **Left side:** shadows (dark tones)
• **Middle:** midtones
• **Right side:** highlights (bright areas)

The taller the spike — the more pixels in that brightness range.

**One glance tells you:**
→ Are the shadows crushed (graph stuck on the left)?
→ Are the highlights blown (graph slammed to the right)?`,
            transitionTo: null,
          },
          {
            id: "blown-highlights",
            title: "2. Blown Highlights — The Worst-Case Scenario",
            darkBackground: true,
            content: `Shadows can usually be lifted.
Noise can be handled.
But **blown highlights are gone forever** — no detail, no texture.

**How to recognize:**
→ Histogram touches the right edge and clips.
→ Camera shows blinking alert (blinkies / zebras).

This is the "pure white" zone — and it's unrecoverable.`,
            transitionTo: null,
          },
          {
            id: "crushed-shadows",
            title: "3. Crushed Shadows — Not As Bad",
            darkBackground: true,
            content: `A histogram glued to the left edge means you've got pure black.

In RAW, you often *can* recover shadow detail.
In JPEG — difficult, but not tragic.`,
            callout: {
              icon: "💡",
              textLines: [
                "**Overall: blown highlights = dead image. Crushed shadows = recoverable.**"
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "ettr-explained",
            title: "4. Exposure to the Right (ETTR) — What It Is & Why It Matters",
            darkBackground: true,
            content: `ETTR means **exposing so your histogram moves as far right as possible, without touching the edge**.

**Why?**
→ RAW sensors gather the most info in the brighter part
→ Brighter exposure = cleaner shadows
→ Less noise overall
→ Maximum data for post-processing`,
            callout: {
              icon: "🎯",
              textLines: [
                "**ETTR is the highest-quality way to shoot RAW.**"
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "ettr-practical",
            title: "5. How to Use ETTR Practically",
            darkBackground: true,
            content: `**Step-by-step process:**

1. Set ISO as low as possible
2. Adjust shutter/aperture to push the graph toward the right
3. Stop when the highlights are *close* — but not clipped
4. Shoot

**Result:**
→ Maximum information in highlights
→ Cleaner shadows
→ Flexible editing in post`,
            transitionTo: null,
          },
          {
            id: "raw-vs-jpeg",
            title: "6. RAW vs JPEG — Different Rules",
            darkBackground: true,
            content: `**RAW loves ETTR**
You can push exposure down later without harming image quality.

**JPEG hates ETTR**
JPEG is already contrasty and fragile.
Highlights blow fast and irreversibly.

**So:**
→ JPEG – better underexpose slightly
→ RAW – better expose to the right (not necessarily overexpose, just push the histogram right)`,
            transitionTo: null,
          },
          {
            id: "color-histograms",
            title: "7. Reading Color Histograms",
            darkBackground: true,
            content: `**Two types:**

**RGB histogram** — shows clipping in each color channel
(important for neon lights, sunsets, stage lighting)

**Luma histogram** — shows overall brightness

**A tricky situation:**
Overall histogram looks fine, but **red channel is clipped** → skin turns weird magenta.

**Solution:**
→ When shooting colorful light, check RGB histograms.`,
            transitionTo: null,
          },
          {
            id: "zebras",
            title: "8. Zebra / Highlight Warnings — Fastest Exposure Guide",
            darkBackground: true,
            content: `Turn on Zebras (95–98%).
They show **where potential clipping happens** — in real time.

**Correct use:**
→ Let zebras appear in the *background* — that's okay
→ Avoid zebras on important subjects (skin, dress details, flowers, rings)
→ Adjust exposure until zebras disappear from critical areas`,
            transitionTo: null,
          },
          {
            id: "cheat-sheet",
            title: "9. Quick Cheat Sheet",
            darkBackground: true,
            content: `→ Always check the right edge
→ If it's clipped → reduce exposure
→ If shadows are crushed → decide if it's stylistic or accidental
→ RAW: use ETTR safely
→ JPEG: avoid bright exposure
→ Colorful lighting → check RGB
→ [How to Read a Histogram](https://youtube.com/shorts/r4c06TsdYa0?si=N_DgsCSOP_xi_4fs)`,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "10. Little-Known Fact: Your Preview is NOT RAW",
            darkBackground: true,
            content: `What you see on your camera screen is a **JPEG preview**, even when shooting RAW.

**That means:**
→ The camera shows "clipping" based on the JPEG version
→ RAW might still have detail
→ Zebras react to JPEG processing, not true RAW data`,
            callout: {
              icon: "💡",
              textLines: [
                "**So ETTR often *looks* overexposed but is correct in the RAW file.**"
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "long-exposure",
        title: "Long Exposure & Motion Blur",
        estimatedMinutes: 12,
        hook: "This is where photography becomes time sculpture. Long exposure allows you to capture what the eye cannot see — the passage of time.",
        sections: [
          {
            id: "what-it-is",
            title: "1. What Long Exposure Really Is",
            darkBackground: true,
            callout: {
              icon: "💡",
              textLines: [
                "**Long exposure** = shutter open for 1/4 sec, 1 sec, 5 seconds, 30 seconds, or minutes.",
                "**Motion blur** = the artistic trail created by moving subjects.",
                "With long exposure you're not photographing a moment — you're photographing **time**."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/long-exposure/photo_2025-12-05 12.53.42.jpeg",
                alt: "Long exposure example 1",
                span: 2
              },
              {
                src: "/training/long-exposure/photo_2025-12-05 12.31.27.jpeg",
                alt: "Long exposure example 4",
                span: 2,
                stackWith: {
                  src: "/training/long-exposure/photo_2025-12-05 12.53.29.jpeg",
                  alt: "Long exposure example 3"
                }
              },
              {
                src: "/training/long-exposure/photo_2025-12-05 12.53.40.jpeg",
                alt: "Long exposure example 2",
                span: 2
              },
            ],
            transitionTo: null,
          },
          {
            id: "silky-water",
            title: "2. Silky Water — The Iconic Landscape Cliché (For a Reason)",
            darkBackground: true,
            content: `**Used for:**
• Waterfalls
• Rivers
• Sea
• Fountains`,
            callout: {
              icon: "⚙️",
              title: "Settings:",
              textLines: [
                "• Manual mode",
                "• 1–5 seconds",
                "• ISO 100",
                "• f/8–f/16",
                "• Tripod",
                "• ND filter if sunny",
                "",
                "**Result:** dreamy, soft, flowing texture"
              ],
              color: "#3d4a5c"
            },
            transitionTo: null,
          },
          {
            id: "light-painting",
            title: "3. Light Painting — Drawing with Light",
            darkBackground: true,
            content: `One of the most artistic uses of long exposure.

**Tools:**
• Flashlight
• LED strips
• Sparklers
• Phone screen
• Colored gels`,
            callout: {
              icon: "📖",
              title: "History trivia:",
              textLines: [
                "In 1949, photographer **Gjon Mili** invited **Pablo Picasso** to \"draw with light,\" creating the legendary light painting series for LIFE Magazine."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "motion-blur-weddings",
            title: "4. Motion Blur for Weddings",
            darkBackground: true,
            content: `**Great for:**
• Dance floor
• Guests running with sparklers
• Dynamic party moments
• The groom spinning the bride`,
            callout: {
              icon: "💬",
              textLines: [
                "**Explain to couple:** motion blur = intentional emotion, not mistake."
              ],
              color: "#3d4a5c"
            },
            transitionTo: null,
          },
          {
            id: "dramatic-blur",
            title: "5. Dramatic Blur",
            darkBackground: true,
            content: `**Best for:**
• Chaos
• Laughter
• Dancing
• Excitement`,
            callout: {
              icon: "⚙️",
              title: "Settings:",
              textLines: [
                "• Manual",
                "• 1/10–1/5",
                "• Moderate ISO",
                "• Use IBIS if available"
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "rear-curtain-sync",
            title: "6. Rear Curtain Sync — Secret Cinematic Magic",
            darkBackground: true,
            content: `Flash fires at the **end** of the exposure.

**Effect:**
• Light trails behind the subject
• Subject frozen at the end
• Dynamic, energetic feel`,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "7. Secrets Professionals Don't Talk About",
            darkBackground: true,
            callouts: [
              {
                icon: "🔑",
                title: "You DON'T always need a tripod",
                textLines: ["Handheld long exposure can create organic motion."],
                color: "#4a4a4a"
              },
              {
                icon: "🔑",
                title: "Avoid ghosting",
                textLines: ["Ask the subject to remain still for a second."],
                color: "#4a4a4a"
              },
              {
                icon: "🔑",
                title: "Always shoot RAW at night",
                textLines: ["More recoverable shadows."],
                color: "#4a4a4a"
              },
              {
                icon: "🔑",
                title: "Avoid reflective clothing",
                textLines: ["It creates messy highlights."],
                color: "#4a4a4a"
              }
            ],
            galleryImages: [
              {
                src: "/training/long-exposure/photo_2025-12-05 12.53.48.jpeg",
                alt: "Long exposure example",
                span: 3
              },
              {
                src: "/training/long-exposure/photo_2025-12-05 12.53.50.jpeg",
                alt: "Long exposure example",
                span: 3
              },
            ],
            transitionTo: null,
          },
        ],
      },
      {
        id: "flash-sync",
        title: "Flash Sync & Shutter Speed",
        estimatedMinutes: 15,
        hook: "Balancing Flash with Ambient Light (HSS Explained)",
        sections: [
          {
            id: "what-is-this",
            title: "What This Is About",
            darkBackground: true,
            callout: {
              icon: "🔍",
              textLines: [
                "**Flash sync** = the relationship between the camera's shutter and the moment the flash fires.",
                "",
                "If you push shutter faster than your camera's flash-sync speed (usually ~1/200–1/250s), the shutter curtain will block part of the sensor — unless you use **High-Speed Sync (HSS)**. HSS lets you use flash at very fast shutter speeds, but with trade-offs.",
                "",
                "This topic is essential for wedding photographers because you often need to:",
                "- freeze movement with flash,",
                "- keep ambient exposure (bright daylight or dramatic backlight),",
                "- and create natural-looking portraits without ugly black bands."
              ],
              color: "#4a4a4a"
            },
            image: {
              src: "/training/flash-sync/IMG_2534.jpg",
              alt: "Flash sync example"
            },
            transitionTo: null,
          },
          {
            id: "basic-concepts",
            title: "Basic concepts",
            darkBackground: true,
            content: `- **Flash Sync Speed (X-sync)** — maximum shutter speed at which the flash can light the entire frame with a single burst (commonly 1/200 or 1/250).
- **Front-Curtain (1st) Sync** — flash fires at the start of exposure (default behavior). Good for freezing motion.
- **Rear-Curtain (2nd) Sync** — flash fires at the end of exposure — creates motion trails *behind* the moving subject (more natural-looking motion).
- **High-Speed Sync (HSS)** — flash pulses very rapidly during the exposure so you can use shutter speeds faster than X-sync. HSS reduces effective flash power and reach.
- **TTL vs Manual Flash** — TTL is fast and adaptive (useful for run-and-gun), manual gives consistency and control (preferred for crafted lighting).`,
            transitionTo: null,
          },
          {
            id: "why-weddings",
            title: "Why it matters for weddings",
            darkBackground: true,
            content: `- **Outdoor portraits at wide aperture**: You want pretty bokeh (f/1.8) plus fill-flash to lift faces — but it's bright outside, so shutter must be fast. HSS lets you keep wide aperture + balanced flash.
- **Backlit couple at golden hour**: Use HSS or ND + standard sync to keep dreamy background while filling faces.
- **Dance floor**: Use rear-curtain sync + slower shutter (1/15–1/60) + flash to get ambient trails + crisp subject.`,
            transitionTo: null,
          },
          {
            id: "practical-combos",
            title: "Practical combos & starting points",
            darkBackground: true,
            content: `*Manual mode recommended*

**Scenario: Golden hour couple portrait (wide aperture, bright background)**
- Camera: Manual
- Mode: HSS on (if using) OR use ND filter + X-sync
- Shutter: 1/250–1/640 (HSS allows >1/250)
- Aperture: f/1.8–f/2.8
- ISO: 100–400
- Flash: TTL or Manual (start at -0.3 EV TTL)
- Trigger: off-camera preferred, soft modifier (mini softbox)

**Scenario: Backlit bride in front of bright window**
- Shutter: 1/200 (X-sync) or HSS if wider aperture needed
- Aperture: f/1.8–f/2.8
- Flash: soft fill, rear-curtain optional for movement

**Scenario: First dance (ambient trails + crisp subject)**
- Shutter: 1/30–1/60
- Aperture: f/2.8–f/4
- ISO: 800–3200
- Flash: rear-curtain sync, manual power to avoid recycling delays

**Scenario: Sunlit outdoor group photo (freeze motion, lots of light)**
- Shutter: 1/250 (X-sync)
- Aperture: f/5.6–f/8
- ISO: 100–200
- Flash: fill, manual or TTL`,
            transitionTo: null,
          },
          {
            id: "pro-tips",
            title: "Pro tips & things few mention",
            darkBackground: true,
            callout: {
              icon: "🧠",
              textLines: [
                "**1. HSS reduces power and range.** You'll often need to raise ISO or move the flash closer — HSS eats effective guide number.",
                "",
                "**2. Use ND instead of HSS for pure quality.** If you can (and prefer full flash power and cleaner light), add ND filter and stay at X-sync — better light quality, no chatter from HSS.",
                "",
                "**3. Rear-curtain for natural trails.** For moving subjects on dance floors, rear-curtain sync makes motion trails fall behind the subject, so the frozen subject looks like it's *in front* of the motion. First-curtain makes trails *in front* — often looks unnatural.",
                "",
                "**4. Mind recycle time at events.** Manual flash with consistent power (and faster recycle) beats TTL's small surprise exposures when action is continuous.",
                "",
                "**5. Gel your flash to match ambient.** Use CTO/CTB gels to match tungsten daylight and avoid odd color mixes. Gels + flash can also create stylized looks. **(PRO)**",
                "",
                "**6. Test before ceremony.** Know your camera's X-sync and flash HSS behavior before you're in the aisle.",
                "",
                "**7. Watch for high shutter + flash on some mirrorless cameras.** Some bodies behave strangely with electronic first curtain — test EFC (electronic front curtain), or turn it off for critical work."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "troubleshooting",
            title: "Troubleshooting checklist",
            darkBackground: true,
            content: `- **Black band across frame** → shutter faster than X-sync (and HSS off) → slow shutter to X-sync or enable HSS.
- **Flash underpowered in daylight** → enable HSS (reduces power) OR add ND to use full-power flash at X-sync.
- **Weird color casts** → check white balance and gels; TTL tries to compensate unpredictably.
- **Motion trails in front of subjects during dance** → switch to **rear-curtain**.`,
            transitionTo: null,
          },
          {
            id: "summary",
            title: "Quick mindset for weddings",
            darkBackground: true,
            content: `- **Prefer consistent manual flash** when you can control background light.
- **Use HSS** when you must keep very wide aperture in bright light — but accept compromises.
- **Use ND + X-sync** for best image quality when possible.`,
            galleryImages: [
              {
                src: "/training/flash-sync/Screenshot 2025-12-03 at 04.07.43.png",
                alt: "Flash sync example",
                span: 2
              },
              {
                src: "/training/flash-sync/Screenshot 2025-12-03 at 04.05.40.png",
                alt: "Flash sync example",
                span: 2,
                stackWith: {
                  src: "/training/flash-sync/Screenshot 2025-12-03 at 04.06.17.png",
                  alt: "Flash sync example"
                }
              },
              {
                src: "/training/flash-sync/Screenshot 2025-12-03 at 04.07.15.png",
                alt: "Flash sync example",
                span: 2
              },
              {
                src: "/training/flash-sync/Screenshot 2025-12-03 at 00.56.28.png",
                alt: "Flash sync example",
                span: 3
              },
              {
                src: "/training/flash-sync/Screenshot 2025-12-03 at 04.02.41.png",
                alt: "Flash sync example",
                span: 3
              },
            ],
            transitionTo: null,
          },
        ],
      },
      {
        id: "sensor-cleaning",
        title: "Sensor Cleaning & Maintenance",
        estimatedMinutes: 10,
        hook: "Either way, the best solution is to use the help of professionals.",
        sections: [
          {
            id: "truth-1",
            title: "Truth №1",
            darkBackground: true,
            callout: {
              icon: "✓",
              title: "Dust is not the apocalypse.",
              textLines: [
                "You see it only at **f/11–f/16** and on flat backgrounds.",
                "At weddings you shoot **f/1.2–f/2.8** most of the day → dust is almost invisible."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "truth-2",
            title: "Truth №2",
            darkBackground: true,
            callout: {
              icon: "✓",
              title: "You are not touching the actual sensor.",
              textLines: [
                "You touch the **protective filter on top of it**.",
                "This makes cleaning much safer than beginners think."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "level-1",
            title: "Level 1 – Dry / blower cleaning",
            darkBackground: true,
            content: `**Most common and safest:**

- Use a blower (NOT canned air)
- Hold camera facing downward
- Blow gently 10–20 times
- No touching, no tools
- Test shot at f/16`,
            callout: {
              icon: "📌",
              title: "Hidden truth:",
              textLines: [
                "Canned air contains propellant → it can leave oily spots on the sensor."
              ],
              color: "#3d4a5c"
            },
            transitionTo: null,
          },
          {
            id: "level-2",
            title: "Level 2 – Wet cleaning",
            darkBackground: true,
            content: `**For stubborn spots or stains:**

- Buy proper sensor swabs
- Use Eclipse / E2 cleaning fluid
- Lock the sensor up
- One smooth swipe per side
- Don't re-use the swab`,
            callout: {
              icon: "📌",
              title: "Tip:",
              textLines: [
                "Slight streaks disappear after the second swipe."
              ],
              color: "#3d4a5c"
            },
            transitionTo: null,
          },
          {
            id: "level-3",
            title: "Level 3 – Professional cleaning",
            darkBackground: true,
            content: `**When needed:**
- Sticky stains
- Oil spots
- Scratches
- Strange fixed dots

Older DSLRs sometimes had shutter lubrication splatter (D600, early Canon).

Mirrorless cameras rarely suffer from this.`,
            transitionTo: null,
          },
          {
            id: "wedding-specific",
            title: "Wedding-specific reasons to clean",
            darkBackground: true,
            content: `- Blue sky shots easily reveal dust
- First dance at f/1.4 hides everything
- If you don't clean → editing will be slower

**Do a cleaning *before the wedding day*, not during the wedding.**`,
            transitionTo: null,
          },
          {
            id: "insights",
            title: '"Rarely discussed" insights',
            darkBackground: true,
            callouts: [
              {
                icon: "🔹",
                title: "1: Lens dust = harmless",
                textLines: ["Only sensor dust matters."],
                color: "#4a4a4a"
              },
              {
                icon: "🔹",
                title: "2: Sensor cleaning is easy, but requires focus",
                textLines: ["If you're nervous — do Level 1 or go to a pro."],
                color: "#4a4a4a"
              },
              {
                icon: "🔹",
                title: "3: Sensor-shake mode is not enough",
                textLines: ["It removes only loose particles."],
                color: "#4a4a4a"
              },
              {
                icon: "🔹",
                title: "4: Static dry air increases dust",
                textLines: ["Humidity keeps particles down."],
                color: "#4a4a4a"
              }
            ],
            transitionTo: null,
          },
          {
            id: "summary",
            title: "Photographers / references",
            darkBackground: true,
            content: `- **Joe McNally** recommends cleaning **before big events**
- **Annie Leibovitz's** assistants cleaned cameras daily on campaigns`,
            callout: {
              icon: "⚠️",
              textLines: [
                "**Either way, the best solution is to use the help of professionals.**"
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "natural-light",
        title: "Natural Light — Reflection, Diffusion, Direction",
        estimatedMinutes: 25,
        hook: "As soon as you think about light, you're thinking like a photographer. — Greg Williams",
        sections: [
          {
            id: "intro",
            title: "Reading the Light",
            darkBackground: true,
            imageAboveContent: "/training/natural-light/Screenshot 2025-11-27 at 02.24.46.png",
            imageAboveContentAlt: "Natural light introduction",
            content: `Every time you step into a space, **read the light** before you touch your camera.

Look for its **source**, its **angle**, its **size**, and how far it is from your subject.

These four factors — **size, angle, diffusion, and distance** — are the foundations of all light, whether it's sunlight, moonlight, a window, indoor light or a flash.

And above all, remember:`,
            callout: {
              icon: "✨",
              textLines: [
                "**\"When I walk into a room, I imagine that all this light has been given to me — what am I going to do with it?\"**",
                "— *Greg Williams*"
              ],
              color: "#5D4037"
            },
            contentAfter: `This mindset transforms you from a person taking pictures into a photographer creating stories.`,
            transitionTo: null,
          },
          {
            id: "direction",
            title: "1. Direction — the angle of light",
            darkBackground: true,
            content: `In professional photography, we often talk about the **keylight** — the dominant source of light in a scene.

Outdoors it's usually the **sun**, but not always: by a campfire, the flame becomes the keylight; in a street night scene, it might be headlights; in a wedding hall, perhaps a chandelier or even a window.

Once you identify your keylight, you can place your subject in any relationship to it — frontlit, sidelited, or backlit — and create entirely different emotions, textures, and stories.`,
            transitionTo: null,
          },
          {
            id: "front-light",
            title: "1) Front Light",
            darkBackground: true,
            content: `**Technique:**
Light source is directly behind you, hitting the subject head-on.

**Visual effect:**
- Minimal shadows
- Smooth skin
- Clean, flat look
- Soft and gentle, but not sculpted

**Best for:**
- Beauty portraits
- Details
- Even light situations

**Issue:**
- Eyes squint in harsh sun
- Highlights blow out`,
            callout: {
              icon: "💡",
              title: "Tip:",
              textLines: [
                "Move the couple into soft shade, not deep shadow. Examples:",
                "- under a café awning",
                "- under a balcony",
                "- in an archway",
                "- near the edge of shade"
              ],
              color: "#5D4037"
            },
            contentAboveGrid: `There are *many forms of frontlight*.

The most flattering one — seen in magazines — is when the light sits **45° above the subject**. It makes eyes sparkle and gives a sculpted, classic look. It's the most forgiving and universally setup you can use outdoors.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 19.51.56.png", alt: "Front light example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 23.34.26.png", alt: "Front light example", span: 3, stackWith: { src: "/training/natural-light/Screenshot 2025-11-25 at 23.32.31.png", alt: "Front light example" } },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 00.39.15.png", alt: "Front light example", span: 3, stackWith: { src: "/training/natural-light/Screenshot 2025-11-26 at 22.07.41.png", alt: "Front light example" } },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 00.39.40.png", alt: "Front light example", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "side-light",
            title: "2) Side Light",
            darkBackground: true,
            content: `**Technique:**
Light from the side (90°).

**Visual effect:**
- Strong volume
- Dramatic texture
- Cinematic mood`,
            callouts: [
              {
                icon: "💡",
                title: "Tip:",
                textLines: ["Use a reflector (white, silver) or any bright surface to fill shadows."],
                color: "#5D4037"
              },
              {
                icon: "✨",
                textLines: [
                  "**\"When you light people who are a little older, who've lived more, you can really see the story of their life etched into their face.\"**",
                  "— *Greg Williams*"
                ],
                color: "#4a4a4a"
              }
            ],
            contentAboveGrid: `Sidelighting is one of the simplest ways to create *intriguing, emotional imagery*.

That's why sidelight works beautifully for emotional portraits, parents, or grandparents during a wedding — faces full of lived experience become sculpture under good side light.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.17.05.png", alt: "Side light example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.08.44.png", alt: "Side light example", span: 2, stackWith: { src: "/training/natural-light/Screenshot 2025-11-25 at 20.23.34.png", alt: "Side light example" } },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.02.03.png", alt: "Side light example", span: 2 },
            ],
            transitionTo: null,
          },
          {
            id: "backlight",
            title: "3) Backlight",
            darkBackground: true,
            content: `**Technique:**
Light behind the subject.

**Effect:**
- Glowing hair
- Silhouettes
- Warm atmosphere`,
            callouts: [
              {
                icon: "💡",
                title: "Tip:",
                textLines: ["Expose for the face. Let the background overexpose slightly."],
                color: "#5D4037"
              },
              {
                icon: "✨",
                textLines: [
                  "**Backlight creates a lovely line of light down the side of someone's face, and then soft reflected light in front — it hides imperfections beautifully.**"
                ],
                color: "#4a4a4a"
              }
            ],
            contentAboveGrid: `That's why backlight feels *romantic, cinematic, and forgiving*. It's not about perfection — it's about atmosphere.`,
            galleryColumns: 4,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 22.16.59.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 22.29.32.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 23.03.47.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 21.59.30.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-24 at 23.55.42.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 22.05.39.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 22.15.03.png", alt: "Backlight example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 23.13.48.png", alt: "Backlight example" },
            ],
            transitionTo: null,
          },
          {
            id: "diffusion",
            title: "2. Diffusion — softening the light",
            darkBackground: true,
            content: `Clouds, curtains, shade — they are all natural diffusers.

When a cloud passes in front of the sun, it changes everything:

Shadows soften, edges blur, and suddenly the whole scene feels cinematic.`,
            callout: {
              icon: "☁️",
              textLines: [
                "**Put lots of cloud in front of the sun, and you may lose all shadows at all. On a cloudy day in the snow, you're not even aware of where the sun is — it's all reflected around you. That's the magic of diffusion: it erases direction and fills every space equally.**"
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.39.44.png", alt: "Diffusion example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.44.06.png", alt: "Diffusion example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.52.03.png", alt: "Diffusion example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 20.40.59.png", alt: "Diffusion example", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "open-shade",
            title: "Open Shade",
            darkBackground: true,
            content: `**Meaning:**
You're in shade close to the bright area.
Shade + bright surroundings = soft, even light.

**Effect:**
- Flattering skin
- Bright eyes
- Low contrast`,
            callout: {
              icon: "💡",
              textLines: [
                "This is your \"free studio.\" Step just into the edge of shadow, not deep inside — that's where light wraps softly but still has sparkle."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 00.01.07.png", alt: "Open shade example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 00.04.14.png", alt: "Open shade example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 00.00.02.png", alt: "Open shade example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 00.33.22.png", alt: "Open shade example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 00.33.52.png", alt: "Open shade example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 22.04.32.png", alt: "Open shade example", span: 2 },
            ],
            transitionTo: null,
          },
          {
            id: "window-light",
            title: "Window Light",
            darkBackground: true,
            content: `**45° to window** — the most flattering

**Side to window** — dramatic, Rembrandt vibe

**1–2 meters from window** — soft, even

**Back to window** — glowing atmosphere`,
            galleryColumns: 4,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 22.04.48.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 22.07.12.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 22.05.05.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 22.06.38.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 00.30.20.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 00.30.05.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 18.34.17.png", alt: "Window light example" },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 18.31.53.png", alt: "Window light example" },
            ],
            transitionTo: null,
          },
          {
            id: "reflection",
            title: "3. Reflection — controlling light bounce",
            darkBackground: true,
            content: `**How it works:**
Light bounces off nearby surfaces.

Bright surfaces = more bounce.
Dark = less.
Coloured = contamination.

**Examples:**
- White = soft fill
- Metal/glass = specular, sharp
- Green/wood = unwanted tint

Use reflections intentionally.

Even a pale wall, a wedding dress, or a groom's white shirt can act as a natural reflector.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-26 at 18.50.40.png", alt: "Reflection example", span: 3, caption: "Looking at this photo, we can see that the window light comes from the **left side**, illuminating the **right sides** of both faces. But the **left side** of the bride's face is also softly lit — this happens because the light bounces off the bridesmaid's **light-colored outfit**, acting like a natural reflector. This is the simplest example of **bounce light** at work." },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 00.02.39.png", alt: "Reflection example", span: 3, caption: "In this photo, we can see how the sunlight bounces off the bride's **white dress**, **light skin**, and **bright hair**, creating a strong reflected fill on the groom's face. The warm, slightly orange tone comes from the color of the bride's hair." },
            ],
            transitionTo: null,
          },
          {
            id: "final-thoughts",
            title: "4. Final thoughts",
            darkBackground: true,
            callout: {
              icon: "⚡",
              textLines: [
                "**\"There's no such thing as bad light. It's just how you choose to use it.\"**",
                "— *Greg Williams*"
              ],
              color: "#4a4a4a"
            },
            contentAfter: `"Ugly lights" challenge you to make beauty where most photographers would give up.

Tilt the head, change your shooting angle, catch the reflection in their eyes — and that "ugly" light turns into editorial magic.

The takeaway is simple:

Whether you shoot in golden hour or a dimly lit bathroom — the light isn't your enemy.

**It's a story waiting to be shaped.**`,
            transitionTo: null,
          },
          {
            id: "harsh-light-intro",
            title: "Let's break down this example with harsh light:",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 17.47.53.png", alt: "Harsh light example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 17.47.27.png", alt: "Harsh light example", span: 3 },
            ],
            content: `We have direct light — harsh, strong, killing texture, creating intense and very sharp shadows.

We also have a couple that we somehow need to photograph beautifully.

We are not using flash in this scene.

Aside from the light, we also have a location — and as we can see, it is not very aesthetic.

The images look flat and awful.

So what do we do?`,
            transitionTo: null,
          },
          {
            id: "harsh-light-option1",
            title: "Option 1 — Don't fight the light. Shape it.",
            darkBackground: true,
            content: `Instead of trying to overpower the harsh light and strong shadows, we embrace them and look for a **unique shadow pattern**.

Shadow is either your enemy or your best friend.

In shadows, you can *hide* imperfections if needed.

And with the correct turn of the head/neck, you remove the shadows that shouldn't be on the face (such as "panda eyes," double noses, etc.) while keeping the ones that add character.

With shadows, you stop being just a photographer — you become a painter.

You're painting on your canvas with sunlight and darkness.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 18.16.57.png", alt: "Shaping harsh light", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 18.29.51.png", alt: "Shaping harsh light", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "harsh-light-option2",
            title: "Option 2 — Work with silhouettes.",
            darkBackground: true,
            content: `You have a perfect opportunity to play with silhouettes.

To achieve this, we hide the couple entirely in the shade.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 18.37.53.png", alt: "Silhouette example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 18.53.50.png", alt: "Silhouette example", span: 3 },
            ],
            contentAfter: `As you can see, we can create a **fully dark silhouette**,

or place the couple on the **border of light and shadow** — **the open shade**.`,
            transitionTo: null,
          },
          {
            id: "harsh-light-rays",
            title: "Light Rays & Angles",
            darkBackground: true,
            content: `We look for **light rays**, experiment with angles, and explore how poses interact with the rhythm of light.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-25 at 19.08.29.png", alt: "Light rays example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 19.13.37.png", alt: "Light rays example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 19.24.19.png", alt: "Light rays example", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-25 at 19.15.54.png", alt: "Light rays example", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "scene-intro",
            title: "Let's take a look at this wedding scene as an example to understand how light works and how to read it.",
            darkBackground: true,
            sideByeSideImage: {
              src: "/training/natural-light/Screenshot 2025-11-27 at 00.04.29.png",
              alt: "Bride in strong bounce zone"
            },
            content: `In this photo, the bride is not standing in direct sunlight, but she is still in a **strong bounce zone** — light is reflecting off the floor, the walls, or her bright dress. Because of this, the **lower part of her face receives more light**, while the upper part stays darker. The chin becomes noticeably brighter, and the **eyes fall slightly into shadow**.

To fix this, simply **move the bride one step forward or sideways**, increasing the distance from the reflective surface. This instantly brings balance back to the lighting on her face.`,
            transitionTo: null,
          },
          {
            id: "scene-tilt",
            title: "Tilting the Head",
            darkBackground: true,
            content: `**On these images, we can see the same strong bounce hitting the lower part of the face — especially on the bride. But notice the photo where she gently lowers her head while the flower girl stands in front of her.**

As soon as she tilts her chin down, the light spreads more evenly across her features:

– the lower-face hotspot disappears,

– shadows balance out,

– the expression becomes soft, dreamy and poetic.

This is a simple trick you can use even when you *can't physically move* the bride or change the setup.

Just ask her to slightly lower her gaze — as if she's looking at her bouquet or admiring the ring.

It immediately evens out the light and creates a romantic, thoughtful mood.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-26 at 23.58.40.png", alt: "Tilting head example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 23.58.55.png", alt: "Tilting head example", span: 2 },
              { src: "/training/natural-light/Screenshot 2025-11-26 at 23.59.20.png", alt: "Tilting head example", span: 2 },
            ],
            transitionTo: null,
          },
          {
            id: "scene-open-shade",
            title: "Open Shade Indoors",
            darkBackground: true,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-27 at 00.01.35.png", alt: "Open shade indoors", span: 3, caption: "In these images, the bride has stepped out of the bounce zone and is now fully in soft shade — but still close enough to the window to receive clean, directional light. This creates an indoor version of *open shade*: even, diffused illumination that gently wraps around the faces of everyone in the frame. No hotspots, no harsh shadows — just soft, flattering light on all subjects." },
              { src: "/training/natural-light/Screenshot 2025-11-27 at 00.03.44.png", alt: "Open shade indoors", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "scene-distance",
            title: "Distance from Windows",
            darkBackground: true,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-27 at 00.07.49.png", alt: "Distance from window", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-27 at 00.06.30.png", alt: "Distance from window", span: 3, caption: `In these photos, the bride and her bridesmaids are standing at the very back of the room — far from the window light.

Because the light has to travel a long distance and loses intensity on the way, it reaches them weak, flat, and dull.

There is almost no directional modeling on their faces, which makes the overall image look muted and low-contrast.

• Window light behaves like any other light source: it gets softer *but also weaker* with distance.
• The further people stand from the window → the more the light spreads → the lower the contrast → the flatter the portrait.
• Corners of rooms often act like *light traps*: they swallow light instead of reflecting it.` },
            ],
            transitionTo: null,
          },
          {
            id: "scene-close-window",
            title: "Close to Window",
            darkBackground: true,
            sideByeSideImage: {
              src: "/training/natural-light/Screenshot 2025-11-27 at 00.06.11.png",
              alt: "Close to window example"
            },
            content: `Here, the bride is standing very close to the window.

Because of that, the bottom part of the frame — especially the dress — is **overexposed**, catching the full strength of direct sunlight.

However, her **face remains in open shade**: she is positioned just slightly behind the line of direct light.

This means the light hitting her face is soft, diffused, and flattering.

As a result, the skin looks velvety and smooth, with no harsh shadows and no emphasized texture — even if there were any imperfections, this type of light would hide them beautifully.

That's why the portrait still feels balanced and pleasing, despite the bright highlights on the dress.`,
            transitionTo: null,
          },
          {
            id: "scene-side-reflector",
            title: "Side Light + Reflector",
            darkBackground: true,
            content: `These images are similar to the previous example, but here the bride and bridesmaid are standing **side-on to the light source** (the window).

That's why the **right side of their faces is more illuminated** — this is classic *side light*.

However, because the bridesmaid is wearing **light-colored clothing**, her outfit acts as a natural reflector.

The bounced light softens the shadow on the bride's face, reduces contrast, and even adds a gentle warm tint that looks like a natural blush.

In the photo with the groom, the bride is turned **two-thirds away from the window**, but she remains **close to the light** and still within the *open shade* zone.

This gives her face soft illumination with a touch of contrast — flattering, dimensional, and clean.`,
            galleryImages: [
              { src: "/training/natural-light/Screenshot 2025-11-27 at 00.07.27.png", alt: "Side light reflector", span: 3 },
              { src: "/training/natural-light/Screenshot 2025-11-27 at 00.02.45.png", alt: "Side light reflector", span: 3 },
            ],
            transitionTo: null,
          },
        ],
      },
      {
        id: "artificial-light",
        title: "ARTIFICIAL LIGHT",
        estimatedMinutes: 30,
        hook: "Mastering artificial light in real wedding scenarios.",
        headerImage: {
          src: "/training/artificial-light/Screenshot 2025-11-24 at 19.41.28.png",
          alt: "Artificial light example"
        },
        sections: [
          {
            id: "on-camera-flash",
            title: "1) ON-CAMERA FLASH — your reliable, always-with-you tool",
            darkBackground: true,
            content: `On-camera flash is **NOT** "bad taste".

In the right hands, it gives **sharp, stylish, direct, high-contrast images** that feel editorial, nightlife-inspired, chic, intentional.

**Think:** Juergen Teller, fashion backstage, 2000s magazine aesthetic.`,
            callout: {
              icon: "✓",
              title: "Where direct flash looks great:",
              textLines: [
                "• dark receptions",
                "• dance floors",
                "• bouquet toss / garter toss",
                "• chaotic party areas",
                "• guest entrances in dim spaces",
                "• first look moments in low light",
                "",
                "**Direct flash = defined faces, strong presence, instant punch.**"
              ],
              color: "#5c3d3d"
            },
            galleryColumns: 4,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-24 at 19.49.08.png", alt: "On-camera flash example", span: 1 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 17.50.58.png", alt: "On-camera flash example", span: 1 },
              { src: "/training/artificial-light/Screenshot 2025-11-24 at 19.46.10.png", alt: "On-camera flash example", span: 1 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 17.49.37.png", alt: "On-camera flash example", span: 1 },
              { src: "/training/artificial-light/Screenshot 2025-11-24 at 19.23.51.png", alt: "On-camera flash example", span: 2 },
              { src: "/training/artificial-light/Screenshot 2025-11-24 at 19.41.45.png", alt: "On-camera flash example", span: 2 },
            ],
            transitionTo: null,
          },
          {
            id: "direct-vs-bounce-intro",
            title: "2) Direct flash vs bounce — when to choose what",
            darkBackground: true,
            callouts: [
              {
                icon: "✦",
                title: "Direct flash (pointed at the subject)",
                textLines: [
                  "✔ strong, clear faces",
                  "✔ freezes chaos",
                  "✔ creates a bold, intentional mood",
                  "✔ cuts through ugly ambient light",
                  "",
                  "**Downside:**",
                  "– hard shadows behind the subject",
                  "(But sometimes this is a stylistic choice.)"
                ],
                color: "#4a4a4a"
              },
              {
                icon: "✦",
                title: "Bounce flash",
                textLines: [
                  "✔ softer light",
                  "✔ smoother gradients",
                  "✔ cleaner skin tones",
                  "✔ timeless wedding look",
                  "",
                  "**BUT here's the core physics photographers often forget:**",
                  "",
                  "When you bounce into the ceiling:",
                  "• the top of the subject is well lit",
                  "• the bottom becomes darker",
                  "• dresses look uneven",
                  "• faces glow while legs disappear into shadow",
                  "",
                  "This isn't \"your mistake\"",
                  "**This is how directional top light works.**"
                ],
                color: "#4a4a4a"
              },
              {
                icon: "✓",
                title: "How to fix uneven bounce light:",
                textLines: [
                  "• Tilt your flash **slightly backward** → light hits the wall first, then wraps forward",
                  "• Use a **bounce card** → adds sparkle to eyes",
                  "• Mix angles → 70% into ceiling, 30% forward",
                  "",
                  "You gain softness *and* even coverage."
                ],
                color: "#4a4a4a"
              }
            ],
            transitionTo: null,
          },
          {
            id: "direct-vs-bounce-comparison",
            title: "Direct vs Bounce — Comparison",
            darkBackground: true,
            contentAboveGrid: `We're comparing **direct flash vs bounce flash**. These images are a perfect example of when each method works — and when it does not.

**The situation:**
The room is completely white, the couple is wearing white, and they are standing very close to the wall and the window.`,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.16.27.png", alt: "Bounce flash example", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.17.12.png", alt: "Direct flash example", span: 3 },
            ],
            content: `### **Left photo — bounce flash**

In the first image, the flash is pointed upward and bounced off the ceiling.

**Why it fails here:**
• the ceiling is high, so the light spreads too broadly
• it falls top-down, creating shadows in the eye sockets
• the top of the image becomes brighter than the bottom
• the faces fall into shadow
• the light looks heavy and unflattering

This is a classic example where bounce **doesn't work**, because:
1. the subjects are too close to the wall
2. the white background flattens everything
3. the bounce doesn't return enough light to the faces

### **Right photo — direct flash**

In the second image, the flash is directed straight at the couple.

**This works better because:**
• the faces are evenly illuminated
• unwanted shadows disappear
• the skin tone looks clear and clean

**However, the image still appears flat because:**
• the couple is standing right against the wall
• there is no separation from the background
• the groom's suit blends into the white wall and the bride's dress

**To improve the scene:**
• move the couple at least 1–1.5 meters away from the wall
• or have them sit down to naturally change the angle and depth`,
            transitionTo: null,
          },
          {
            id: "couch-example",
            title: "Changing Perspective",
            darkBackground: true,
            sideByeSideImage: {
              src: "/training/artificial-light/Screenshot 2025-11-27 at 18.23.43.png",
              alt: "Couch perspective example"
            },
            content: `In this image, placing the couple on the couch and changing the shooting perspective immediately creates a more dynamic and visually interesting scene.

Here, the hard direct flash works much better because it enhances texture, adds contrast, and gives the photo a fashionable editorial feel.

By shifting the level and adjusting the pose, the final result looks much more stylish and intentional.`,
            transitionTo: null,
          },
          {
            id: "group-comparison",
            title: "Group Shot Comparison",
            darkBackground: true,
            contentAboveGrid: `In these images we see a very similar situation to the first example.`,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.19.08.png", alt: "Bounce flash group", span: 2 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.19.28.png", alt: "Direct flash group", span: 2 },
              { src: "/training/artificial-light/Screenshot 2025-11-24 at 19.23.51.png", alt: "Tilted angle group", span: 2 },
            ],
            content: `In the photo on the left, even though we have the couch, contrasting outfits, and the groom stepping a bit farther from the wall, the image still looks unflattering because the flash was bounced off the ceiling. The ceiling is high, so the light spreads too much, falls from above, and creates shadows under the eyes. In settings like this, ceiling bounce usually produces flat and unaesthetic light. Sometimes it's useful, but only when the environment supports it. In this scene, it removes texture and depth — exactly what we need.

Bouncing the flash off the wall behind the photographer (if it's white) usually gives softer and more pleasing light, but here it would also reduce contrast. In this particular situation, we want the opposite — strong texture and depth.

In the photo on the right, the direct flash gives a much better result: it adds contrast, volume, and character, making the group look more stylish and lively.

Since the room is very small, creating more dynamic group shots is tricky, but tilting the camera slightly or changing the angle can produce a more interesting and playful image — as seen in the third example. This part is a matter of taste and style.`,
            transitionTo: null,
          },
          {
            id: "shadow-comparison",
            title: "Reading Flash Direction from Shadows",
            darkBackground: true,
            contentAboveGrid: `These images clearly show the difference between bounce flash and direct flash.`,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.13.48.png", alt: "Bounce flash shadows", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.15.14.png", alt: "Direct flash shadows", span: 3 },
            ],
            content: `**In the first photo (left)**

You can identify the direction of the light by looking at the shadows: under the groom's sleeve, there is a wide, soft, diffused shadow. This indicates that the light is coming from above and slightly from the side — meaning the flash was bounced off a wall or the ceiling behind the photographer.

The space around the couple is also evenly lit, which is typical of bounce flash: the reflected light spreads out and fills the entire room.

**In the second photo (right)**

The groom's sleeve shows a small, sharp, high-contrast shadow. The couple is noticeably brighter than the background — the direct flash separates them from the scene behind them.

All unwanted shadows are minimized, the clothing looks cleaner, and the overall image appears fresher. This is classic direct flash.`,
            transitionTo: null,
          },
          {
            id: "distance-background",
            title: "Distance from Background",
            darkBackground: true,
            contentAboveGrid: `In these photos, we can clearly see how the distance between the couple and the background affects the image.`,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.33.14.png", alt: "Close to wall", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.33.36.png", alt: "Far from wall", span: 3 },
            ],
            content: `In short:

**when the couple stands close to the wall, the background appears brighter, and depending on the interior and their outfits, they may blend into it.** This is exactly what we see in the first image.

In the second image, the couple is positioned much farther from the back wall, which makes the background darker and less prominent, allowing the couple to stand out more clearly.

Knowing this simple principle makes it easy to control depth, contrast, and the visual separation of your subjects in any scene.`,
            transitionTo: null,
          },
          {
            id: "foreground-objects",
            title: "Foreground Objects & Flash",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.29.07.png", alt: "Foreground example 1", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-28 at 21.09.26.png", alt: "Foreground example 2", span: 3 },
            ],
            content: `Thanks to this accidental photo, we can highlight one very important rule when working with flash:

**If there is any object in the foreground and your main subject is farther away, a direct flash will always hit the foreground object first.**

This becomes even more noticeable when the foreground object is **white**, as in this example (the white chairs). White surfaces reflect light strongly, pulling the exposure toward themselves.

When photographing people—especially large groups—someone almost always ends up in the foreground, unintentionally becoming much brighter than everyone else.

Sometimes this effect can look artistic, but more often it looks distracting and unflattering, like on the second photo.

So always pay attention to **what stands between you and your subjects** when using direct flash.`,
            transitionTo: null,
          },
          {
            id: "interior-detail-shots",
            title: "Flash for Interior & Detail Shots",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.31.36.png", alt: "Without flash", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.31.21.png", alt: "With flash", span: 3 },
            ],
            content: `This comparison shows why using flash can be helpful even for interior or detail shots.

In the first image, the ambient light is weak, so colors look dull and details fade.

In the flash image:
• colors are brighter
• textures pop
• contrast and depth improve

Flash can significantly enhance a scene when the available light isn't enough.`,
            transitionTo: null,
          },
          {
            id: "off-camera-flash",
            title: "3) OFF-CAMERA FLASH",
            darkBackground: true,
            content: `You don't need 3 lights, radio grids, multi-angle setups, or dramatic studio schemes.

**One flash + one trigger** = enough.`,
            callouts: [
              {
                icon: "✓",
                title: "Who holds it?",
                textLines: [
                  "• an assistant",
                  "• OR **you** (yes, most wedding photographers hold it themselves)",
                  "• OR mount it on a light stand / monopod / table",
                  "",
                  "Wedding moments are fast — you need **mobility**, not theatrical lighting."
                ],
                color: "#4a4a4a"
              },
              {
                icon: "✓",
                title: "What you MUST have:",
                textLines: [
                  "• Flash",
                  "• Radio trigger",
                  "(Otherwise off-camera simply won't fire.)",
                  "",
                  "**Reliable options:**",
                  "Godox XPro, X2T, Profoto Air Remote."
                ],
                color: "#4a4a4a"
              }
            ],
            image: {
              src: "/training/artificial-light/Screenshot 2025-11-27 at 23.31.27.png",
              alt: "Off-camera flash setup"
            },
            transitionTo: null,
          },
          {
            id: "when-flash-matters",
            title: "4) When flash truly matters",
            darkBackground: true,
            content: `Not "when it looks pretty," but **when the situation demands it:**

• low-light receptions
• dark dance floors
• evening parties
• DJ lights + fog
• orange tungsten rooms
• crowded bars and lounges
• corridor entrances
• backlit stage or performance
• chaotic mixed-temperature lighting
• when you need motion freeze`,
            galleryColumns: 4,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.41.41.png", alt: "Flash example" },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.36.52.png", alt: "Flash example" },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.59.03.png", alt: "Flash example" },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.57.38.png", alt: "Flash example" },
            ],
            transitionTo: null,
          },
          {
            id: "flash-ambient-balance",
            title: "5) Understanding flash + ambient balance",
            darkBackground: true,
            content: `**Let's make it extremely clear:**`,
            callouts: [
              {
                icon: "✓",
                title: "Ambient controls the background:",
                textLines: [
                  "• slower shutter → more atmosphere",
                  "• faster shutter → darker background",
                  "• ISO → raise only to a clean level (don't go to 6400:))",
                  "• aperture → overall exposure"
                ],
                color: "#4a4a4a"
              },
              {
                icon: "✓",
                title: "Flash controls your subject:",
                textLines: [
                  "• face",
                  "• hands",
                  "• details",
                  "• silhouette"
                ],
                color: "#4a4a4a"
              }
            ],
            transitionTo: null,
          },
          {
            id: "recommended-settings",
            title: "6) Recommended basic settings",
            darkBackground: true,
            callout: {
              icon: "⚙",
              title: "Power (manual):",
              textLines: [
                "Start at **1/32 or 1/16** and adjust by feel.",
                "",
                "**Zoom:**",
                "• direct → 50–85 mm (tighter beam)",
                "• bounce → 35–50 mm (wider throw)",
                "",
                "**TTL:**",
                "Great for unpredictable movement.",
                "Less reliable in tricky mixed lighting."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "practical-tips",
            title: "7) Practical things wedding photographers NEED to know",
            darkBackground: true,
            callouts: [
              {
                icon: "✓",
                title: "Glasses reflections",
                textLines: ["Rotate the person slightly or raise the flash angle."],
                color: "#4a4a4a"
              },
              {
                icon: "✓",
                title: "Colored ceilings/walls",
                textLines: ["Bounce from them → color cast on the skin."],
                color: "#4a4a4a"
              },
              {
                icon: "✓",
                title: "Dark ceilings",
                textLines: ["They swallow your flash → direct flash is safer."],
                color: "#4a4a4a"
              },
              {
                icon: "✓",
                title: "Specular highlights (intentional shine)",
                textLines: [
                  "Direct flash angled from above or 45° gives beautiful reflections on:",
                  "",
                  "• metallic décor",
                  "• sequins",
                  "• champagne glasses",
                  "• shimmering fabrics",
                  "",
                  "This creates a vibrant, party-energy look — **intentional, aesthetic.**"
                ],
                color: "#4a4a4a"
              }
            ],
            transitionTo: null,
          },
          {
            id: "holding-flash",
            title: "8) Holding a flash in your hand (for off-camera style without an assistant)",
            darkBackground: true,
            content: `**Totally valid and common.**`,
            callout: {
              icon: "✓",
              title: "Position options:",
              textLines: [
                "• above your head pointing downward",
                "• slightly to the side at 30–45°",
                "• pointed up/back for softer spill",
                "",
                "You simply hold it and move with the flow — this gives **elegant contours and more dimensionality** than on-camera."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "analyze-examples",
            title: "Let's analyze:",
            darkBackground: true,
            galleryColumns: 3,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.38.24.png", alt: "Flash analysis", caption: "This is a great example of why using flash at events is so effective — it freezes the moment perfectly, keeping expressions sharp and clean even in low light." },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.38.11.png", alt: "Flash analysis", caption: "Catching real emotions! You can also see that the shutter speed here is slightly slower — the background is brighter because the camera let in more ambient light, while the flash keeps the subject sharp." },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.37.37.png", alt: "Flash analysis", caption: "A good reminder: your subject will always cast a shadow on nearby walls when using flash. In some scenes this shadow becomes very noticeable, so keep it in mind when choosing your angle and distance." },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.42.38.png", alt: "Flash analysis", caption: "Here we also see a shadow behind the standing man, but because he's farther from the wall and the shutter speed is faster, the background falls into darkness, making the shadow much less noticeable. And don't be afraid to crop people around your main subject — it brings life into the frame and looks perfectly natural." },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.39.40.png", alt: "Flash analysis", caption: "These men are standing very close to a white wall, which is why the background appears bright. The angle also reveals a noticeable shadow cast by the man in the foreground. If we shifted slightly to the right, he would block his own shadow, making it far less visible." },
            ],
            transitionTo: null,
          },
          {
            id: "analyze-shutter-speed",
            title: "Shutter Speed & Background",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.57.38.png", alt: "Shutter speed example", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.44.10.png", alt: "Shutter speed example", span: 3 },
            ],
            content: `In these photos the shutter speed is slower, so the background appears brighter and we can clearly see the other guests behind the main subject. If the shutter were faster, the background would fall into darkness and the images would look stronger — but we'd lose the people in the back.

Choose based on the moment.

And a reminder: with flash, the **shutter speed controls the background exposure**, while the **aperture controls how much flash light hits your subjects**.

Use this intentionally — and don't drop below **1/125** unless you *want* motion blur.`,
            galleryImagesAfter: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.42.51.png", alt: "Shutter speed example", span: 2 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.58.17.png", alt: "Shutter speed example", span: 2, stackWith: { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.41.12.png", alt: "Shutter speed example" } },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.41.24.png", alt: "Shutter speed example", span: 2 },
            ],
            transitionTo: null,
          },
          {
            id: "wedding-analysis-1",
            title: "Let's analyze the following wedding:",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.12.58.png", alt: "Wedding analysis", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.13.13.png", alt: "Wedding analysis", span: 3 },
            ],
            content: `The photographer chose to keep the background visible because the venue lighting looks warm, festive, and beautiful — it adds atmosphere to the image.

However, ceiling lights often create unflattering color casts and shadows on the skin.

That's why we still use flash: it corrects the skin tones and removes unwanted shadows while preserving the ambient light.`,
            transitionTo: null,
          },
          {
            id: "wedding-analysis-1-details",
            title: "Capturing Moments with Flash",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.13.54.png", alt: "Emotional moment", span: 3, caption: "These are the kinds of beautiful, emotional moments you can highlight and preserve with flash.\nAlso remember: when a person wearing glasses looks directly at the camera, the flash will reflect in the lenses.\nIt's not a big issue in candid shots like this, but if they slightly turn their head, the glare will disappear." },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.13.27.png", alt: "DJ shot", span: 3, caption: "How cool does this look! The flash adds that perfect pop, keeping the DJ sharp and well-lit while still preserving the warm ambience of the room." },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.33.17.png", alt: "Party moment", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.31.51.png", alt: "Party moment", span: 3 },
            ],
            content: `Capture those unforgettable moments! Without flash, these scenes would look much flatter and lose that lively, vibrant energy.`,
            transitionTo: null,
          },
          {
            id: "wedding-analysis-1-more",
            title: "Real Life & Emotion",
            darkBackground: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.14.08.png", alt: "Emotion", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 18.32.21.png", alt: "Emotion", span: 3 },
            ],
            content: `A bit more real life and emotion — these moments feel vivid and alive thanks to the flash.`,
            transitionTo: null,
          },
          {
            id: "flash-analysis-examples",
            title: "Flash Analysis Examples",
            darkBackground: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.20.34.png", alt: "Editorial look", span: 3, caption: "Still, I want to emphasize: the most fashionable, editorial-looking flash photos come when the background is darker (especially if there are dark walls or floors behind the subject). That gives you that clean, magazine-style or documentary flash look.\nBut it always depends on the situation — so stay flexible and adjust!" },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.16.17.png", alt: "Distance analysis", span: 3, caption: "Let's break this photo down:\n\nBecause the woman is standing farther from the camera, much less flash reaches her. And since the shutter speed is quite fast, the background stays bright, so she blends into the scene instead of standing out. The shadow behind her on the wall also becomes more noticeable.\n\nThe photo itself looks great and captures the moment well — we're simply analyzing how the light behaves and what other options a photographer could consider." },
            ],
            transitionTo: null,
          },
          {
            id: "flash-vs-no-flash",
            title: "Flash vs No Flash",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.18.19.png", alt: "Without flash", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.18.06.png", alt: "With flash", span: 3 },
            ],
            content: `Here we can clearly see the difference.
The photo on the left is shot without flash, while the photo on the right is shot with flash — all unwanted shadows are gone, and the image looks clean, polished, and stylish.`,
            transitionTo: null,
          },
          {
            id: "backlit-flash",
            title: "Backlit Sunlight + Flash",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.20.19.png", alt: "Backlit flash", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.15.47.png", alt: "Backlit flash", span: 3 },
            ],
            content: `The most refined, expensive-looking light is a combination of backlit sunlight and flash.

This mix creates a *very* stylish, polished look.`,
            transitionTo: null,
          },
          {
            id: "wedding-analysis-2",
            title: "Let's look at the next wedding:",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.21.56.png", alt: "Wedding 2", span: 2 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.21.27.png", alt: "Wedding 2", span: 2 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.22.40.png", alt: "Wedding 2", span: 2 },
            ],
            content: `As I mentioned earlier — the strongest, most high-end look comes from backlit sunlight combined with flash. Of course, many other factors matter too: stylish outfits, a beautiful location, the overall mood…

But just look at how stunning this combination is!`,
            transitionTo: null,
          },
          {
            id: "daylight-portraits",
            title: "Daylight Portraits with Flash",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.22.14.png", alt: "Daylight portrait", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.21.43.png", alt: "Daylight portrait", span: 3 },
            ],
            content: `Portraits shot in daylight with flash also look very refined and elegant.`,
            transitionTo: null,
          },
          {
            id: "precious-emotions",
            title: "Capturing Precious Emotions",
            darkBackground: true,
            sideByeSideImage: {
              src: "/training/artificial-light/Screenshot 2025-11-27 at 19.23.41.png",
              alt: "Precious emotions"
            },
            content: `Capturing the most precious emotions and unforgettable moments!

From a technical perspective, everything works well here, but the scene would look even more refined with a slightly darker background — it would make the couple stand out more, and the flash would create a subtle "spotlight" effect on them.

Still, the frame feels incredibly alive thanks to the bride's genuine reaction.`,
            transitionTo: null,
          },
          {
            id: "party-lighting-flash",
            title: "Flash vs Indoor Party Lighting",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.25.42.png", alt: "Without flash", span: 3 },
              { src: "/training/artificial-light/Screenshot 2025-11-27 at 19.26.31.png", alt: "With flash", span: 3 },
            ],
            content: `In the first photo there's no flash, and in the second one there is — and you can instantly see the difference.

Indoor party lighting is usually the worst mix of green and purple tones, which makes skin look unnatural.

Using flash removes that color cast and brings back clean, natural-looking skin.`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "lighting-troubleshooting",
        title: "LIGHTING TROUBLESHOOTING",
        estimatedMinutes: 20,
        hook: "Weather shifts, venue color casts, temperature mismatches — solve common lighting challenges.",
        sections: [
          {
            id: "weather-shifts-intro",
            title: "1) Weather Shifts — When the Sky Keeps Changing Its Mind",
            darkBackground: true,
            content: `Wedding days rarely offer stable light. Clouds drift, sun pops in and out, rain changes tone instantly. The trick is not to fight the weather, but to **adapt smarter and faster**.`,
            callout: {
              icon: "☁️",
              title: "When clouds suddenly arrive",
              textLines: [
                "Clouds act like a giant softbox — flattering, diffused light.",
                "",
                "**Problem:** exposure drops fast.",
                "",
                "**Fix:**",
                "• Increase ISO or open your aperture *before* reaching for flash.",
                "• If you use flash, dial it down — you don't need harsh light when nature already softened everything."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-11-24 at 17.15.00.png", alt: "Cloudy light example", span: 3 },
              { src: "/training/lighting/Screenshot 2025-11-24 at 17.13.32.png", alt: "Cloudy light example", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "weather-sun",
            title: "When Sun Suddenly Appears",
            darkBackground: true,
            callout: {
              icon: "☀️",
              title: "When sun suddenly appears",
              textLines: [
                "Harsh sun = harsh shadows, squinting, shiny foreheads.",
                "",
                "**Fix:**",
                "• Move couple into open shade (trees, building corners, doorway).",
                "• If no shade: position the sun *behind* them → natural rim light.",
                "• Add a tiny bit of flash to fill shadows (just a touch, not blasting)."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-11-27 at 19.22.14.png", alt: "Harsh sun example", span: 3 },
              { src: "/training/lighting/Screenshot 2025-11-27 at 19.20.19.png", alt: "Sun positioning example", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "weather-storm",
            title: "When Storm Mood Appears",
            darkBackground: true,
            callout: {
              icon: "⛈️",
              title: "When storm mood appears",
              textLines: [
                "Clouds turn bluish, contrasty, dramatic.",
                "",
                "**Fix:**",
                "• Warm your flash slightly to keep skin tones alive.",
                "• Don't try to match the sky perfectly — let drama be drama."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-12-03 at 02.28.23.png", alt: "Storm mood example", span: 3 },
              { src: "/training/lighting/Screenshot 2025-12-03 at 02.28.52.png", alt: "Dramatic storm lighting", span: 3 },
            ],
            calloutAfterImages: {
              icon: "🎯",
              title: "Pro mindset:",
              textLines: [
                "You don't chase one fixed look on a changing day. You *ride the wave* of the atmosphere while keeping skin tones consistent."
              ],
              backgroundColor: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "venue-color-casts-intro",
            title: "2) Venue Color Casts — When Walls Attack",
            darkBackground: true,
            content: `Reception halls love weird colors: amber bulbs, green LEDs, purple uplights, tungsten chandeliers, mixed sources…`,
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-12-03 at 00.33.37.png", alt: "Color cast example", span: 2 },
              { src: "/training/lighting/Screenshot 2025-12-03 at 01.41.35.png", alt: "Mixed lighting venue", span: 2, stackWith: { src: "/training/lighting/Screenshot 2025-12-03 at 01.41.22.png", alt: "Green LED venue" } },
              { src: "/training/lighting/Screenshot 2025-12-03 at 00.34.29.png", alt: "Tungsten lighting", span: 2 },
            ],
            contentAfter: `It looks odd, unflattering, and straight-up bad.
Not knowing how to work with this kind of light leads to losing important shots.`,
            contentAfterColor: "#FF7F7F",
            transitionTo: null,
          },
          {
            id: "venue-tungsten",
            title: "Warm Tungsten + Flash",
            darkBackground: true,
            content: `Your flash needs to **overpower bad sources** ***just enough*** while still preserving ambience.`,
            callouts: [
              {
                icon: "🟠",
                title: "Warm tungsten + flash",
                textLines: [
                  "**Problem:** flash is cool, tungsten is warm → couple blue, background orange.",
                  "",
                  "**Fix:**",
                  "• Add a **½ CTO gel** on your flash → flash becomes warmer.",
                  "• Match your white balance to tungsten → everything blends."
                ],
                color: "#4a4a4a"
              },
              {
                icon: "🔥",
                title: "Pro tip:",
                textLines: [
                  "Gels are thin, transparent colored sheets you place over your flash to adjust its color. They're used mostly in cinema and photography, but wedding photographers use them very rarely — and only when absolutely necessary. They're powerful but dangerous: easy to overdo, easy to ruin skin tones. Handle them carefully."
                ],
                color: "#5c3d3d"
              }
            ],
            transitionTo: null,
          },
          {
            id: "venue-led",
            title: "Crazy LED Venue Lights",
            darkBackground: true,
            callout: {
              icon: "🎨",
              title: "Crazy LED venue lights",
              textLines: [
                "Purple, green or blue LEDs are your enemy. Skin turns grey, zombie-like.",
                "",
                "**Fix:**",
                "• You can't fix LED color with gels — ignore them.",
                "• Make your flash the **dominant light source**, let LEDs fall back in intensity.",
                "• Position the couple so LEDs are behind them, not on their faces."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "venue-mirrors",
            title: "Mirror Halls / Reflective Surfaces",
            darkBackground: true,
            callout: {
              icon: "🪞",
              title: "Mirror halls / reflective surfaces",
              textLines: [
                "Light bounces everywhere unpredictably.",
                "",
                "**Fix:**",
                "• Switch to direct flash from above (45°).",
                "• Avoid bouncing flash into reflective walls."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "temperature-intro",
            title: "3) Temperature Mismatches — When Light Sources Don't Agree",
            darkBackground: true,
            content: `All light sources have different color temperatures:

• **Sun:** ~5500K
• **Shade:** cooler (~6500–7000K)
• **Tungsten bulbs:** warm (~2800–3200K)
• **LED:** unpredictable (can be green, magenta, blue)

Mixing them creates CHAOS.

The solution? **Decide who is the boss** — your flash or the environment.`,
            transitionTo: null,
          },
          {
            id: "temperature-option-a",
            title: "Option A: Let Ambient Be Your Base",
            darkBackground: true,
            callout: {
              icon: "🌅",
              title: "Option A: Let ambient be your base",
              textLines: [
                "Good when ambient looks beautiful (golden hour, candlelit dinner, warm fairy lights).",
                "",
                "• Lower flash power.",
                "• Use it only to lift shadows.",
                "• Keep the ambience golden and soft."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-12-03 at 02.07.59.png", alt: "Ambient base example", span: 3 },
              { src: "/training/lighting/Screenshot 2025-12-03 at 02.08.22.png", alt: "Golden ambient light", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "temperature-option-b",
            title: "Option B: Let Flash Dominate",
            darkBackground: true,
            callout: {
              icon: "⚡",
              title: "Option B: Let flash dominate",
              textLines: [
                "Good when ambient looks ugly (green LEDs, mixed sources, random colors).",
                "",
                "• Increase flash power.",
                "• Darken ambient with faster shutter speed.",
                "• Let the couple glow while the messy background fades."
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-12-03 at 02.09.05.png", alt: "Flash dominant example", span: 3 },
              { src: "/training/lighting/Screenshot 2025-12-03 at 02.11.21.png", alt: "Flash overpowering ambient", span: 3 },
            ],
            transitionTo: null,
          },
          {
            id: "temperature-insight",
            title: "Professional Insight",
            darkBackground: true,
            callout: {
              icon: "✨",
              title: "Professional insight:",
              textLines: [
                "You *cannot* keep a single color style throughout a crazy-weather wedding. Better to adapt and create beauty in each phase rather than forcing one look. Skill is in working with the existing light, not fighting it."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "led-banding-intro",
            title: "4) LED Banding — What It Is and How to Fix It",
            darkBackground: true,
            content: `Banding is the appearance of dark or light horizontal lines across an image. It most often happens indoors with LED lighting when the camera's shutter speed doesn't sync with the flicker frequency of the LEDs.

It's very easy to miss while shooting, but extremely noticeable later in editing. As more venues switch to LEDs, this issue is becoming increasingly common, so it's important to know how to recognize and fix it quickly.`,
            transitionTo: null,
          },
          {
            id: "led-banding-why",
            title: "Why Banding Happens",
            darkBackground: true,
            callout: {
              icon: "💡",
              title: "Why it happens",
              textLines: [
                "LED lights flicker at a high frequency that we don't see with the naked eye, but the camera can record those pulses.",
                "If your shutter speed doesn't match that flicker cycle, banding appears.",
                "**This is most common:**",
                "• Indoors",
                "• When shooting without flash",
                "• At relatively fast shutter speeds"
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              { src: "/training/lighting/Screenshot 2025-12-03 at 00.15.44.png", alt: "LED banding example", span: 2 },
              { src: "/training/lighting/Screenshot 2025-12-03 at 00.16.38.png", alt: "Banding lines visible", span: 2, stackWith: { src: "/training/lighting/Screenshot 2025-12-03 at 00.18.55.png", alt: "Severe banding" } },
              { src: "/training/lighting/Screenshot 2025-12-03 at 00.16.16.png", alt: "Banding in photo", span: 2 },
            ],
            transitionTo: null,
          },
          {
            id: "led-banding-fix",
            title: "How to Fix LED Banding",
            darkBackground: true,
            callout: {
              icon: "🛠️",
              title: "How to fix it (practical steps)",
              textLines: [
                "**1. Start around** {{1/250}}",
                "Take a test shot. If you see banding, adjust the shutter slightly up or down (for example 1/200, 1/160, or 1/320). Small changes usually solve the problem.",
                "",
                "**2. Keep in mind the trade-offs:**",
                "• Slower shutter speeds require steadier shooting, especially on longer lenses.",
                "• Faster shutter speeds reduce ambient light, so you'll need a wider aperture or higher ISO.",
                "",
                "**3. The most reliable way to avoid banding is to** {{use flash}}",
                "The flash burst overpowers the LED flicker, eliminating the lines entirely.",
                "",
                "**4. Check your images as the lighting changes.**",
                "Every venue's LEDs behave differently, and banding can appear or disappear depending on your angle or where you're standing."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
        ],
      },
    ],
    quiz: {
      title: "TECHNICAL QUIZ",
      description: "Test your knowledge of camera settings, lighting, and technical photography concepts.",
      questions: [
        {
          id: 1,
          question: "What does a histogram primarily show?",
          options: ["The camera's battery health", "The tonal distribution of your image", "The lens sharpness level", "The number of shots left on the card"],
          correct: 1
        },
        {
          id: 2,
          question: "In harsh sunlight, which aperture helps maintain detail in group photos?",
          options: ["f/1.4", "f/2.0", "f/5.6 – f/16", "f/4 – f/5.6"],
          correct: 2
        },
        {
          id: 3,
          question: "What does ISO control?",
          options: ["Motion blur", "Color saturation", "Sensor sensitivity to light", "File size"],
          correct: 2
        },
        {
          id: 4,
          question: "When shooting in mixed indoor lighting, what is the biggest risk if white balance is incorrect?",
          options: ["Motion blur", "Color casts on skin tones", "Crooked horizons", "Lens distortion"],
          correct: 1
        },
        {
          id: 5,
          question: "Which shutter speed is safest for freezing movement during a wedding ceremony?",
          options: ["1/30", "1/60", "1/125", "1/250"],
          correct: 3
        },
        {
          id: 6,
          question: "The best way to reduce noise in low-light conditions is:",
          options: ["Lower ISO", "Raise ISO as high as possible", "Shoot only in JPEG", "Always use f/16"],
          correct: 0
        },
        {
          id: 7,
          question: "In flash photography, ISO should typically stay around:",
          options: ["3200–6400", "800–1600", "100–400", "10–50"],
          correct: 2
        },
        {
          id: 8,
          question: "What is the main advantage of backlighting your subject?",
          options: ["More dramatic shadows", "Perfectly even skin tones", "Strong front illumination", "Hair glow and atmospheric separation"],
          correct: 3
        },
        {
          id: 9,
          question: "What does Spot Metering do?",
          options: ["Measures the whole frame evenly", "Measures only a tiny central area", "Measures shadows only", "Measures highlights only"],
          correct: 1
        },
        {
          id: 10,
          question: "What is the effect of using a very high f-stop number (e.g., f/16)?",
          options: ["Shallow depth of field", "Soft, blurry edges", "Very deep depth of field", "Increased ISO"],
          correct: 2
        },
        {
          id: 11,
          question: "Which scenario MOST benefits from High-Speed Sync (HSS)?",
          options: ["Shooting indoors with constant light", "Shooting in bright sun with wide aperture", "Shooting long exposure landscapes", "Shooting underwater"],
          correct: 1
        },
        {
          id: 12,
          question: "What does \"Evaluative/Matrix Metering\" do?",
          options: ["Ignores highlights completely", "Uses the entire frame to calculate exposure", "Measures only the center", "Measures only the brightest area"],
          correct: 1
        },
        {
          id: 13,
          question: "What is the correct way to fix LED banding (horizontal lines caused by LED flicker) when shooting indoors at a wedding?",
          options: ["Adjust shutter speed slightly up or down until the flicker syncs and the lines disappear", "Increase ISO until the banding disappears", "Change white balance to Tungsten", "Switch to manual focus to stop the flicker"],
          correct: 0
        },
        {
          id: 14,
          question: "Which camera setting determines how much of the background stays in focus?",
          options: ["ISO", "Aperture", "Shutter speed", "Flash power"],
          correct: 1
        },
        {
          id: 15,
          question: "When photographing guests dancing without flash, which problem is most common?",
          options: ["Over-sharpening", "Motion blur", "High contrast", "Excessive saturation"],
          correct: 1
        },
        {
          id: 16,
          question: "A fast shutter speed (1/1000) will:",
          options: ["Brighten the image", "Add blur", "Darken the image", "Increase noise"],
          correct: 2
        },
        {
          id: 17,
          question: "What does back-button focus help with?",
          options: ["Avoiding accidental zoom", "Keeping stable continuous focus on moving subjects", "Adding saturation", "Changing metering mode"],
          correct: 1
        },
        {
          id: 18,
          question: "Which lens type typically gives the smoothest bokeh?",
          options: ["Ultra-wide zoom", "Telephoto prime", "Kit lens", "Macro lens"],
          correct: 1
        },
        {
          id: 19,
          question: "What does \"clean your sensor\" NOT fix?",
          options: ["Dust spots", "Random black dots in the same place every frame", "Lens flare", "Dark circular artifacts at small apertures"],
          correct: 2
        },
        {
          id: 20,
          question: "What tool is best for avoiding blown highlights in wedding dresses?",
          options: ["Histogram", "ISO", "Lens hood", "JPEG mode"],
          correct: 0
        },
        {
          id: 21,
          question: "Which accessory helps reduce harsh sunlight for portraiture?",
          options: ["UV filter", "ND filter", "Flash diffuser", "Macro extension tube"],
          correct: 1
        },
        {
          id: 22,
          question: "When shooting moving subjects, which AF mode is most reliable?",
          options: ["One-shot / AF-S", "Manual focus", "Continuous AF / AI-Servo / AF-C", "Face detection only"],
          correct: 2
        },
        {
          id: 23,
          question: "Which setting combination is LEAST ideal for indoor no-flash photography?",
          options: ["ISO 1600, f/2.0, 1/250", "ISO 400, f/4, 1/250", "ISO 100, f/8, 1/250", "ISO 800, f/2.8, 1/250"],
          correct: 2
        },
        {
          id: 24,
          question: "What is the purpose of the flash sync speed?",
          options: ["The brightest flash can fire", "The fastest shutter speed without black bars", "Higher color accuracy", "Faster autofocus"],
          correct: 1
        },
        {
          id: 25,
          question: "Shooting at f/1.4 during a ceremony mainly risks:",
          options: ["Missed focus on moving subjects", "Too much noise", "Overexposed highlights", "Crooked compositions"],
          correct: 0
        },
      ]
    },
  },
  creative: {
    id: "creative",
    icon: Palette,
    title: "CREATIVE",
    emoji: "🎨",
    description: "How you shape meaning — storytelling, emotion, angles, mood, the art behind the technical choices.",
    modules: [
      {
        id: "moodboard",
        title: "MOODBOARD FOR THE COUPLE — Visual Storytelling Before the Shoot",
        estimatedMinutes: 10,
        hook: "Before you take a photo, you need to know what it should feel like.",
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            content: `A **moodboard** isn't just about "pretty pictures."`,
            galleryImages: [
              {
                src: "/training/moodboard/05.12.2025%2012.11.45%20967%20136.jpg",
                alt: "Moodboard visual storytelling",
                span: 6
              }
            ],
            contentAfter: `It's about *translating feelings into visuals* — tone, emotion, texture, story.

For wedding photographers, this is the first step toward visual harmony with your couple.`,
            transitionTo: null,
          },
          {
            id: "purpose",
            title: "1. Purpose — Why Moodboards Matter",
            darkBackground: true,
            content: `A great shoot doesn't start on the day — it starts in your conversations.

Moodboards help you and the couple:

• **Align expectations**
• **Define style** (cinematic, editorial, documentary, fine art, bohemian, etc.)
• **Understand colors, textures, and emotions**
• **Avoid misunderstandings** about lighting, poses, or editing tone

It's not a contract — it's a **conversation in pictures**.`,
            transitionTo: null,
          },
          {
            id: "building",
            title: "2. Building the Moodboard",
            darkBackground: true,
            content: `Use **Pinterest**, **Notion**, or **Canva** — platforms where you can mix references, textures, and color palettes.

Aim for **8–12 images** that communicate the essence — too many will confuse the vision.

**Include:**

• **Lighting mood** (soft morning, golden hour, candlelight)
• **Composition** (close & intimate vs. wide & cinematic)
• **Posing inspiration** (gentle touch, laughter, movement)
• **Details** (rings, flowers, table setups, shoes)
• **Emotional tone** (romantic, joyful, calm, nostalgic)`,
            callout: {
              title: "Pro Tip:",
              items: [
                "Add short captions under each image — \"soft backlight,\" \"warm tone,\" \"close framing.\"",
                "It helps clients *read* the imagery the same way you do."
              ],
              footer: null,
              color: "#3d4a5c"
            },
            galleryImages: [
              {
                src: "/training/moodboard/05.12.2025%2010.57.55%20416%2067.jpg",
                alt: "Moodboard example with wedding imagery",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "couple-involvement",
            title: "3. The Couple's Involvement",
            darkBackground: true,
            content: `Invite the couple to share their own visual references — not from other weddings, but from life:

• Favorite **movies** or **brands**
• **Travel photos** they love
• **Fashion campaigns** or **art**

This helps reveal their *true aesthetic language*.`,
            quote: `**"When a couple shows you their favorite film, they're showing you how they want to be remembered."**`,
            quoteHighlighted: true,
            transitionTo: null,
          },
          {
            id: "emotional-storyboarding",
            title: "4. Emotional Storyboarding",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              {
                src: "/training/moodboard/05.12.2025%2010.52.47%20777%2029.jpg",
                alt: "Wedding day emotional moments",
                span: 6
              }
            ],
            content: `Every wedding has rhythm — morning calm, ceremony emotion, evening chaos.

Try to structure your moodboard like a film sequence:

1. **Preparation** — intimacy, quietness
2. **Ceremony** — anticipation, unity
3. **Portraits** — romance, storytelling
4. **Party** — energy, flash, movement

It helps both you and the couple imagine **the day as a narrative**.`,
            galleryImagesAfter: [
              {
                src: "/training/moodboard/04.12.2025%2023.56.51%20632%20224.jpg",
                alt: "Wedding reception atmosphere",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "style-harmony",
            title: "5. Style Harmony",
            darkBackground: true,
            content: `Keep your style consistent:

If you shoot soft and warm, don't promise moody black tones.

The moodboard isn't to please — it's to *define truthfully*.

You can say:`,
            quote: `"This is how I see your story. If you feel the same — we're in perfect sync."`,
            galleryImages: [
              {
                src: "/training/moodboard/05.12.2025%2000.50.04%20474%20273.jpg",
                alt: "Consistent style wedding photography",
                span: 6
              }
            ],
            transitionTo: null,
          },
          {
            id: "safety-kit",
            title: "6. Hidden Tip — The \"Safety Kit\" Section",
            darkBackground: true,
            content: `Add a small note or separate block:

**"What to bring for emergencies"** — it makes you look thoughtful and professional.

Include:

• Hairpins & tissues
• Flat shoes for breaks
• Small towel, makeup fix, blanket, umbrella
• Mini lint roller (for grooms!)

Couples love this — it shows you care beyond the camera.`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "posing-interaction",
        title: "POSING & INTERACTION — Capturing Connection, Not Poses",
        estimatedMinutes: 12,
        hook: "A portrait is not made in the camera but on either side of it. — Edward Steichen",
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            imagesFirst: true,
            content: `Good posing isn't about arranging bodies — it's about **guiding energy**.

People remember how they felt, not how they stood.

Your role is to create the space where connection naturally appears — and then recognize the exact second it blooms.`,
            galleryImages: [
              {
                src: "/training/posing-interaction/05.12.2025%2011.54.08%20924%20132.png",
                alt: "Couple in natural intimate pose",
                span: 6
              }
            ],
            transitionTo: "Let's start with the foundation of every great portrait...",
          },
          {
            id: "connection-first",
            title: "1. Connection Before Direction",
            darkBackground: true,
            content: `Before you lift your camera, connect.

Talk, listen, observe how they move together. Are they playful? Calm? Nervous?

Your tone and rhythm will shape theirs.`,
            callout: {
              title: "Pro Tip:",
              items: [
                "Start with conversation, not instruction.",
                "If they feel safe and seen, they'll start to move as themselves — and that's when the real poses happen."
              ],
              footer: null,
              color: "#3d4a5c"
            },
            transitionTo: "Now let's shift from poses to interactions...",
          },
          {
            id: "natural-interaction",
            title: "2. Natural Interaction — Not Choreography",
            darkBackground: true,
            content: `Think of posing as **guided interaction**.

You're not telling them what to do — you're setting the mood.

**Example cues instead of poses:**

• "Walk slowly and look at each other like it's your first date again."
• "Whisper something only they can hear."
• "Close your eyes and breathe together."

Each cue creates movement and emotion, not stiffness.`,
            galleryImages: [
              {
                src: "/training/posing-interaction/Screenshot%202025-11-25%20at%2020.40.59.png",
                alt: "Couple walking naturally",
                span: 2
              },
              {
                src: "/training/posing-interaction/IMG_5180.jpg",
                alt: "Intimate couple moment",
                span: 2
              }
            ],
            galleryText: {
              content: `In *Greg Williams'* philosophy, real beauty lies in "unforced moments" — he directs through energy, not commands.`,
              span: 2
            },
            transitionTo: "The smallest gestures often tell the biggest stories...",
          },
          {
            id: "micro-moments",
            title: "3. Micro-Moments — Hands, Eyes, Pauses",
            darkBackground: true,
            largeText: true,
            content: `Small gestures tell the biggest stories.`,
            galleryImages: [
              {
                src: "/training/posing-interaction/IMG_3618.jpg",
                alt: "Detail of hands touching",
                span: 3
              },
              {
                src: "/training/posing-interaction/IMG_3423.jpg",
                alt: "Intimate glance between couple",
                span: 3
              }
            ],
            contentAfter: `**Look for:**

• fingers gently touching fabric,
• overlapping hands,
• exchanged glances mid-sentence,
• one person fixing the other's hair or boutonnière.

These micro-moments turn static portraits into emotional frames.

That's what separates **a photo of two people** from **a photograph of love**.`,
            galleryImagesAfter: [
              {
                src: "/training/posing-interaction/Screenshot%202025-11-24%20at%2023.53.31.png",
                alt: "Tender moment captured",
                span: 3
              },
              {
                src: "/training/posing-interaction/Screenshot%202025-11-25%20at%2000.02.22.png",
                alt: "Emotional connection",
                span: 3
              }
            ],
            transitionTo: "Movement is your greatest ally...",
          },
          {
            id: "movement-flow",
            title: "4. Movement and Flow",
            darkBackground: true,
            content: `Stillness is your enemy.

Encourage couples to move — walk, dance, spin, lean, breathe.

When they move, their bodies fall into natural alignment; tension dissolves.

**Ideas:**

• "Walk toward me and talk about your honeymoon."
• "Hold hands and turn slowly."
• "Let your forehead rest on theirs."

Use **continuous shooting mode** and stay ready for *between* moments — that's where authenticity lives.`,
            galleryImages: [
              {
                src: "/training/posing-interaction/IMG_5448.jpg",
                alt: "Couple in motion",
                span: 3
              },
              {
                src: "/training/posing-interaction/IMG_5411.jpg",
                alt: "Natural movement captured",
                span: 3
              }
            ],
            transitionTo: "Groups present their own challenges...",
          },
          {
            id: "groups",
            title: "5. Groups — Storytelling Through Interaction",
            darkBackground: true,
            content: `Group photos don't need to be rigid.

Think of them as small narratives — clusters of connection.

**Tricks for real smiles:**

• Let them laugh together, not at you.
• Ask someone to tell a story about the couple.
• Capture side conversations — often they're more powerful than the posed one.`,
            quote: `"Don't control the moment — compose within it."
**— Greg Heisler**`,
            galleryImages: [
              {
                src: "/training/posing-interaction/Screenshot%202025-11-26%20at%2018.12.55.png",
                alt: "Group candid moment",
                span: 3
              },
              {
                src: "/training/posing-interaction/Screenshot%202025-11-26%20at%2018.13.17.png",
                alt: "Natural group interaction",
                span: 3
              }
            ],
            transitionTo: "Finally, the art of invisible guidance...",
          },
          {
            id: "invisible-direction",
            title: "6. The Secret of Invisible Direction",
            darkBackground: true,
            content: `The best photographers give direction **without interrupting emotion.**

Instead of saying "Turn left," say "Tilt your face toward the light."

Instead of "Smile," say "Think about that moment this morning."

Emotion blooms when people forget they're performing.

That's your art — to make forgetting possible.`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "couple-posing",
        title: "COUPLE POSING TECHNIQUES — Comfort, Chemistry & Natural Movement",
        estimatedMinutes: 15,
        hook: "A great pose isn't about position. It's about permission — to be themselves.",
        sections: [
          {
            id: "intro",
            title: "The Secret of Real Connection",
            darkBackground: true,
            callout: {
              title: "THE SECRET OF REAL CONNECTION",
              items: [
                "*\"A great pose isn't about position. It's about permission — to be themselves.\"*",
                "True posing is **not instruction** — it's **invitation**. It's how you make two people forget the camera and remember *each other*."
              ],
              footer: null
            },
            galleryImages: [
              {
                src: "/training/couple-posing/IMG_5529-2.jpg.png",
                alt: "Natural couple connection",
                span: 3
              },
              {
                src: "/training/couple-posing/IMG_5548.jpg",
                alt: "Intimate couple moment",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "comfort-first",
            title: "1. Comfort First — Posing Starts Before Posing",
            darkBackground: true,
            content: `Before you lift the camera, build **trust**. If they don't trust you — they'll never relax, no matter how good your light is.

Ask about their story. Make them laugh. Notice how they naturally interact — one might lead, the other follow. That's your *starting choreography*.`,
            callout: {
              title: "Pro Tip:",
              items: [
                "Begin with movement, not stillness. Walks, spins, a hug in motion — let them *arrive* in the frame.",
                "*The first 10 photos are warm-up.*"
              ],
              footer: null
            },
            transitionTo: null,
          },
          {
            id: "connection-beats-perfection",
            title: "2. Connection Beats Perfection",
            darkBackground: true,
            content: `Most couples think they need to "look good." Your job is to show them they already do.

**Try prompts, not commands:**

• "Breathe together."
• "Whisper something you'll never say out loud."
• "Look at their lips, but don't kiss yet..."

*Small micro-pauses between movements give you the most honest moments.*`,
            galleryImages: [
              {
                src: "/training/couple-posing/IMG_3678.jpg",
                alt: "Couple breathing together",
                span: 2
              },
              {
                src: "/training/couple-posing/IMG_3381.jpg",
                alt: "Intimate whisper moment",
                span: 2,
                stackWith: {
                  src: "/training/couple-posing/IMG_3659.jpg",
                  alt: "Connection before kiss"
                }
              },
              {
                src: "/training/couple-posing/IMG_3568.jpg",
                alt: "Micro-pause moment",
                span: 2
              }
            ],
            transitionTo: null,
          },
          {
            id: "posing-through-emotion",
            title: "3. Posing Through Emotion",
            darkBackground: true,
            content: `Think in **verbs**, not **nouns**. Not "pose," but "walk," "touch," "lean," "laugh." Actions activate emotion.

You can say: *"Pull her a bit closer, then look away like you're about to laugh."*

This turns the photo from a frozen frame into a living scene.`,
            transitionTo: null,
          },
          {
            id: "hands",
            title: "4. Hands — The Language of Intimacy",
            darkBackground: true,
            content: `Hands reveal connection. If they look awkward — the whole photo does.

**Guidelines:**

• Keep hands *doing something*: fixing hair, adjusting a sleeve, holding gently.
• Avoid "floating hands" — they should touch or rest naturally.
• Interlocked fingers = closeness.
• Gentle touch on shoulder or cheek = tenderness.`,
            quote: `*When you don't know what to do with hands — think what they'd do if the camera weren't here.*`,
            quoteHighlighted: true,
            transitionTo: null,
          },
          {
            id: "body-angles",
            title: "5. Body Angles & Energy Flow",
            darkBackground: true,
            content: `**To avoid stiffness:**

• Shift their weight — one foot forward.
• Don't align bodies perfectly parallel — slight angle creates depth.
• If one partner leads — let the other "melt in" slightly.
• Create diagonals — they add movement to static poses.`,
            callout: {
              title: "Dynamic Trick:",
              items: [
                "Separately, tell one partner to do something unexpected — this way, you'll capture the other's genuine reaction."
              ],
              footer: null
            },
            galleryImages: [
              {
                src: "/training/couple-posing/IMG_3142.jpg",
                alt: "Dynamic angle example",
                span: 3,
                stackWith: {
                  src: "/training/couple-posing/IMG_3175.jpg",
                  alt: "Weight shift pose"
                }
              },
              {
                src: "/training/couple-posing/IMG_3179.jpg",
                alt: "Diagonal composition",
                span: 3,
                stackWith: {
                  src: "/training/couple-posing/IMG_3019.jpg",
                  alt: "Natural energy flow"
                }
              }
            ],
            transitionTo: null,
          },
          {
            id: "emotional-cues",
            title: "6. Emotional Cues",
            darkBackground: true,
            content: `Ask questions instead of giving orders.

*"What's the first thing you noticed about them?"*

*"When did you realize you were in love?"*

They'll respond — and that sparkle you get right after is the **shot**.`,
            transitionTo: null,
          },
          {
            id: "almost-moment",
            title: "7. The \"Almost Moment\"",
            darkBackground: true,
            content: `Don't rush kisses. The second *before* lips touch — that's electricity.

Hold the silence — click.

*Real romance happens in anticipation.*`,
            transitionTo: null,
          },
          {
            id: "reading-personalities",
            title: "8. Reading Personalities",
            darkBackground: true,
            content: `No two couples are the same.

• **Shy couples** → focus on closeness, forehead touches, whispers.
• **Energetic couples** → laughter, running, dancing.
• **Elegant couples** → symmetry, slower movement, framing.

Your direction should **fit their rhythm**, not impose yours.`,
            transitionTo: null,
          },
          {
            id: "final-thought",
            title: "9. Final Thought",
            darkBackground: true,
            callout: {
              icon: "🕊️",
              title: null,
              textLines: [
                "When people feel seen, they open up.",
                "Your goal isn't to *pose* them — it's to *see* them."
              ],
              quote: `**Photographers freeze time. But great ones free emotion.**`
            },
            galleryImages: [
              {
                src: "/training/couple-posing/DSC_0621.jpg",
                alt: "Final portrait example",
                span: 3
              },
              {
                src: "/training/couple-posing/DSC_0626.jpg",
                alt: "Final portrait example",
                span: 3
              }
            ],
            transitionTo: null,
          },
        ],
      },
      {
        id: "group-posing",
        title: "GROUP POSING & INTERACTION — Families, Friends & Bridal Party",
        estimatedMinutes: 15,
        hook: `"You're not photographing people. You're photographing their relationships."`,
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            content: `**DIRECTING GROUPS WITHOUT KILLING THE VIBE**`,
            quote: `"You're not photographing people.
You're photographing their relationships."`,
            galleryImages: [
              {
                src: "/training/group-posing/Screenshot_2025-11-24_at_19.44.16.png",
                alt: "Group portrait example",
                span: 6
              }
            ],
            contentAfter: `Group portraits are one of the hardest — and most underrated — parts of wedding photography.

Because here, it's not just about lighting or posing — it's about **human orchestration**.

You're managing energy, emotions, and a crowd that just wants champagne.`,
            transitionTo: null,
          },
          {
            id: "preparation",
            title: "1. Preparation = Peace",
            darkBackground: true,
            content: `Before the wedding, ask the couple to list the **must-have group combinations**.

Parents, siblings, grandparents, bridesmaids, etc.

This saves chaos later and gives you authority without stress.`,
            callout: {
              title: "Pro Tip:",
              items: [
                "Print the list or keep it on your phone.",
                "Nominate one friend or coordinator to help call people — you'll thank yourself later."
              ],
              footer: null,
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "reading-room",
            title: "2. Reading the Room",
            darkBackground: true,
            content: `Every group has its own energy.

Some families are formal, others are wild.

Don't try to make everyone fit the same mold.

**Observe:**

• Who's the leader (usually mom)?
• Who's shy?
• Who makes everyone laugh?

Once you know these roles, you can guide them naturally.`,
            galleryImages: [
              {
                src: "/training/group-posing/Screenshot_2025-11-24_at_19.40.55.png",
                alt: "Reading group energy",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-11-24_at_19.49.52.png",
                alt: "Family group dynamics",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "directing-humor",
            title: "3. Directing with Humor & Empathy",
            darkBackground: true,
            content: `The best tool you have isn't your camera — it's your **voice**.

Keep it friendly but firm.

Say:

*"Alright, big smiles for grandma — she's the star!"*

*"Okay, you two look too serious — are we at a wedding or a passport office?"*

People respond better to playfulness than to orders.`,
            callout: {
              icon: "✨",
              title: null,
              textLines: [
                "*You're conducting laughter, not posing statues.*"
              ],
              borderOnly: true,
              smallMargin: true,
              color: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/group-posing/Screenshot_2025-11-24_at_22.44.44.png",
                alt: "Directing with humor",
                span: 3
              },
              {
                src: "/training/group-posing/3c56da29-db45-4d97-a16d-05406cde59a4.png",
                alt: "Group laughing naturally",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "arranging-composition",
            title: "4. Arranging the Composition",
            darkBackground: true,
            content: `Here's a formula that always works:

• **Center the couple** — they're your anchor.
• **Parents and immediate family** close to the couple.
• **Bridesmaids/groomsmen** balanced by color or height.
• **Keep lines loose** — avoid stiff symmetry.`,
            callout: {
              title: "Pro Tip:",
              items: [
                "Have everyone *lean slightly in* toward the center — it adds warmth subconsciously."
              ],
              footer: null,
              color: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/group-posing/Screenshot_2025-11-26_at_18.31.25.png",
                alt: "Group composition example",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-11-27_at_18.19.28.png",
                alt: "Balanced group arrangement",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-12-03_at_05.58.37.png",
                alt: "Couple centered in group",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-12-03_at_06.01.59.png",
                alt: "Family arrangement",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-12-03_at_06.03.58.png",
                alt: "Bridal party composition",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-12-03_at_05.56.38.png",
                alt: "Loose lines in group",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "hands-heads",
            title: "5. Hands, Heads & Hierarchy",
            darkBackground: true,
            content: `Little details make the difference:

• Hands should rest naturally — on waist, shoulder, bouquet.
• Avoid hidden hands or crossed arms.
• Heads tilted slightly toward the middle — it signals connection.

If a group looks "flat," create layers — people in front sitting, others standing behind.

**Depth = life.**`,
            transitionTo: null,
          },
          {
            id: "mood-alive",
            title: "6. Keep the Mood Alive",
            darkBackground: true,
            content: `Between formal group shots, shoot the **in-between** moments:

Laughter, someone fixing a tie, grandma hugging the bride.

Those are gold.

Don't rush — if you rush, tension grows.

Keep talking, keep them breathing, keep it *light*.`,
            callout: {
              icon: "🎯",
              title: null,
              textLines: [
                "*The moment you stop posing and start observing — real magic happens.*"
              ]
            },
            galleryImages: [
              {
                src: "/training/group-posing/Screenshot_2025-11-24_at_22.30.10.png",
                alt: "In-between moments",
                span: 3
              },
              {
                src: "/training/group-posing/Screenshot_2025-11-24_at_22.51.15.png",
                alt: "Candid group moment",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "chaos-control",
            title: "7. Chaos Control During the Party",
            darkBackground: true,
            content: `Evening group shots are trickier — people scatter, lighting shifts, alcohol increases.

**Quick setup:**

• Use a single flash bounced off wall.
• ISO 800–1600, f/4.5–7.1, 1/250.
• Use direct flash.
• Call people in clusters — 4–5 max at a time.

Keep it fast, fun, and positive — your calm energy leads.`,
            galleryImages: [
              {
                src: "/training/group-posing/Screenshot_2025-11-25_at_00.25.34.png",
                alt: "Party group shot",
                span: 2
              },
              {
                src: "/training/group-posing/Screenshot_2025-11-25_at_00.28.23.png",
                alt: "Evening group lighting",
                span: 2
              },
              {
                src: "/training/group-posing/Screenshot_2025-11-25_at_00.26.08.png",
                alt: "Flash group shot",
                span: 2
              },
              {
                src: "/training/group-posing/Screenshot_2025-11-25_at_00.58.23.png",
                alt: "Party cluster shot",
                span: 3
              },
              {
                src: "/training/group-posing/fd6da966-694c-4533-a907-fa8752bfad5d.png",
                alt: "Evening party group",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "signature-shot",
            title: "8. Bonus — Your \"Signature\" Group Shot",
            darkBackground: true,
            content: `Find a playful signature shot that fits your style.

Maybe a champagne pop, a coordinated jump, or a cinematic walking frame.

Clients **love** when your portfolio has a recognizable "moment."`,
            callout: {
              icon: "🔆",
              title: null,
              textLines: [
                "*Consistency creates your brand. Connection keeps it human.*"
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "composition",
        title: "COMPOSITION ESSENTIALS — Seeing Like a Storyteller",
        estimatedMinutes: 15,
        hook: "Rule of thirds, leading lines, framing, layering, visual rhythm",
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            imagesFirst: true,
            galleryImages: [
              {
                src: "/training/composition/Screenshot_2025-11-25_at_20.07.51.png",
                alt: "Composition example",
                span: 6
              }
            ],
            quote: `"A good photograph is knowing where to stand."
— **Ansel Adams**`,
            contentAfter: `Composition isn't about rules — it's about **guiding emotion**.
It's the language of how the viewer feels what you saw.
And in wedding photography, composition isn't about control — it's about translating **connection and chaos into harmony**.`,
            link: {
              text: "Composition practice by @jan.sollberger",
              url: "https://vm.tiktok.com/ZMAEFmfEE/"
            },
            transitionTo: null,
          },
          {
            id: "rule-of-thirds",
            title: "1. Rule of Thirds — Balance Without Symmetry",
            darkBackground: true,
            content: `Imagine your frame divided into nine equal parts.
Place key elements — faces, eyes, rings, flowers — along those lines or intersections.`,
            callout: {
              icon: "💡",
              title: "Why it works:",
              textLines: [
                "Our brains find *off-center balance* natural and dynamic. It gives breathing space and creates a feeling of presence."
              ],
              color: "#4a4a4a"
            },
            contentAfter: `**Pro tip:**
Don't always aim for perfection — shift slightly.
Sometimes, placing the couple *just outside* the grid adds spontaneity, as if the moment continues beyond the frame.`,
            transitionTo: null,
          },
          {
            id: "golden-ratio",
            title: "Add-on: The Golden Ratio — Nature's Visual Harmony",
            darkBackground: true,
            content: `The **golden ratio (1:1.618)** is nature's version of perfect balance.
You'll find it in seashells, leaves, architecture — and in many classic photographs.

In composition, it's like the rule of thirds, but more **organic**.
The golden spiral gently guides the eye through the image, leading it to the emotional core — a face, gesture, or focal point.

Many masters — from *Henri Cartier-Bresson* to *Sebastião Salgado* — composed instinctively following this flow long before digital overlays existed.`,
            galleryImages: [
              {
                src: "/training/composition/Screenshot_2025-12-02_at_23.00.56.png",
                alt: "Golden ratio example",
                span: 3
              }
            ],
            callout: {
              icon: "⚡",
              title: "Pro tip:",
              textLines: [
                "In Lightroom or some cameras, you can activate the *Golden Spiral* or *Phi Grid* overlay.",
                "It trains your eye to see **fluid, natural balance** — composition that feels effortless rather than measured."
              ],
              color: "#4a3f3f",
              inGrid: true
            },
            transitionTo: null,
          },
          {
            id: "leading-lines",
            title: "2. Leading Lines — Guiding the Gaze",
            darkBackground: true,
            content: `Every photo has invisible paths — roads, walls, railings, shadows, arms, even glances.
Use them to **lead the viewer's eye** toward your subject.`,
            callout: {
              icon: "📸",
              title: "Practical wedding example:",
              textLines: [
                "The aisle leading to the altar, or guests' gaze toward the bride — both are natural leading lines."
              ],
              color: "#4a4a4a"
            },
            contentAfterCallout: `**Hidden trick:**
Soft diagonal lines often create more movement than horizontal ones.
A photographer like *Jose Villa* uses them subtly in film work — they make even still images feel alive.`,
            galleryImages: [
              {
                src: "/training/composition/b57ef4a4-3e3c-4798-a4ae-6b16f6f160c6.png",
                alt: "Leading lines example",
                span: 3
              },
              {
                src: "/training/composition/Screenshot_2025-11-25_at_19.13.37.png",
                alt: "Leading lines in wedding",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "framing",
            title: "3. Framing — Intimacy Within Chaos",
            darkBackground: true,
            content: `You can frame your subjects *within something else* — doorways, mirrors, curtains, tree branches, or even people's shoulders.`,
            callout: {
              icon: "💡",
              title: "Why it works:",
              textLines: [
                "It isolates the emotion. You're not capturing \"everything,\" you're letting the viewer peek *into* a story."
              ],
              color: "#4a4a4a"
            },
            contentAfterCallout: `**Tip:**
Step behind something instead of in front.
That one gesture transforms your role from "observer" to "witness."`,
            galleryColumns: 4,
            galleryImages: [
              {
                src: "/training/composition/Screenshot_2025-11-24_at_22.35.00.png",
                alt: "Framing example"
              },
              {
                src: "/training/composition/Screenshot_2025-11-27_at_16.14.30.png",
                alt: "Framing through doorway"
              },
              {
                src: "/training/composition/Screenshot_2025-11-28_at_21.24.57.png",
                alt: "Framing with mirrors"
              },
              {
                src: "/training/composition/Screenshot_2025-11-25_at_00.37.32.png",
                alt: "Framing with branches",
                stackWith: {
                  src: "/training/composition/Screenshot_2025-11-29_at_17.40.53.png",
                  alt: "Framing with people"
                }
              }
            ],
            transitionTo: null,
          },
          {
            id: "layering",
            title: "4. Layering — Depth and Presence",
            darkBackground: true,
            content: `Depth comes from **three planes**: foreground, subject, and background.
Having an element close to the lens adds "texture" and realism.

**Example:**
A blurred flower in front of a kissing couple.
Or guests' silhouettes in front of the dancing bride.`,
            callout: {
              icon: "⚡",
              title: "Pro trick:",
              textLines: [
                "Use low apertures (f/1.4–f/2.8) to blend layers softly and create that creamy cinematic feel — but always keep focus where the emotion lives."
              ],
              color: "#4a3f3f"
            },
            transitionTo: null,
          },
          {
            id: "visual-rhythm",
            title: "5. Visual Rhythm — The Unseen Harmony",
            darkBackground: true,
            content: `Rhythm in composition is repetition of **shapes, colors, gestures, or light**.
It's what makes a series of photos *feel musical*.

**How to apply:**

• Capture repetition: chairs, steps, arms, candles.
• Break rhythm intentionally: one different gesture or expression becomes the emotional peak.`,
            quote: `"If it can be written, or thought, it can be photographed."
— **Stanley Kubrick**`,
            contentAfter: `This is rhythm in storytelling — visual poetry through observation.`,
            transitionTo: null,
          },
          {
            id: "secret",
            title: "The Secret Nobody Talks About",
            darkBackground: true,
            content: `Even "wrong" composition can be right — if it **serves emotion**.

A slightly cut-off hand, a tilted horizon, a blurred foreground — these aren't flaws; they're part of the story's honesty.`,
            galleryImages: [
              {
                src: "/training/composition/_MG_2378.jpg",
                alt: "Breaking composition rules",
                span: 3
              }
            ],
            callout: {
              icon: "✨",
              title: null,
              textLines: [
                "The real mastery isn't following rules — it's **knowing when to break them gracefully**."
              ],
              color: "#4a4a4a",
              inGrid: true
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "angles-depth",
        title: "ANGLES & DEPTH — Movement and Space for Storytelling",
        estimatedMinutes: 12,
        hook: `"Don't shoot what it looks like. Shoot what it feels like."`,
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            quote: `"Don't shoot what it looks like. Shoot what it feels like."
— **David Alan Harvey**`,
            content: `In wedding photography, **depth isn't just visual — it's emotional.**

Angles and spatial layering shape how the viewer *enters* the story. They decide whether a photo feels staged or alive, flat or cinematic.`,
            contentInGrid: true,
            galleryImages: [
              {
                src: "/training/angles-depth/IMG_4457.jpg",
                alt: "Angles and depth example",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "language-of-angles",
            title: "1. The Language of Angles",
            darkBackground: true,
            content: `Every angle changes what the viewer *feels* about a subject.`,
            table: {
              headers: ["Angle", "Emotion / Effect", "Use in Weddings"],
              rows: [
                ["**Eye level**", "Neutral, honest", "Portraits, vows, storytelling moments"],
                ["**Low angle**", "Power, importance, admiration", "Bride's walk, first dance, architecture"],
                ["**High angle**", "Fragility, tenderness", "Getting ready, couple cuddling, details"],
                ["**Diagonal (tilted)**", "Energy, tension, chaos", "Dancing, confetti, emotions"],
                ["**Through objects**", "Mystery, intimacy", "Reflections, doorways, windows"]
              ]
            },
            contentAfterTable: `Each choice of angle is not just composition — it's **narrative.**

The same smile, shot from above, feels soft and nostalgic. From below — iconic, powerful.`,
            galleryImages: [
              {
                src: "/training/angles-depth/IMG_4034-2.jpg",
                alt: "Low angle shot",
                span: 2
              },
              {
                src: "/training/angles-depth/IMG_3906-куул.jpg",
                alt: "High angle shot",
                span: 2
              },
              {
                src: "/training/angles-depth/IMG_4349.jpg",
                alt: "Through objects shot",
                span: 2
              }
            ],
            transitionTo: null,
          },
          {
            id: "depth-dimension",
            title: "2. Depth — Building Dimension",
            darkBackground: true,
            content: `Depth gives a photograph its *cinematic presence.*

A flat photo shows people; a deep photo places them *in their world.*

**Technique:**

• Combine **foreground, subject, background.**
• Keep something close to the lens — flowers, fabric, glass, shoulders.
• Use apertures from **f/1.4 to f/2.8** to separate planes gently.
• Focus not on the sharpest image, but on the **most emotional layer**.`,
            callout: {
              icon: "💡",
              title: "Pro Tip:",
              textLines: [
                "If a scene feels too \"flat,\" move 2–3 steps sideways or crouch lower.",
                "Sometimes *one meter of movement* changes a story completely."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "movement",
            title: "3. Movement — When the Frame Breathes",
            darkBackground: true,
            content: `Depth also lives in motion — wind in the dress, walking, laughter, a veil in sunlight.

**Try this:**

• Don't stop the couple mid-step — walk *with* them.
• Follow with a slow pan to keep a sense of flow.
• Shoot through objects like leaves or guests — let blur tell part of the story.

**Remember:**

Your lens doesn't just record movement; it *translates rhythm.*`,
            transitionTo: null,
          },
          {
            id: "perspective",
            title: "4. Perspective — Emotion Through Position",
            darkBackground: true,
            content: `Where you stand changes everything.

Move around your subject — not just for variety, but for **meaning**.

• **Behind** = intimacy
• **Beside** = equality
• **Below** = admiration
• **Above** = tenderness
• **Far away** = storytelling and atmosphere`,
            quote: `"Change your position, and you change the truth of the image."
— **Gregory Heisler**`,
            contentAfterQuote: `Weddings are full of layers — *emotionally and physically.*

Your goal is to move through them like a dancer — quietly, purposefully.`,
            galleryImages: [
              {
                src: "/training/angles-depth/IMG_4140-2.jpg",
                alt: "Perspective example",
                span: 2
              },
              {
                src: "/training/angles-depth/IMG_4192.jpg",
                alt: "Position and meaning",
                span: 2
              },
              {
                src: "/training/angles-depth/IMG_41382.jpg",
                alt: "Moving through layers",
                span: 2
              }
            ],
            transitionTo: null,
          },
          {
            id: "real-world-example",
            title: "5. Real-World Example",
            darkBackground: true,
            content: `A bride hugs her father.

• **From the side** — it's a moment.
• **From slightly behind** — it's *their* moment.
• **From above** — it's memory.
• **From the floor** — it's poetry.

That's what angles and depth do:

**They turn a simple hug into a story.**`,
            transitionTo: null,
          },
          {
            id: "hidden-truth",
            title: "The Hidden Truth",
            darkBackground: true,
            content: `Beginners often think "better gear = better depth."

But true depth is not from the lens — it's from *awareness.*

It's about stepping into the space, feeling the distance, and using composition to make emotions visible.`,
            galleryImages: [
              {
                src: "/training/angles-depth/IMG_2961.jpg",
                alt: "Depth through awareness",
                span: 2
              },
              {
                src: "/training/angles-depth/IMG_2951.jpg",
                alt: "Feeling the distance",
                span: 2,
                stackWith: {
                  src: "/training/angles-depth/IMG_3119.jpg",
                  alt: "Composition and emotion"
                }
              },
              {
                src: "/training/angles-depth/IMG_2957.jpg",
                alt: "Making emotions visible",
                span: 2
              }
            ],
            transitionTo: null,
          },
        ],
      },
      {
        id: "angles-movement",
        title: "ANGLES & MOVEMENT — Ceremonies, Dances, Emotional Beats",
        estimatedMinutes: 12,
        hook: `"Angles create meaning. Movement reveals truth."`,
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            topCallout: {
              icon: "✨",
              title: "THE FLOW OF A WEDDING IS A CHOREOGRAPHY — YOU MOVE WITH IT",
              textLines: [
                `"Angles create meaning. Movement reveals truth."`
              ],
              backgroundColor: "#4a4a4a"
            },
            content: `A wedding is not static.

Your photography shouldn't be either.

Angles and movement are two of your strongest storytelling tools — they shape the viewer's emotional experience without saying a single word.`,
            galleryImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.37.20.png",
                alt: "Angles in wedding photography",
                span: 3
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.37.37.png",
                alt: "Movement in wedding photography",
                span: 3
              }
            ],
            transitionTo: "ceremony",
          },
          {
            id: "ceremony",
            title: "CEREMONY — move like you're not there",
            darkBackground: true,
            content: `Ceremony movement must be:

• slow
• soft
• intentional
• invisible

You're not a participant — you're a quiet observer.`,
            galleryImages: [
              {
                src: "/training/angles-movement/16a4fb7b-73b6-4dc2-9430-f0ec06cbdc94.png",
                alt: "Quiet observation",
                span: 3
              },
              {
                src: "/training/angles-movement/973c023e-ab42-46fe-988b-9c29cfd13c75.png",
                alt: "Ceremony movement",
                span: 3
              }
            ],
            contentAfterImages: `**The Three-Zone Method (industry secret)**

**Zone 1 — Front Left**
Reactions of the groom, family, first tears.

**Zone 2 — Front Right**
Bride's expressions, close emotional moments.

**Zone 3 — Back Center**
Wide storytelling frame, context, symmetry.`,
            calloutAfterImages: {
              icon: "⚡",
              textLines: [
                "Rotate between these zones during:",
                "• transitions",
                "• readings",
                "• music pauses",
                "• unity rituals"
              ],
              backgroundColor: "#5c3d3d"
            },
            contentBeforeAdditionalImages: `**Must-have ceremony shots:**

• hands holding
• parents' reactions
• wide shot of the entire space
• close-up of rings exchange
• listeners, not just speakers
• walking out (hug chaos = gold)`,
            additionalImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_21.58.57.png",
                alt: "Ceremony must-have shot",
                span: 3
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.01.51.png",
                alt: "Ceremony must-have shot",
                span: 3
              }
            ],
            transitionTo: "first-dance",
          },
          {
            id: "first-dance",
            title: "FIRST DANCE — orbit, don't stand still",
            darkBackground: true,
            content: `Static photography = static emotion.

The first dance is ALL about movement.

If you don't move with them — you lose 70% of the magic.

**Movement technique: ORBITING**

Walk in a slow circle around the couple (½ or full arc).

Use:

• **35mm** for intimacy
• **50mm** for cinematic portraits
• **24mm** for dramatic wide atmosphere`,
            callout: {
              icon: "🔑",
              title: "Key:",
              textLines: [
                "Always move *slower* than they do — this creates floating, elegant motion in your frames."
              ],
              borderOnly: true,
              color: "#4a4a4a"
            },
            contentAfterCallout: `**Capture:**

• hands clasping
• dress movement
• hair glow from backlight
• facial microexpressions
• silhouettes when DJ lights hit from behind`,
            galleryImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_23.02.33.png",
                alt: "First dance movement",
                span: 3
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.56.57.png",
                alt: "First dance orbiting",
                span: 3
              }
            ],
            additionalCallout: {
              title: "Pro Tip:",
              textLines: [
                "The best frame often happens when they're turning away from the guests — the \"private moment in public.\""
              ],
              borderOnly: true,
              backgroundColor: "#5c3d3d"
            },
            transitionTo: "speeches",
          },
          {
            id: "speeches",
            title: "SPEECHES — your angles shape the emotion",
            darkBackground: true,
            content: `You should shoot speeches from *three* angles:

**1. The speaker**
Not too close — allow hands and gestures.

**2. The couple listening**
This is the real gold.
Soft smiles, tears, pride.

**3. Audience reactions**
Laughter, tears, whispered commentary.`,
            callout: {
              icon: "💡",
              textLines: [
                "Your job is not to capture the speech — but its emotional echo."
              ],
              backgroundColor: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_19.47.11.png",
                alt: "Speech angles",
                span: 3
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_19.37.41.png",
                alt: "Emotional echo",
                span: 3
              }
            ],
            transitionTo: "dance-floor",
          },
          {
            id: "dance-floor",
            title: "DANCE FLOOR — controlled chaos",
            darkBackground: true,
            content: `Dance floors are wild — this is where creativity blooms.

**Your movement philosophy:**

• Get close. Closer. Even closer.
• Lower your angle for energy.
• Lift the camera above your head for crowd hype.
• Shoot through shoulders, glasses, confetti.

**Try lenses:**

• **24mm** → energy
• **35mm** → interaction
• **50mm** → "editorial moments" among chaos

**Micro-movements:**

• step in / step out
• tilt slightly
• sway with the rhythm
• shoot between arms, hair, champagne splashes

This creates frames that *feel* like you were dancing too.`,
            galleryImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.15.57.png",
                alt: "Dance floor energy",
                span: 2
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.14.20.png",
                alt: "Dance floor chaos",
                span: 2
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-24_at_22.17.12.png",
                alt: "Dance floor creativity",
                span: 2
              }
            ],
            transitionTo: "emotional-beats",
          },
          {
            id: "emotional-beats",
            title: "EMOTIONAL BEATS — anticipate, don't chase",
            darkBackground: true,
            content: `Emotion has a pattern:

**build-up → peak → release.**

You should position yourself *before* the peak happens.`,
            galleryImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-11-25_at_00.06.56.png",
                alt: "Emotional anticipation",
                span: 3
              },
              {
                src: "/training/angles-movement/Screenshot_2025-11-25_at_00.25.34.png",
                alt: "Emotional peak",
                span: 3
              }
            ],
            contentAfterImages: `**Examples:**

• hugging parents
• groom seeing bride
• bride reacting to speech
• children running to the couple
• sudden laughter explosions

**How to anticipate:**

• watch hands
• watch breathing
• watch eyes
• watch the person who's about to "burst"`,
            calloutAfterImages: {
              icon: "⭐",
              textLines: [
                "Great photographers don't react.",
                "",
                "They *predict*."
              ],
              borderOnly: true,
              backgroundColor: "#4a4a4a"
            },
            transitionTo: "storytelling-triangle",
          },
          {
            id: "storytelling-triangle",
            title: "THE \"STORYTELLING TRIANGLE\" — a movement rule",
            darkBackground: true,
            content: `For any big scene, shoot:

**➤ wide → medium → close-up**

Repeat.

This gives your gallery cinematic rhythm.

• **Wide** = context
• **Medium** = story
• **Close-up** = emotion

Never stay in one distance too long.`,
            transitionTo: "bonus",
          },
          {
            id: "bonus",
            title: "BONUS — iconic movement shots",
            darkBackground: true,
            content: `Try these:

• walking hand-in-hand toward low sun
• couple spinning
• veil in the wind
• confetti storm
• bride running toward light source
• groom lifting bride (movement + joy)`,
            galleryImages: [
              {
                src: "/training/angles-movement/Screenshot_2025-12-03_at_02.47.37.png",
                alt: "Iconic movement shot",
                span: 3
              },
              {
                src: "/training/angles-movement/Screenshot_2025-12-03_at_02.47.20.png",
                alt: "Iconic movement shot",
                span: 3
              }
            ],
            calloutAfterImages: {
              icon: "✨",
              textLines: [
                "If your couple is stiff → movement solves everything."
              ],
              borderOnly: true,
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "creative-lighting",
        title: "CREATIVE Lighting Techniques",
        estimatedMinutes: 15,
        sections: [
          {
            id: "silhouettes",
            title: "1) Silhouettes — when shape becomes everything",
            darkBackground: true,
            content: `A silhouette works only when the **SHAPE** is readable, not when everything is black.

The key to a great silhouette:

• Light behind the couple
• Profile visible and clean
• Space between arms and body (creates separation)
• Stretch the neck slightly (elongates the form)
• Add a rim-light for cinematic edge definition`,
            callout: {
              icon: "💡",
              textLines: [
                "Silhouettes aren't about losing detail — they're about emphasizing form. If you can't recognize the couple from the outline, it's not a silhouette, it's just underexposed."
              ],
              color: "#3d4a5c"
            },
            galleryImages: [
              {
                src: "/training/creative-lighting/Screenshot_2025-11-25_at_18.37.53.png",
                alt: "Silhouette example",
                span: 6
              }
            ],
            transitionTo: "backlight",
          },
          {
            id: "backlight",
            title: "2) Backlight (spotlights/windows/headlights)",
            darkBackground: true,
            content: `Backlighting is one of the most flattering and versatile techniques in wedding photography.

How it works:

• Light positioned behind the subject
• To avoid direct flares hitting the face you can use lens hood, but sometimes it might be aesthetically
• Hair glow = instant magic and separation from background`,
            callout: {
              icon: "✨",
              title: "Why it matters:",
              textLines: [
                "Backlight creates depth, warmth, and a dreamy halo effect. It works beautifully during golden hour, but also with artificial lights in dim venues."
              ],
              backgroundColor: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/creative-lighting/Screenshot_2025-11-27_at_19.21.56.png",
                alt: "Backlight example 1",
                span: 3
              },
              {
                src: "/training/creative-lighting/Screenshot_2025-11-29_at_17.47.06.png",
                alt: "Backlight example 2",
                span: 3
              }
            ],
            transitionTo: "golden-hour",
          },
          {
            id: "golden-hour",
            title: "3) Golden Hour — the wedding photographer's secret weapon",
            darkBackground: true,
            content: `Golden hour is soft, warm, flattering — and forgiving.

Best practices:

• Works best 30–40 minutes before sunset
• Use reflectors to bounce light back onto faces (or position couple near a bright wall)
• Meter for the couple, let the background glow`,
            callout: {
              icon: "⚡",
              title: "Pro insight:",
              textLines: [
                "Golden hour is short. Scout your location beforehand, know your angles, and move fast. Don't waste 10 minutes deciding where to stand."
              ],
              color: "#4a3f3f"
            },
            galleryImages: [
              {
                src: "/training/creative-lighting/Screenshot_2025-11-26_at_00.39.15.png",
                alt: "Golden hour example 1",
                span: 3
              },
              {
                src: "/training/creative-lighting/Screenshot_2025-11-26_at_22.05.23.png",
                alt: "Golden hour example 2",
                span: 3
              }
            ],
            transitionTo: "night-scenes",
          },
          {
            id: "night-scenes",
            title: "4) Night Scenes — embrace the darkness. (Rarely used in wedding photography)",
            darkBackground: true,
            content: `Night photography isn't about fighting the dark — it's about using existing light intentionally.

Technical approach:

• Wide aperture (f/1.4–f/2.8)
• Higher ISO (1600–3200)
• Use neon signs, street lamps, car headlights, fairy lights`,
            callout: {
              icon: "🌙",
              title: "Mood matters:",
              textLines: [
                "Night scenes feel editorial, urban, cinematic. Don't flood the scene with flash — let the environment tell the story.",
                "",
                "But for classic wedding photos at the night, use a flash as your best assistant."
              ],
              backgroundColor: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.22.15.jpeg",
                alt: "Night scene example 1",
                span: 3
              },
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.22.19.jpeg",
                alt: "Night scene example 2",
                span: 3
              }
            ],
            transitionTo: "light-beams",
          },
          {
            id: "light-beams",
            title: "5) Light Beams — physics meets poetry",
            darkBackground: true,
            content: `Light beams (also called "God rays") appear only when particles scatter light: smoke, dust, mist, fog.

How to capture them:

• Position couple at the edge of light and shadow
• Capture rays breaking through trees, blinds, windows
• Underexpose slightly to preserve beam definition`,
            callout: {
              icon: "🔬",
              title: "Core physics:",
              textLines: [
                "You can't shoot beams in clean air. You need atmosphere. Wedding venues with fog machines, outdoor mist, or dusty barns are perfect."
              ],
              backgroundColor: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.28.44.jpeg",
                alt: "Light beams example 1",
                span: 3
              },
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.28.46.jpeg",
                alt: "Light beams example 2",
                span: 3
              }
            ],
            transitionTo: "curtain-light",
          },
          {
            id: "curtain-light",
            title: "6) Curtain Light — framing with fabric and glow",
            darkBackground: true,
            content: `One of the most romantic and elegant lighting setups — simple, repeatable, timeless.

Setup:

• Couple stands 1–2 meters from the window
• Photographer positions near the window
• Curtains naturally frame the scene and diffuse harsh light`,
            callout: {
              icon: "💡",
              title: "Why it works:",
              textLines: [
                "You get soft directional light + built-in framing in one shot."
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: "prism-work",
          },
          {
            id: "prism-work",
            title: "7) Prism Work — intentional distortion and color",
            darkBackground: true,
            content: `Prism effects add dreamy, editorial, fashion-forward energy to your images.

How to use:

• Hold a prism or glass piece close to the lens, on the edge of the frame
• Creates glow, color distortion, soft reflections, and bokeh scatter
• Move it slowly — small shifts create big changes`,
            callout: {
              icon: "⚠️",
              title: "Caution:",
              textLines: [
                "Easy to overdo. Use prisms sparingly and intentionally — they're a spice, not the main dish."
              ],
              color: "#4a3f3f"
            },
            transitionTo: "controlled-blur",
          },
          {
            id: "controlled-blur",
            title: "8) Controlled Blur — motion as an artistic choice",
            darkBackground: true,
            content: `Blur isn't a mistake when it's intentional. It creates emotion, energy, and movement.

Techniques:

• **Twist blur:** rotate the camera slightly during exposure
• **Zoom blur:** zoom in or out while the shutter is open (it exist, but it's not very popular these days)
• Use slow shutter speed (1/20–1/50)`,
            callout: {
              icon: "🎯",
              title: "When to use it:",
              textLines: [
                "First dance, party scenes, chaotic reception moments. Controlled blur adds life and atmosphere — sharp images can feel too static. But be careful."
              ],
              color: "#3d4a5c"
            },
            galleryImages: [
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.31.27.jpeg",
                alt: "Controlled blur example 1",
                span: 3,
                stackWith: {
                  src: "/training/creative-lighting/photo_2025-12-05_12.31.33.jpeg",
                  alt: "Controlled blur example 2"
                }
              },
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.31.30.jpeg",
                alt: "Controlled blur example 3",
                span: 3
              },
              {
                src: "/training/creative-lighting/photo_2025-12-05_12.31.31.jpeg",
                alt: "Controlled blur example 4",
                span: 6
              }
            ],
            transitionTo: "mist-filter",
          },
          {
            id: "mist-filter",
            title: "9) Mist Filter — analog softness in a digital world",
            darkBackground: true,
            content: `The mist filter technique gives a soft, dreamy, film-like quality to digital images.

How it works:

• Breathe lightly on your lens to fog it slightly
• Shoot immediately before the fog clears (5–10 seconds max)
• Result: soft glowing edges, reduced contrast, ethereal mood`,
            callout: {
              icon: "⚠️",
              title: "Important:",
              textLines: [
                "This works best in backlit or high-contrast scenes. In flat light, it just looks out of focus. Know when to use it."
              ],
              color: "#4a3f3f"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "specular-highlights",
        title: "Specular Highlights — \"The Jewelry of Light\"",
        estimatedMinutes: 8,
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            topCallout: {
              icon: "💡",
              title: "What are specular highlights?",
              textLines: [
                "Specular highlights are small, bright, concentrated reflections that create **sparkle, depth, and luxury** in an image.",
                "",
                "Wedding photographers LOVE them — in the background, in the eyes, and in bokeh."
              ],
              borderOnly: true,
              backgroundColor: "#4a4a4a"
            },
            content: `Specular highlights are the jewelry of light — those magical bright spots that transform ordinary photos into cinematic moments.`,
            transitionTo: "background-highlights",
          },
          {
            id: "background-highlights",
            title: "1) Background Specular Highlights (behind the subject)",
            darkBackground: true,
            content: `This is the magical bokeh — warm light balls, glittering dots, fairy-tale atmosphere.`,
            contentInGrid: true,
            galleryImages: [
              {
                src: "/training/specular-highlights/Screenshot_2025-12-03_at_02.34.24.png",
                alt: "Background specular highlights example",
                span: 3
              }
            ],
            calloutAfterImages: {
              icon: "📍",
              title: "Created when:",
              textLines: [
                "• small bright light sources are **behind** the subject",
                "• aperture is wide open (f/1.2–f/2.8)",
                "• the background is far away"
              ],
              backgroundColor: "#4a4a4a"
            },
            additionalCallout: {
              icon: "📸",
              title: "What it gives:",
              textLines: [
                "• cinematic, dreamy look",
                "• depth and separation",
                "• romantic atmosphere"
              ],
              backgroundColor: "#4a4a4a"
            },
            thirdCallout: {
              icon: "✅",
              iconColor: "#22c55e",
              title: "Pro Tip:",
              textLines: [
                "Move the lights FAR behind the subject — the farther they are, the softer and rounder the bokeh."
              ],
              borderOnly: true,
              backgroundColor: "#4a4a4a"
            },
            transitionTo: "catchlights",
          },
          {
            id: "catchlights",
            title: "2) Specular Highlights in the Eyes (catchlights) — in front of the subject",
            darkBackground: true,
            content: `You are absolutely right: **catchlights require a front-point light source**.

Background lights alone will never light up the eyes.`,
            galleryImages: [
              {
                src: "/training/specular-highlights/IMG_2342.jpg",
                alt: "Catchlight example 1",
                span: 3
              },
              {
                src: "/training/specular-highlights/photo_2025-12-05_12.45.13.jpeg",
                alt: "Catchlight example 2",
                span: 3
              }
            ],
            calloutAfterImages: {
              icon: "📍",
              title: "Created when:",
              textLines: [
                "• a small light source is **in front of** the subject",
                "• positioned high and angled (30–60°)",
                "• eyes face the light"
              ],
              backgroundColor: "#4a4a4a",
              borderOnly: true
            },
            additionalCallout: {
              icon: "📸",
              title: "What it gives:",
              textLines: [
                "• life and dimension",
                "• emotional engagement",
                "• polished, professional look"
              ],
              backgroundColor: "#4a4a4a",
              borderOnly: true
            },
            thirdCallout: {
              icon: "✅",
              iconColor: "#22c55e",
              title: "Practice:",
              textLines: [
                "• phone flashlight + tissue (makes a tiny softbox)",
                "• small LED tube",
                "• gaffer-taped LED puck",
                "• classic **reflector** bouncing sunlight toward the face"
              ],
              backgroundColor: "#4a4a4a",
              borderOnly: true
            },
            transitionTo: "get-both",
          },
          {
            id: "get-both",
            title: "How to Get Both: Sparkling Background + Glowing Eyes",
            darkBackground: true,
            content: `**1. Behind** → fairy lights, lamps, sun through trees

**2. In front** → small diffused light

**Result:** cinema-level beauty.`,
            transitionTo: "diagram",
          },
          {
            id: "diagram",
            title: "Quick Diagram",
            darkBackground: true,
            callout: {
              icon: "⚙️",
              preformattedText: `   Background lights (fairy lights, bulbs)
o   o   o   o   o
        ↓
    [Subject]
        ↑
  [Catchlight source] (soft light, for example, a softbox or a window)
        ↑
      Camera`,
              color: "#4a4a4a"
            },
            transitionTo: "pro-trick",
          },
          {
            id: "pro-trick",
            title: "Wedding Pro Trick",
            darkBackground: true,
            callout: {
              icon: "✅",
              title: "Wedding Pro Trick",
              textLines: [
                "During the first dance:",
                "",
                "• ask your assistant to hold a tiny LED at 45° in front",
                "• collect all background light sources into the frame",
                "• shoot at f/1.4–f/2",
                "",
                "Boom — Vogue-level drama + romance."
              ],
              color: "#4a4a4a",
              borderOnly: true
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "special-scenes",
        title: "SPECIAL SCENES — Low-Light, Sparklers, Candles, Rain & Night Portraits",
        estimatedMinutes: 12,
        hook: "How to create cinematic magic when light disappears",
        sections: [
          {
            id: "low-light",
            title: "1. Low-Light Scenes — When Light Fades, Mood Begins",
            darkBackground: true,
            content: `Low light isn't a problem.

Low light is **atmosphere**, **intimacy**, **cinema**.

### What truly matters:

• stable shutter (min 1/250 for people)
• one clear light direction
• intentional mood
• clean background

### Recommended settings:

**Manual mode always**

• ISO 800–1600
• Shutter 1/250–1/500
• Aperture wide open (f/1.4–f/2.2)`,
            callout: {
              icon: "🔥",
              title: "Pro secret:",
              textLines: [
                "**Let darkness be part of the story.**",
                "Don't brighten everything — let the shadows breathe."
              ],
              color: "#5c3d3d"
            },
            galleryImages: [
              {
                src: "/training/special-scenes/Screenshot_2025-11-25_at_20.40.59.png",
                alt: "Low light scene example 1",
                span: 3
              },
              {
                src: "/training/special-scenes/Screenshot_2025-11-25_at_20.44.06.png",
                alt: "Low light scene example 2",
                span: 3
              }
            ],
            transitionTo: "sparklers",
          },
          {
            id: "sparklers",
            title: "2. Sparkler Exits — controlled chaos",
            darkBackground: true,
            contentAboveGrid: `Sparkler exits look "simple," but every amateur gets them wrong.

**Why?**`,
            content: `Because sparklers = bright + point light + fast movement.

### Most reliable setup:

**Manual**

• ISO 2000–3200
• Shutter 1/250
• Aperture f/2.8–f/6.3`,
            contentInGrid: true,
            imagesLeftInGrid: true,
            galleryImages: [
              {
                src: "/training/special-scenes/Screenshot_2025-12-03_at_00.39.19.png",
                alt: "Sparkler exit example 1",
                span: 3,
                stackWith: { src: "/training/special-scenes/1000106763.jpg", alt: "Sparkler exit example 2", aspectRatio: "2/3" }
              }
            ],
            contentAfterImages: `### Two ways to shoot:

**A) No Flash → cinematic glow**

Beautiful atmosphere, soft faces.

But only if sparklers are MANY.

**B) With Flash → editorial clarity**

Perfect for capturing motion + emotion sharply.

On-camera flash is fine here.

### Pose tip:

Tell guests to hold sparklers **up and out**, not in front of faces.

Otherwise the couple disappears in blinding sparks.`,
            transitionTo: "candlelight",
          },
          {
            id: "candlelight",
            title: "3. Candlelight — the most flattering light ever",
            darkBackground: true,
            content: `*Candles are magical but tricky.*

**Candlelight qualities:**

• warm
• directional
• fast falloff
• flattering to skin
• creates deep atmosphere

**Best settings:**

**Manual**

• ISO 800–2000
• Shutter 1/250
• Aperture f/1.6–f/2
• White balance: 2800K–3200K

**Posing tip:**

Keep faces close together.

Candlelight covers only small areas — use proximity.`,
            callout: {
              icon: "✨",
              title: "Secret:",
              textLines: [
                "Candles = **free cinematic practicals**",
                "",
                "Use them as:",
                "• keylight (close to face)",
                "• rim light",
                "• background bokeh"
              ],
              color: "#4a4a4a",
              inGrid: true,
              borderOnly: true
            },
            galleryImages: [
              {
                src: "/training/special-scenes/1000106766.jpg",
                alt: "Candlelight portrait example",
                span: 3,
                colStart: 4
              }
            ],
            transitionTo: "rain",
          },
          {
            id: "rain",
            title: "4. Rain Portraits — the underrated dream",
            darkBackground: true,
            content: `Rain = texture + glow + reflections.

Most photographers fear it.

Professionals celebrate it.

**Required:**

• a clear umbrella
• one light source (streetlamp or flash)
• backlight for raindrops
• dark background for contrast

**Settings:**

**Manual**

• ISO 100–800
• Shutter 1/250–1/500
• Aperture f/1.8–f/2.2

**Tips:**

• Ask the couple to freeze for 1–2 seconds
• Look for puddles (free mirrors!)
• Use car lights as backlight`,
            galleryImages: [
              {
                src: "/training/special-scenes/1000106769.jpg",
                alt: "Rain portrait example 1",
                span: 3,
                colStart: 4,
                stackWith: { src: "/training/special-scenes/1000106721.jpg", alt: "Rain portrait example 2" }
              }
            ],
            transitionTo: "night",
          },
          {
            id: "night",
            title: "5. Night Portraits — when you paint with darkness",
            darkBackground: true,
            contentAboveGrid: `*Night photography is where you become an artist, not a technician.*

**Three keys:**

1. one strong light direction
2. contrast between subject & background
3. intentional negative space`,
            content: `**Light sources you can use:**

• café signs
• neon
• streetlights
• phone screens
• headlights
• sparkler leftovers`,
            contentInGrid: true,
            galleryImages: [
              {
                src: "/training/special-scenes/1000106531.jpg",
                alt: "Night portrait example",
                span: 3,
                colStart: 4
              }
            ],
            contentAfterImages: `**Settings:**

**Manual**

• ISO 1250–1600
• Shutter 1/250
• Aperture f/2–f/5.6`,
            calloutAfterImages: {
              icon: "🎬",
              title: "Secret technique:",
              textLines: [
                "**Hide the light source just outside the frame.**",
                "It creates drama + cinematic edge lighting."
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "film-analog",
        title: "FILM: ANALOG LOOK — GRAIN, TONES, DYNAMIC RANGE",
        estimatedMinutes: 10,
        hook: "Film photography isn't just nostalgia — it's a mood, a texture, a rhythm.",
        sections: [
          {
            id: "what-makes-film",
            title: "1. What makes film *film*",
            darkBackground: true,
            content: `Film photography isn't just nostalgia — it's a mood, a texture, a rhythm.

Unlike digital sensors that interpret light in pixels, **film captures light chemically**, creating unique tones and imperfections that can't be truly replicated.

Every roll of film — Kodak Portra, Ilford HP5, Fuji Superia — has its own soul.

• **Kodak Portra** loves skin tones and warmth
• **Ilford HP5** celebrates contrast and grit
• **Fuji** sings in greens and blues`,
            callout: {
              icon: "🎥",
              textLines: [
                "*Film \"forces you to be present\" — every shot counts.*"
              ],
              color: "#4a4a4a",
              inGrid: true
            },
            galleryImages: [
              {
                src: "/training/film-analog/1000106777.jpg",
                alt: "Film photography example",
                span: 3,
                colStart: 4
              }
            ],
            transitionTo: "grain",
          },
          {
            id: "grain",
            title: "2. Grain – not noise, but character",
            darkBackground: true,
            content: `Film grain isn't a flaw — **it's personality.**

Digital *noise* is random and artificial, while *grain* comes from silver halide crystals reacting to light.

• **Fine-grain films** (like Kodak Ektar 100) → sharp, clean portraits
• **Coarse-grain films** (like CineStill 800T or pushed Tri-X) → cinematic and raw`,
            callout: {
              icon: "💡",
              title: "Tip:",
              textLines: [
                "You can add digital grain in post (via Lightroom or Dehancer), but balance it — too much looks fake."
              ],
              color: "#5D4037"
            },
            transitionTo: "tones",
          },
          {
            id: "tones",
            title: "3. Tones – the language of feeling",
            darkBackground: true,
            content: `Film renders highlights and shadows with gentle transitions — that **"creamy" dynamic curve.**

Digital sensors, especially cheaper ones, tend to *clip* highlights and crush shadows.

Film, on the other hand, gives you:

• **Soft highlights** (white never truly white, but tinted with warmth)
• **Rich midtones** (skin looks alive)
• **Velvety shadows** (not pure black, but deep charcoal)`,
            callout: {
              icon: "📷",
              title: "Example:",
              textLines: [
                "Gregory Crewdson uses large-format film for cinematic control of tone — every color speaks softly, yet precisely."
              ],
              color: "#4a4a4a"
            },
            transitionTo: "dynamic-range",
          },
          {
            id: "dynamic-range",
            title: "4. Dynamic Range – forgiveness in light",
            darkBackground: true,
            content: `Film has an organic way of handling exposure errors.

You can **overexpose Portra by two stops** and it will still glow.

Try that with a digital sensor — and you'll burn details instantly.

That's why many modern photographers — even with digital cameras like **Leica M11**, **Sony A7R IV**, or **Canon R5** — use *film emulations* or LUTs inspired by analog stocks.`,
            callout: {
              icon: "📚",
              title: "Reference:",
              textLines: [
                "\"The Film Photography Handbook\" by Chris Marquardt beautifully explains how analog dynamic range works compared to digital RAW."
              ],
              color: "#4a4a4a"
            },
            transitionTo: "modern-blend",
          },
          {
            id: "modern-blend",
            title: "5. The modern blend: Digital meets film",
            darkBackground: true,
            content: `**Hybrid workflows are rising:**

Shoot digital, grade with film LUTs, and add analog-style imperfections.

Or shoot real film and scan it — mixing both worlds.

You don't have to choose sides — **film teaches *patience*, digital gives *freedom*.**

Together they make your eye more intentional.`,
            callout: {
              icon: "✨",
              title: "Tiny secret tip",
              textLines: [
                "A tiny layer of **Vaseline** on a UV filter can soften the image and mimic dreamy analog diffusion — but only try this for creative portraits, *not before a wedding shoot without permission!*"
              ],
              color: "#5D4037"
            },
            galleryImages: [
              {
                src: "/training/film-analog/IMG_7732.jpg",
                alt: "Film analog example 1",
                span: 3
              },
              {
                src: "/training/film-analog/IMG_7761.jpg",
                alt: "Film analog example 2",
                span: 3
              }
            ],
            transitionTo: null,
          },
        ],
      },
      {
        id: "trends-modern",
        title: "TRENDS & MODERN APPROACHES — Contemporary Storytelling",
        estimatedMinutes: 12,
        hook: "We don't take pictures anymore. We create experiences.",
        sections: [
          {
            id: "introduction",
            title: "Introduction",
            darkBackground: true,
            content: `Wedding photography has transformed from posed perfection into **authentic artistry**. Today's visual language values *imperfection, atmosphere, and truth.* The best photographers know how to mix timeless composition with modern experimentation — creating images that feel like memories, not staged portraits.`,
            transitionTo: "tilted-framing",
          },
          {
            id: "tilted-framing",
            title: "1. Tilted & Angled Framing — modern energy",
            darkBackground: true,
            content: `A slightly tilted horizon brings movement and emotion into stillness. It's not a "mistake" — it's rhythm.

**How to use it:**

• Tilt intentionally during dynamic scenes: dancing, confetti, crowd reactions.
• Combine with wide lenses (24–35mm) to amplify space and energy.
• Keep the horizon subtle — 5–15° is enough to feel natural yet vibrant.`,
            callout: {
              icon: "✨",
              title: "Pro inspiration:",
              textLines: [
                "*Benj Haisch* and *India Earl* use gentle tilts to turn chaos into feeling. Their frames feel like fragments of real life — *imperfectly human.*"
              ],
              color: "#4a4a4a"
            },
            galleryImages: [
              {
                src: "/training/trends-modern/photo_2025-11-25_02.24.20.jpeg",
                alt: "Tilted framing example 1"
              },
              {
                src: "/training/trends-modern/IMG_1891.jpg",
                alt: "Tilted framing example 2"
              },
              {
                src: "/training/trends-modern/IMG_2553.jpg",
                alt: "Tilted framing example 3"
              },
              {
                src: "/training/trends-modern/IMG_2534.jpg",
                alt: "Tilted framing example 4"
              },
              {
                src: "/training/trends-modern/IMG_2342.jpg",
                alt: "Tilted framing example 5"
              },
              {
                src: "/training/trends-modern/IMG_2634.jpg",
                alt: "Tilted framing example 6"
              }
            ],
            transitionTo: "asymmetry",
          },
          {
            id: "asymmetry",
            title: "2. Asymmetry & Negative Space — emotional breathing room",
            darkBackground: true,
            content: `Symmetry feels calm. Asymmetry feels *real.*

Leaving open space in your composition — sky, wall, shadow — makes the subject's presence stronger. It also gives emotional pause, like silence in a song.

**Use it when:**

• capturing anticipation, loneliness, or intimacy;
• photographing one person in a vast environment.

**Example:**
A bride on a staircase, with ⅔ of the frame empty — it's not absence; it's storytelling.`,
            callout: {
              icon: "💭",
              textLines: [
                "\"Space is emotion made visible.\"",
                "— *André Kertész*"
              ],
              color: "#5D4037"
            },
            transitionTo: "editorial-documentary",
          },
          {
            id: "editorial-documentary",
            title: "3. Editorial & Documentary Fusion",
            darkBackground: true,
            content: `The modern wedding photographer is both an **observer and an art director.**

The style combines:

• *documentary honesty* (truth, chaos, spontaneity)
• *editorial aesthetics* (composition, light, detail).

**How to balance it:**

Capture the real — then refine it through framing, light, and timing. You're not just documenting what happened — you're showing *how it felt.*`,
            callout: {
              icon: "🎬",
              title: "References:",
              textLines: [
                "– *Greg Williams* (energy and elegance in motion)",
                "– *Jose Villa* (film tones and minimalism)",
                "– *Nirav Patel* (emotive cinematic light)"
              ],
              color: "#4a4a4a"
            },
            transitionTo: "motion-blur",
          },
          {
            id: "motion-blur",
            title: "4. Motion Blur & Imperfection",
            darkBackground: true,
            content: `Blurry hands, half-smiles, unposed reactions — they're not technical flaws; they're emotional truths.

**How to use it:**

• Lower shutter to **1/30–1/60 sec** while keeping ISO low.
• Track the movement slightly — let emotion streak across the frame.
• Focus on atmosphere, not sharpness.

This kind of blur feels like memory — *something seen, then gone.*`,
            transitionTo: "flash-aesthetic",
          },
          {
            id: "flash-aesthetic",
            title: "5. Flash Aesthetic — retro yet timeless",
            darkBackground: true,
            content: `Direct flash is back — but not as paparazzi chaos, rather as bold storytelling. It adds **drama, texture, and a sense of "now."**

**Use it for:**

• evening parties and dance floors;
• champagne pops;
• stylish group moments.`,
            galleryImages: [
              {
                src: "/training/trends-modern/Screenshot_2025-12-03_at_22.38.30.png",
                alt: "Flash aesthetic example 1",
                span: 3,
                calloutBelow: {
                  icon: "⚡",
                  title: "Pro Tip:",
                  textLines: [
                    "Balance with ambient: use manual mode, 1/250–1/500 sec, ISO 400–800, bounce when possible. Experiment — sometimes straight flash is the art itself."
                  ],
                  color: "#4a4a4a"
                }
              },
              {
                src: "/training/trends-modern/aefb0526-08f5-4a0e-ac7c-6cabe3188f67.png",
                alt: "Flash aesthetic example 2",
                span: 3
              }
            ],
            transitionTo: "color-storytelling",
          },
          {
            id: "color-storytelling",
            title: "6. Color Storytelling — cinematic and emotional",
            darkBackground: true,
            content: `Color now defines mood more than sharpness ever did.

Warm golds for nostalgia, greens for calm, blue tones for solitude, pinks for intimacy.

**Try this:**

• Adjust white balance intentionally — not for accuracy, but for emotion.
• Build a consistent color language across a wedding gallery — it's what makes your signature recognizable.`,
            callout: {
              icon: "🎨",
              textLines: [
                "\"Color is a power which directly influences the soul.\"",
                "— *Wassily Kandinsky*"
              ],
              color: "#5D4037"
            },
            transitionTo: "realness",
          },
          {
            id: "realness",
            title: "7. Realness over Perfection",
            darkBackground: true,
            content: `The strongest trend isn't visual — it's emotional.

Raw moments, imperfect light, genuine laughter. A wrinkle in the dress, a tear, a gust of wind — these are what clients remember.

Forget "picture-perfect." Aim for **emotion-perfect.**`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "resources",
        title: "RESOURCES",
        displayType: "page",
        sections: [
          {
            id: "films-intro",
            titleBelowImage: "🎬 Films to Train a Photographer's Eye",
            darkBackground: true,
            galleryImages: [
              {
                src: "/training/resources/18.11.2025_17.01.19_734_260.jpg",
                alt: "Films hero image",
                span: 6
              }
            ],
            transitionTo: "film-gone-with-wind",
          },
          {
            id: "film-gone-with-wind",
            title: "Gone with the Wind (1939)",
            darkBackground: true,
            content: `Striking silhouettes, dramatic light traps, beautifully shaped shadows. A perfect study in how light can create emotion, not just exposure.

*Silhouettes, light traps, sensual shots.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.52.48.jpeg",
                alt: "Gone with the Wind"
              }
            ],
            transitionTo: "film-pomegranates",
          },
          {
            id: "film-pomegranates",
            title: "The Color of Pomegranates (1969)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.52.51.jpeg",
            imageAboveContentAlt: "The Color of Pomegranates",
            content: `Pure visual poetry. Composition as ritual, color as symbol, every frame as a standalone artwork. Teaches you to see structure deeper than "pretty".

*Composition, color, balance, visual narrative.*`,
            transitionTo: "film-godfather",
          },
          {
            id: "film-godfather",
            title: "The Godfather (1972)",
            darkBackground: true,
            content: `A masterpiece of chiaroscuro. Deep shadows, sculpted faces, legendary gaffer work. Proof that darkness can speak louder than light.

*Shadowplay, drama, iconic lighting.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.52.54.jpeg",
                alt: "The Godfather"
              }
            ],
            transitionTo: "film-pulp-fiction",
          },
          {
            id: "film-pulp-fiction",
            title: "Pulp Fiction (1994)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.52.56.jpeg",
            imageAboveContentAlt: "Pulp Fiction",
            content: `Tarantino's signature style: bold color, vivid saturation, confident angles, visual rhythm. A masterclass in energy and character.

*Color, saturation, angles.*`,
            transitionTo: "film-english-patient",
          },
          {
            id: "film-english-patient",
            title: "The English Patient (1996)",
            darkBackground: true,
            content: `Cinema you want to photograph. Gentle transitions, warm tones, lyrical lighting. Inspires emotional, soft, intimate portraiture.

*Cinematic tone, warmth, light.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.52.58.jpeg",
                alt: "The English Patient"
              }
            ],
            transitionTo: "film-amelie",
          },
          {
            id: "film-amelie",
            title: "Amélie (2001)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.00.jpeg",
            imageAboveContentAlt: "Amélie",
            content: `Playful control of color, charming angles, soft magical compositions. Teaches how to guide a viewer's gaze with intention.

*Angles, viewer guidance, composition.*`,
            transitionTo: "film-atonement",
          },
          {
            id: "film-atonement",
            title: "Atonement (2007)",
            darkBackground: true,
            content: `Subtle, poetic light: soft highlights, delicate details, atmospheric presence. A beautiful study of cinematic mood in ordinary spaces.

*Highlights, details, atmosphere.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.53.03.jpeg",
                alt: "Atonement"
              }
            ],
            transitionTo: "film-tree-of-life",
          },
          {
            id: "film-tree-of-life",
            title: "The Tree of Life (2011)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.05.jpeg",
            imageAboveContentAlt: "The Tree of Life",
            content: `Natural light in its purest form: backlight, shadows, free-moving camera. A film that teaches you to see light rather than search for it.

*Natural light, backlight, composition, angles.*`,
            transitionTo: "film-artist",
          },
          {
            id: "film-artist",
            title: "The Artist (2011)",
            darkBackground: true,
            content: `A film without color or dialogue that still communicates emotion loud and clear. Lessons in expression, gesture, and framing feelings.

*Emotion, framing, expressiveness.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.53.07.jpeg",
                alt: "The Artist"
              }
            ],
            transitionTo: "film-moonrise",
          },
          {
            id: "film-moonrise",
            title: "Moonrise Kingdom (2012)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.09.jpeg",
            imageAboveContentAlt: "Moonrise Kingdom",
            content: `A charming symphony of symmetry and decorative visuals. Each frame is a poster. Teaches tonal precision and visual intention.

*Symmetry, decorativeness, composition.*`,
            transitionTo: "film-budapest",
          },
          {
            id: "film-budapest",
            title: "The Grand Budapest Hotel (2014)",
            darkBackground: true,
            content: `Pure compositional architecture — symmetry, rhythm, group staging, spatial balance. A must-watch for learning order and visual harmony.

*Group composition, symmetry, structure.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.53.11.jpeg",
                alt: "The Grand Budapest Hotel"
              }
            ],
            transitionTo: "film-dressmaker",
          },
          {
            id: "film-dressmaker",
            title: "The Dressmaker (2015)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.13.jpeg",
            imageAboveContentAlt: "The Dressmaker",
            content: `Dramatic shadows, fashion-like angles, strong visual character. A lesson in stylizing real people without losing authenticity.

*Composition, angles, shadows.*`,
            transitionTo: "film-cafe-society",
          },
          {
            id: "film-cafe-society",
            title: "Café Society (2016)",
            darkBackground: true,
            content: `Gorgeous backlight, subtle shadows, soft glamour. A guide to portrait-making through light and color rather than posing.

*Backlight, shadows, composition.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.53.15.jpeg",
                alt: "Café Society"
              }
            ],
            transitionTo: "film-la-la-land",
          },
          {
            id: "film-la-la-land",
            title: "La La Land (2016)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.17.jpeg",
            imageAboveContentAlt: "La La Land",
            content: `A love letter to color and romantic light. Framing, neon nights, emotional contrasts — brilliant for studying cinematic rhythm.

*Framing, color, light, romance.*`,
            transitionTo: "film-orient-express",
          },
          {
            id: "film-orient-express",
            title: "Murder on the Orient Express (2017)",
            darkBackground: true,
            content: `Elegant cinematography and refined visual structure. Teaches how to use geometry and atmosphere to build tension and beauty.

*Elegance, composition, light.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.53.19.jpeg",
                alt: "Murder on the Orient Express"
              }
            ],
            transitionTo: "film-knives-out",
          },
          {
            id: "film-knives-out",
            title: "Knives Out (2019)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.22.jpeg",
            imageAboveContentAlt: "Knives Out",
            content: `Complex group setups, multilayered blocking, expressive shadows. Excellent reference for wedding photographers handling group scenes.

*Multilayering, group setups, shadows.*`,
            transitionTo: "film-amsterdam",
          },
          {
            id: "film-amsterdam",
            title: "Amsterdam (2022)",
            darkBackground: true,
            content: `A visual playground. Unusual angles, bold light hooks, striking compositions — expands your idea of what's "allowed" in a frame.

*Bold angles, tonal attitude, light hooks.*`,
            galleryImages: [
              {
                src: "/training/resources/photo_2025-11-18_22.53.24.jpeg",
                alt: "Amsterdam"
              }
            ],
            transitionTo: "film-see-how-they-run",
          },
          {
            id: "film-see-how-they-run",
            title: "See How They Run (2022)",
            darkBackground: true,
            imageAboveContent: "/training/resources/photo_2025-11-18_22.53.26.jpeg",
            imageAboveContentAlt: "See How They Run",
            content: `Perfectly balanced group frames, clean composition, confident use of light accents.

*Group balance, light accents, composition.*`,
            transitionTo: "books",
          },
          {
            id: "books",
            title: "📚 Books to Train Your Photographer's Eye",
            darkBackground: true,
            content: `Johannes Itten — **"The Art of Color"**
A foundational study of color theory, contrast, harmony, emotional perception, and how color shapes visual storytelling.

Sophie Howarth & Stephen McLaren — **"Street Photography Now"**
A bold contemporary survey of street photography: timing, intuition, raw storytelling, and the art of noticing.

Borys Krupnyk — **"Light and Composition"** ~*not yet available in English~
A practical and artistic guide to expressive lighting and clean, intentional composition.

Henry Carroll — **"Read This if You Want to Take Great Photographs"**
A simple, modern, highly visual introduction to the core principles of photography — perfect for beginners and intermediates.

Henry Carroll — **"Read This if You Want to Take Great Portraits"**
A friendly, clear guide to portrait photography: emotion, connection, angle, expression, light.

Mary Warner Marien — **"100 Ideas That Changed Photography"**
A historical and conceptual guide to the inventions, ideas, and images that shaped photography.

Charlotte Cotton — **"The Photograph as Contemporary Art"**
A key book for understanding modern photography beyond technique — concepts, narratives, authorship.

Henry Carroll — **"Photographers on Photography: How They See, Think, and Work"**
Short insights from iconic photographers about their process and artistic thinking.

Jackie Higgins — **"Why It Does Not Have to Be in Focus"**
A very accessible explanation of conceptual photography — encourages seeing beyond technical sharpness.

Wally Koval — **"Accidentally Wes Anderson"**
Beautifully curated imagery inspired by Wes Anderson's visual world: symmetry, color palettes, geometry, whimsy.

Steven D. Katz — **"Film Directing: Shot by Shot — Visualizing from Concept to Screen"**
One of the most influential books on visual planning, storyboarding, framing, and directing the viewer's eye.

Gustavo Mercado — **"The Filmmaker's Eye: Learning (and Mastering) the Language of Visual Storytelling"**
A powerhouse book about composition, framing, lens choice, and the psychology of the image.

Charlie Pickard, Jameela Knopf, and others — **"Color and Light (From the Masters of Art)"**
A rich exploration of how artists throughout history used color and light — extremely useful for photographers.`,
            transitionTo: "links",
          },
          {
            id: "links",
            title: "🔗 INTERESTING LINKS",
            darkBackground: true,
            externalLinks: [
              {
                title: "List of films voted the best",
                url: "https://en.wikipedia.org/wiki/List_of_films_voted_the_best",
                image: "/training/resources/Screenshot_2025-11-19_at_00.35.17.png"
              },
              {
                title: "They Shoot Pictures, Don't They?",
                url: "https://www.theyshootpictures.com/",
                image: "/training/resources/Screenshot_2025-11-19_at_00.38.42.png"
              },
              {
                title: "AFI's 100 YEARS...100 MOVIES",
                url: "https://www.afi.com/afis-100-years-100-movies/",
                image: "/training/resources/Screenshot_2025-11-19_at_00.40.49.png"
              },
              {
                title: "Orson Welles - Interview with Michael Parkinson",
                url: "https://youtu.be/6dAGcorF1Vo?si=TtsnGTuvHt1sP3GC",
                image: "/training/resources/Screenshot_2025-11-19_at_00.46.28.png"
              },
              {
                title: "The 100 Greatest Movies of All Time",
                url: "https://variety.com/lists/best-movies-of-all-time/",
                image: "/training/resources/Screenshot_2025-11-19_at_00.37.03.png"
              },
              {
                title: "The Greatest Films of All Time",
                url: "https://www.bfi.org.uk/sight-and-sound/greatest-films-all-time",
                image: "/training/resources/Screenshot_2025-11-19_at_00.40.02.png"
              },
              {
                title: "Martin Scorsese interview on Stanley Kubrick",
                url: "https://youtu.be/vYmMIA1hWDE?si=8LaglglvHRvr8uGT",
                image: "/training/resources/Screenshot_2025-11-19_at_00.42.23.png"
              },
              {
                title: "Quentin Tarantino: The Inspiration For Pulp Fiction",
                url: "https://youtu.be/t5KkI_YS4ug?si=ioFP7eQ8oYvrvCOT",
                image: "/training/resources/Screenshot_2025-11-19_at_00.44.56.png"
              },
              {
                title: "Interview with Jose Villa",
                url: "https://blog.pixieset.com/blog/jose-villa-interview/",
                image: "/training/resources/Screenshot_2025-12-04_at_00.35.52.png"
              },
              {
                title: "Breaking The Wedding Photography Mold w/ Fer Juaristi",
                url: "https://share.google/pFpzLmIsoj8nKwE6R",
                image: "/training/resources/6f18047b-b74e-486d-bd0a-51cf38c668b6.png"
              },
              {
                title: "PhotoPills – calculations of light, sun, moon, exposure",
                url: "https://share.google/NC5deIG48KuKdS4Zd",
                image: "/training/resources/eedb5bda-4373-4f25-a338-6d3620845785.png"
              }
            ],
            transitionTo: "photographers",
          },
          {
            id: "photographers",
            title: "📸 PHOTOGRAPHERS FOR YOUR INSPIRATION",
            darkBackground: true,
            content: `### Wedding photographers

• [Cecilia + Nieves + Aida](https://www.instagram.com/diasdevinoyrosas?igsh=MzRlODBiNWFlZA==)
• [Monika Frias](https://www.instagram.com/latxina?igsh=MzRlODBiNWFlZA==)
• [ANA HINOJOSA](https://www.instagram.com/ah_lovestories?igsh=MzRlODBiNWFlZA==)
• [Pablo Laguia](https://www.instagram.com/pablo_laguia?igsh=MzRlODBiNWFlZA==)
• [Dylan](https://www.instagram.com/thekitcheners?igsh=MzRlODBiNWFlZA==)
• [Vmpineda](https://www.instagram.com/vmpineda.photo?igsh=MzRlODBiNWFlZA==)
• [Marcus Jolly](https://www.instagram.com/marcusjollyweddings?igsh=MzRlODBiNWFlZA==)
• [Gaby Bolívar](https://www.instagram.com/gabybolivarweddings?igsh=MzRlODBiNWFlZA==)
• [CONCORDIA](https://www.instagram.com/concordia.photo?igsh=MzRlODBiNWFlZA==)
• [Los Ébano](https://www.instagram.com/losebano?igsh=MzRlODBiNWFlZA==)
• [Nina and Darek](https://www.instagram.com/ninaanddarek?igsh=MzRlODBiNWFlZA==)
• [Dima Taranenko](https://www.instagram.com/taranenkopro?igsh=MzRlODBiNWFlZA==)

### General photographers

• [Greg Williams](https://www.instagram.com/gregwilliamsphotography?igsh=MzRlODBiNWFlZA==)
• [Jean Black](https://www.instagram.com/kevertai?igsh=MzRlODBiNWFlZA==)
• [Simone Christine Frank](https://www.instagram.com/simone_frank?igsh=MzRlODBiNWFlZA==)
• [Svenja Ava Mueller](https://www.instagram.com/svenja.ava?igsh=MzRlODBiNWFlZA==)
• [Lilia Kunytska](https://www.instagram.com/photo.destruction?igsh=MzRlODBiNWFlZA==)
• [Dave Benett Photographer](https://www.instagram.com/davebenett?igsh=MzRlODBiNWFlZA==)
• [A collective of photographers](https://www.instagram.com/magnumphotos?igsh=MzRlODBiNWFlZA==)
• [Ragib Choudhury](https://www.instagram.com/iragib?igsh=MzRlODBiNWFlZA==)`,
            galleryImages: [
              {
                src: "/training/resources/20.11.2025_00.42.43_670_27.jpg",
                alt: "Photography inspiration 1"
              }
            ],
            closingImage: "/training/resources/18.11.2025_17.12.02_363_324.jpg",
            transitionTo: null,
          },
        ],
      },
    ],
    quiz: {
      title: "CREATIVE QUIZ",
      description: "Test your knowledge of storytelling, composition, posing, and the artistic choices that shape powerful wedding imagery.",
      questions: [
        {
          id: 1,
          question: "What makes an image feel \"layered\" and visually rich?",
          options: ["Using only a wide aperture", "Foreground, subject, and background working together", "Placing the subject directly against a wall", "Avoiding any elements in the foreground"],
          correct: 1
        },
        {
          id: 2,
          question: "Which posing approach creates the most natural-looking couple portraits?",
          options: ["Telling them to hold still and smile", "Asking them to stare into the camera for every shot", "Giving small, real tasks (walk, lean in, breathe out)", "Standing them stiffly side-by-side"],
          correct: 2
        },
        {
          id: 3,
          question: "What is the main benefit of side light in storytelling?",
          options: ["It removes all shadows", "It flattens the subject", "It makes colors less accurate", "It adds dimension and reveals texture"],
          correct: 3
        },
        {
          id: 4,
          question: "What's the biggest mistake when posing a group?",
          options: ["Creating natural height differences", "Keeping people angled slightly to the camera", "Putting everyone on the same plane in a straight line", "Encouraging them to interact"],
          correct: 2
        },
        {
          id: 5,
          question: "Which of these best describes a \"dynamic angle\"?",
          options: ["Shooting everything at eye level", "Slight tilt or height change to add movement", "Cropping out all surroundings", "Shooting only from low angles"],
          correct: 1
        },
        {
          id: 6,
          question: "What helps eliminate the \"stiff hands problem\" in posing?",
          options: ["Ask people to hide their hands behind their backs", "Let them hold an object or touch their partner naturally", "Make them put hands down straight at the sides", "Never show hands in portraits"],
          correct: 1
        },
        {
          id: 7,
          question: "What gives cinematic depth during a ceremony?",
          options: ["Zooming all the way out", "Photographing only from the center aisle", "Using only direct flash", "Shooting through foreground elements (guests, florals)"],
          correct: 3
        },
        {
          id: 8,
          question: "A couple looks disconnected in the frame. What fixes it fastest?",
          options: ["Telling them to smile wider", "Asking one of them to slightly shift weight toward the other", "Making them stand farther apart", "Turning both fully toward the camera"],
          correct: 1
        },
        {
          id: 9,
          question: "What defines a \"hero angle\" in wedding photography?",
          options: ["Shooting from a low angle to elongate the subject", "Shooting from above to make them look smaller", "Cropping out the environment", "Using only 35mm lenses"],
          correct: 0
        },
        {
          id: 10,
          question: "When should you use symmetrical composition?",
          options: ["When you want calm, stable, elegant storytelling", "When the environment is messy", "Only for landscapes", "Never — symmetry is outdated"],
          correct: 0
        },
        {
          id: 11,
          question: "What is the fastest way to make a groom look more confident in portraits?",
          options: ["Chin up as high as possible", "Shoulders slightly back, weight on the back foot", "Hands tightly pressed to sides", "Asking him not to move"],
          correct: 1
        },
        {
          id: 12,
          question: "Which scenario benefits most from backlight?",
          options: ["Harsh midday sun", "Group posing indoors", "Romantic couple photos with glowing edges", "When shooting only details"],
          correct: 2
        },
        {
          id: 13,
          question: "What creates natural interaction in a bridal party?",
          options: ["Very specific, complex posing instructions", "A simple prompt: \"Walk together and talk\"", "Making everyone look at the camera", "Telling them not to move"],
          correct: 1
        },
        {
          id: 14,
          question: "What is a key element of modern wedding storytelling?",
          options: ["Every photo perfectly centered", "Zero motion blur under any circumstances", "Imperfect candid moments with real movement", "Only posed portraits"],
          correct: 2
        },
        {
          id: 15,
          question: "Why use environmental framing (doors, florals, architecture)?",
          options: ["To hide unwanted subjects", "To draw attention to the couple and add depth", "To avoid shooting wide angles", "To make editing easier"],
          correct: 1
        },
        {
          id: 16,
          question: "Which lens behavior creates the most flattering close portrait?",
          options: ["Shooting at 16mm very close", "85mm or 50mm with a bit of distance", "Standing extremely close with a wide angle", "Using a fisheye"],
          correct: 1
        },
        {
          id: 17,
          question: "To create dreamy portraits with fairy lights in the background, what should the photographer prioritize?",
          options: ["Extremely high shutter speed", "Wide aperture (f/1.4–f/2.0)", "Narrow aperture (f/11–f/16)", "ISO 3200+"],
          correct: 2
        },
        {
          id: 18,
          question: "What is the main danger of a Dutch angle (tilted frame)?",
          options: ["It becomes chaotic if overused", "It makes the subject blurry", "It ruins the exposure", "It always distorts faces"],
          correct: 0
        },
        {
          id: 19,
          question: "What helps avoid \"floating heads\" in group photos?",
          options: ["Keeping everyone in the same straight line", "Using natural height variation and staggering rows", "Shooting from too high above", "Cropping tightly around the faces"],
          correct: 1
        },
        {
          id: 20,
          question: "What makes cinematic rain portraits work?",
          options: ["Backlighting the rain so every drop becomes visible", "Shooting from far away without light", "Using only natural overhead light", "Avoiding movement completely"],
          correct: 0
        },
        {
          id: 21,
          question: "What instantly makes a detail shot (rings, bouquet) more sophisticated?",
          options: ["Symmetry and clean backgrounds", "Shooting quickly without arrangement", "Using only direct overhead light", "Placing it anywhere on the floor"],
          correct: 0
        },
        {
          id: 22,
          question: "A bride feels tense. What is the fastest posing fix?",
          options: ["Tell her to freeze", "Turn her fully toward the camera", "Ask her to inhale deeply and hold it", "Ask her to exhale slowly and drop her shoulders"],
          correct: 3
        },
        {
          id: 23,
          question: "What makes a walking shot look natural?",
          options: ["Telling them to walk extremely slowly", "Letting them talk while walking normally", "Asking them to stare at the ground", "Using only telephoto lenses"],
          correct: 1
        },
        {
          id: 24,
          question: "Why do photographers shoot \"through\" objects (veil, florals, glass)?",
          options: ["To hide incorrect exposure", "To avoid focusing", "To add blur and atmosphere", "To cover mistakes in posing"],
          correct: 2
        },
        {
          id: 25,
          question: "What gives sparkler exits that magical, energetic feel?",
          options: ["Shooting from very high above", "Backlighting + movement + candid expressions", "Using only a static pose", "Overexposing the sparklers"],
          correct: 1
        },
      ]
    },
  },
  logistic: {
    id: "logistic",
    icon: Package,
    title: "LOGISTIC",
    emoji: "📦",
    description: "How you operate — communication, planning, mindset, and seamless work on the wedding day.",
    modules: [
      {
        id: "equipment-prep",
        title: "EQUIPMENT PREPARATION — Choosing, Packing & Testing Before the Wedding Day",
        estimatedMinutes: 8,
        hook: "Your calm on the wedding day comes from what you did the night before.",
        sections: [
          {
            id: "intro",
            title: "EQUIPMENT PREPARATION — readiness is quiet confidence",
            darkBackground: true,
            quote: `"Your calm on the wedding day comes from what you did the night before."
— **Greg Williams**`,
            heroImage: {
              src: "/training/equipment-prep/8a9a609792a94a14f188cecba8cb6feb.png",
              alt: "Camera equipment preparation"
            },
            contentAfter: `A wedding is not the place to *experiment*. It's where your preparation meets emotion.

You don't need every gadget — you need **a reliable, intentional setup**.`,
            transitionTo: null,
          },
          {
            id: "core-kit",
            title: "1. Build Your Core Kit",
            darkBackground: true,
            content: `**Main camera + backup body**

If your main camera fails (and it can), you must be able to continue shooting seamlessly.

Backup doesn't need to be identical — even an older DSLR or a crop body is better than panic.`,
            callout: {
              icon: "📷",
              title: "Essential Lenses",
              textLines: [
                "**35 mm** — storytelling & context",
                "**50 mm** — intimacy & emotion",
                "**85 mm** — portraits & compression",
                "**24–70 mm** — flexibility for tight spaces",
                "**70–200 mm** — ceremonies, moments from afar"
              ],
              color: "#4a4a4a"
            },
            contentAfterCallout: `*(Choose 2–3 favorites, not all at once — versatility comes from practice, not weight.)*`,
            transitionTo: null,
          },
          {
            id: "batteries-cards",
            title: "2. Batteries & Cards — Invisible Heroes",
            darkBackground: true,
            content: `• Bring **at least 4–6 batteries** (labeled and charged)
• Use **two chargers**, ideally one dual-slot
• Format all cards *in-camera*, never on a computer
• Keep an **empty, labeled SD card case** — one side for fresh cards, one for full
• After each major part of the day (prep, ceremony, party) — **back up** to SSD`,
            callout: {
              icon: "💾",
              color: "#5c3d3d",
              textLines: [
                "*Memory cards die quietly. Redundancy is survival.*"
              ]
            },
            transitionTo: null,
          },
          {
            id: "light-gear",
            title: "3. Light Gear",
            darkBackground: true,
            content: `Even natural-light photographers need light tools for emergencies.

### Flash (on/off-camera)
• Test trigger connection the night before
• Carry a diffuser cap or mini softbox
• Don't forget AA batteries — lots of them

### Reflector / diffuser
• Small foldable 5-in-1 is a lifesaver
• Silver = strong; white = soft; gold = warm tone`,
            transitionTo: null,
          },
          {
            id: "cleaning",
            title: "4. Cleaning & Maintenance",
            darkBackground: true,
            content: `• Clean lenses and sensors (use air blower, never cloth)
• Check for dust spots by shooting a white wall at f/22
• Tighten tripod and strap screws
• Wipe viewfinder and LCD (oily fingerprints can affect visibility)`,
            galleryImages: [
              { src: "/training/equipment-prep/Screenshot_2025-12-03_at_00.45.54.png", span: 2 },
              {
                src: "/training/equipment-prep/Screenshot_2025-12-03_at_01.20.22.png",
                span: 2,
                stackWith: {
                  src: "/training/equipment-prep/Screenshot_2025-12-03_at_00.47.49.png"
                }
              },
              { src: "/training/equipment-prep/Screenshot_2025-12-03_at_00.46.06.png", span: 2 },
            ],
            calloutAfterImages: {
              icon: "⚠️",
              backgroundColor: "#5c3d3d",
              textLines: [
                "**IT'S EXTREMELY IMPORTANT TO KEEP YOUR LENS CLEAN!!!!!**"
              ]
            },
            transitionTo: null,
          },
          {
            id: "pre-shoot-test",
            title: "5. Pre-Shoot Test",
            darkBackground: true,
            content: `The evening before:

✅ Check focus on all lenses (especially third-party ones)
✅ Test flash power output and recycle time
✅ Verify camera clock time (so both cameras sync)
✅ Test all cards for write speed
✅ Review your last session — correct what you disliked`,
            callout: {
              icon: "💡",
              color: "#4a4a4a",
              textLines: [
                "*Confidence is not perfection — it's familiarity.*"
              ]
            },
            transitionTo: null,
          },
          {
            id: "unexpected-bag",
            title: `6. The "Unexpected Bag"`,
            darkBackground: true,
            content: `Include these lifesavers:

• Gaffer tape
• Mini scissors
• Clear umbrella
• Microfiber cloth
• Small power bank
• Printed timeline of the day
• USB stick with portfolio (you'll thank yourself later)
• First aid kit: Basic set of medicines, bandages, antiseptic, and first aid supplies`,
            transitionTo: null,
          },
          {
            id: "pro-tip",
            title: "7. Pro Tip",
            darkBackground: true,
            content: `Pack your gear the night before — **not the morning of the wedding**.

Morning energy should go to people.`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "mindset",
        title: "PHOTOGRAPHER'S APPEARANCE & MINDSET — How to Look, Move, and Feel Confident on the Wedding Day",
        estimatedMinutes: 8,
        hook: "You are part of the atmosphere — not just a witness.",
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            callout: {
              icon: "🎯",
              title: "LOOK THE PART, FEEL THE PART",
              textLines: [
                `*"You are part of the atmosphere — not just a witness."*`
              ],
              color: "#4a4a4a"
            },
            content: `Being a wedding photographer is not just technical work — it's **performance art**.`,
            contentLeftImageRight: {
              src: "/training/mindset/1a3956e0-3353-4de7-b6b7-bd1e8c10724c.png",
              alt: "Photographer at wedding",
              content: `You're a silent director, a storyteller, a therapist, a problem-solver — all while holding 2.5 kilograms of gear and smiling calmly.`
            },
            transitionTo: null,
          },
          {
            id: "dress",
            title: "1. Dress the way you shoot",
            darkBackground: true,
            content: `Your outfit is part of your communication.

It should whisper *professional*, not shout *look at me*.`,
            callout: {
              icon: "👔",
              title: "Golden rules:",
              items: [
                "**Neutral colors:** black, beige, gray — you blend into every setting.",
                "**Breathable fabrics:** weddings are marathons.",
                "**Comfortable shoes:** no one looks confident while limping.",
                "**Avoid noisy accessories** (bracelets, heels, bags that creak).",
                "**Layers** — so you can adapt to heat or cold easily."
              ],
              color: "#4a4a4a"
            },
            contentAfterCallout: `*Your look should be invisible in the room but unforgettable in the results.*`,
            transitionTo: null,
          },
          {
            id: "movement",
            title: "2. Movement — the choreography of calm",
            darkBackground: true,
            content: `Move with **purpose**, not panic.

Avoid running unless absolutely necessary — your energy sets the tone.

Stay **close** when intimacy is needed, step **back** when emotions unfold naturally.`,
            callout: {
              title: "Tip:",
              textLines: [
                "bend, tilt, crawl if needed — but do it *elegantly*.",
                "",
                "A wedding photographer must move like a guest who happens to hold a camera, not a paparazzi."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "mindset-before",
            title: "3. Mindset before the day",
            darkBackground: true,
            content: `**The night before:**

• Charge all batteries (including your own).
• Visualize the flow of the day.
• Meditate or pray — find stillness.

**On the morning of:**

• Arrive early — calm is contagious.
• Greet everyone warmly — names matter.
• Smile at the staff — they're your secret ally.`,
            calloutLeftImageRight: {
              callout: {
                icon: "✨",
                textLines: [
                  `*"Confidence isn't loud. It's silent readiness."*`
                ],
                color: "#4a4a4a"
              },
              image: {
                src: "/training/mindset/3ce7325b-3511-47d8-b8a1-9ac8d71710c3.png",
                alt: "Photographer mindset"
              }
            },
            transitionTo: null,
          },
          {
            id: "blending",
            title: "4. Blending with the atmosphere",
            darkBackground: true,
            content: `During the day, your **presence affects emotions**.

People mirror your state — if you radiate ease, they relax.`,
            callout: {
              icon: "⏸️",
              title: "Try this:",
              textLines: [
                "When entering a room, pause one second — read the light, the energy, the dynamics.",
                "",
                "Then, start moving.",
                "",
                "That pause will make your photos feel *intentional*, not rushed."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "mental-anchors",
            title: "5. Mental Anchors",
            darkBackground: true,
            content: `When things go wrong (and they will):

• **Breathe.** (Literally. Three deep breaths reset your nervous system.)
• **Think:** *"This is normal."*
• **Focus** on what's controllable — exposure, moment, emotion.

If someone blocks your shot — **reposition**, not react.

If rain starts — **celebrate**, don't complain. It's texture, reflection, emotion.`,
            callout: {
              icon: "⚓",
              textLines: [
                `*Professionalism = composure under chaos.*`
              ],
              color: "#4a4a4a",
              borderOnly: true
            },
            transitionTo: null,
          },
          {
            id: "pro-tip",
            title: "6. Pro Tip — Your invisible energy",
            darkBackground: true,
            content: `When you enter a room, people *feel* you before they see you.

**That's your real brand.**

It's not presets, it's **presence**.

Be kind, grounded, and alert.`,
            callout: {
              icon: "✨",
              textLines: [
                `You're not just documenting love — **you're amplifying it.**`
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "communication",
        title: "COMMUNICATION WITH THE COUPLE — Creating Trust & Emotional Safety",
        hook: "THE ART OF CONNECTION — not just posing, but belonging",
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            callout: {
              icon: "🤍",
              textLines: [
                `"People don't remember how you shot.`,
                ``,
                `They remember how they felt while you were shooting."`,
                ``,
                `*Greg Williams*`
              ],
              color: "#5c3d3d"
            },
            contentLeftImageRight: {
              content: `*Your words shape comfort.*

*Your energy defines trust.*

*Your silence creates space for real emotion.*

__**That's the invisible art of communication in wedding photography.**__`,
              src: "/training/communication/Screenshot_20251204-010545.png",
              alt: "Communication with the couple"
            },
            transitionTo: null,
          },
          {
            id: "before-wedding",
            title: "1. Before the wedding — build trust, not just logistics",
            darkBackground: true,
            content: `Meet the couple at least once before the big day (online or in person).

**Ask questions that matter:**

• "What's the *vibe* you want your photos to have?"
• "What moments are most important for you?"
• "Who are the key people I shouldn't miss?"

Don't only talk about packages and schedules — talk about *them*.

*You're not collecting clients — you're collecting stories.*`,
            callout: {
              icon: "💡",
              title: "Pro Tip:",
              textLines: [
                "Ask to see photos they love — not necessarily wedding photos.",
                "",
                "It reveals how they see themselves, how they want to be portrayed."
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "during-day",
            title: "2. During the day — be calm, but present",
            darkBackground: true,
            content: `When you arrive, smile softly.

Don't rush with the camera — start by connecting.

Ask how they feel. Compliment the dress, the light, the flowers.

Then, fade into the background and let emotions breathe.`,
            galleryImages: [
              {
                src: "/training/communication/Screenshot_20251204-022741.png",
                alt: "Being present during the day",
                span: 3
              }
            ],
            calloutAfterImages: {
              icon: "⚡",
              title: "Body language matters:",
              items: [
                "Keep posture open — never cross arms.",
                "Eye contact = trust.",
                "Gentle tone, slow pace."
              ],
              textLines: [
                "",
                "__People mirror your energy. If you radiate ease, they soften too.__"
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "directing",
            title: "3. Directing without directing",
            darkBackground: true,
            content: `Your goal isn't to control — it's to guide.

Instead of commands, use *invites*:

**Say this:**

• "Take a slow breath together."
• "Touch your forehead to hers."
• "Whisper what you're most excited about today."

**Not this:**

• "Look at me. Smile. Turn left."

*The best emotion is never staged — it's triggered.*`,
            transitionTo: null,
          },
          {
            id: "handling-nerves",
            title: "4. Handling nerves & tension",
            darkBackground: true,
            content: `Some couples freeze in front of the camera.

Normalize it.

Say something like:

*"Everyone feels awkward at first. That's part of the fun."*

Then, move them. Literally. Walking hand in hand always breaks stiffness.`,
            contentLeftImageRight: {
              content: `**Motion unlocks emotion.**`,
              src: "/training/communication/Screenshot_20251204-010347.png",
              alt: "Handling nerves and tension"
            },
            transitionTo: null,
          },
          {
            id: "reading-dynamics",
            title: "5. Reading dynamics",
            darkBackground: true,
            content: `Every couple has a rhythm.

One may be tender and quiet.

Another — playful and bold.

Mirror that energy, don't override it.

• If they're shy — give them space and whisper directions.
• If they're loud — laugh with them, match the tone.`,
            callout: {
              icon: "✨",
              textLines: [
                `**Your adaptability = their comfort.**`
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "after-wedding",
            title: "6. After the wedding — keep the human touch",
            darkBackground: true,
            content: `When sending previews:

• Don't just send a link — write a warm message.
• Mention something specific: *"Your father's expression during the vows moved me."*

It turns your work into *shared memory*, not a transaction.

*You're not delivering files. You're giving back emotions they might have missed.*`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "coverage-strategy",
        title: "COVERAGE STRATEGY — Capturing the Full Story",
        hook: `"A good wedding photographer doesn't just document what happened. They reveal what it felt like."`,
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            callout: {
              icon: "✨",
              title: "THE INVISIBLE NARRATOR",
              textLines: [
                `**"A good wedding photographer doesn't just document what happened.**`,
                ``,
                `**They reveal what it *felt* like."**`,
                ``,
                `Coverage isn't about quantity — it's about rhythm.`,
                ``,
                `The magic of a wedding day lives between grand gestures and quiet breaths.`,
                ``,
                `Your job is to collect both.`
              ],
              color: "#4a3728"
            },
            transitionTo: null,
          },
          {
            id: "philosophy",
            title: "1. The Philosophy of Coverage",
            darkBackground: true,
            content: `Every photo you take belongs to one of four layers:

**1. The Couple** — the soul of the story

**2. The Guests** — context and energy

**3. The Details** — design, texture, aesthetic

**4. The Spontaneous** — life itself

Balance those four, and you'll never miss the essence.`,
            transitionTo: null,
          },
          {
            id: "the-couple",
            title: "2. The Couple — Intimacy & Narrative",
            darkBackground: true,
            content: `Start with their connection, not their poses.

When they're together, they speak a visual language that doesn't need translation — your job is to *listen*.

• Stay close enough to feel, not to interrupt.
• Alternate between wide (environment) and tight (emotion).
• Capture transitions — walking, fixing clothes, hands touching.`,
            contentLeftImageRight: {
              content: `*The truest portraits happen when they forget they're being photographed.*`,
              src: "/training/coverage-strategy/48dd77dc-b3e5-4093-befa-2479730c77c1.png",
              alt: "The couple intimacy"
            },
            calloutAfterImages: {
              icon: "💡",
              title: "Pro Tip:",
              textLines: [
                "during the vows, focus once on the person *listening*, not just the one speaking. That's where the emotion hides."
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "the-guests",
            title: "3. The Guests — Energy, Reactions, Texture",
            darkBackground: true,
            content: `Guests are the rhythm section of your story.

They show joy, chaos, and life outside the couple's bubble.

• During the ceremony → reactions, tears, laughter.
• During dinner → clinking glasses, kids dancing, parents' smiles.
• During speeches → find faces in the crowd, not just the speaker.`,
            calloutLeftImageRight: {
              callout: {
                icon: "⭐",
                title: "Golden Rule:",
                textLines: [
                  "if something makes people laugh — turn your lens *to the audience*, not the source."
                ],
                color: "#4a3728"
              },
              image: {
                src: "/training/coverage-strategy/Screenshot_20251204-010457.png",
                alt: "Guest reactions"
              }
            },
            transitionTo: null,
          },
          {
            id: "the-details",
            title: "4. The Details — Not Decoration, but Atmosphere",
            darkBackground: true,
            content: `Rings, dress, flowers, invitations — they're memory anchors.

But don't shoot them like a catalog.

**Think emotion:**

• Dress → waiting to be worn.
• Empty chairs after ceremony → poetic silence.

*Details tell the truth time forgets.*`,
            callout: {
              icon: "💡",
              title: "Pro Tip:",
              textLines: [
                "use depth of field (f/1.8–2.8) and play with angles — shoot through objects, curtains, reflections."
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "the-spontaneous",
            title: "5. The Spontaneous — The Soul of the Day",
            darkBackground: true,
            content: `These are the frames that will live forever:

Grandma dancing.

A button popping off.

A child hiding under the table.

A couple sharing a secret glance.

You can't plan them — but you can *prepare* for them.

**How:**

• Keep your camera *always ready* (Manual mode, 1/250+, f/2.0–2.8, Auto ISO).
• Anticipate moments, don't chase them.
• Read energy — laughter moves faster than your autofocus.

*Spontaneity rewards attention, not luck.*`,
            transitionTo: null,
          },
          {
            id: "building-rhythm",
            title: "6. Building Rhythm Through Coverage",
            darkBackground: true,
            content: `Your gallery should breathe:

**quiet → loud → quiet again.**

Ceremony → laughter → intimacy → chaos → calm.

That rhythm creates emotion when the couple relives the day.`,
            transitionTo: null,
          },
          {
            id: "shoot-bridges",
            title: `7. Bonus — Shoot "Bridges"`,
            darkBackground: true,
            content: `Between every big scene, there's a transition: walking, waiting, watching, thinking.

Photograph those.

They're the glue that turns a wedding album into a film.`,
            calloutLeftImageRight: {
              callout: {
                icon: "🎬",
                textLines: [
                  `*Storytelling is not about the moments themselves — it's about what happens between them.*`
                ],
                color: "#4a4a4a",
                borderOnly: true
              },
              image: {
                src: "/training/coverage-strategy/f82b6f56-5646-4874-9094-993d30982f07.png",
                alt: "Shooting bridges"
              }
            },
            transitionTo: null,
          },
        ],
      },
      {
        id: "scouting",
        title: "SCOUTING & PRE-VISUALIZATION — Light Tests, Clean Backgrounds, Story Planning",
        hook: `"If you can imagine the photo, you're already halfway to capturing it."`,
        sections: [
          {
            id: "intro",
            title: "Introduction",
            darkBackground: true,
            callout: {
              icon: "✨",
              title: "SEE THE PHOTO BEFORE IT HAPPENS",
              textLines: [
                `Scouting is not about checking locations.`,
                ``,
                `It's about **reading light**, **understanding space**, and **anticipating emotion** — long before the couple steps into the frame.`,
                ``,
                `**This is where great storytellers separate from good photographers.**`
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "core-idea",
            title: "1. The Core Idea: Pre-visualize like a filmmaker",
            darkBackground: true,
            content: `Weddings move fast.

The more you can *predict*, the more freedom you have to create.

**Pre-visualization includes:**

• where the light will fall
• where crowds will move
• where moments naturally happen
• where you'll stand for each scene
• what lenses will give the right emotion
• which backdrops tell the right story

Think of yourself as a director scouting a movie set.`,
            galleryImagesAfter: [
              {
                src: "/training/scouting/ca30d824-6b82-4f85-947f-d37ec3b6a7a5.png",
                alt: "Pre-visualization example 1",
                span: 3
              },
              {
                src: "/training/scouting/9eabfde1-ac20-4bc8-87e9-179ed7a0f575.png",
                alt: "Pre-visualization example 2",
                span: 3
              }
            ],
            transitionTo: null,
          },
          {
            id: "light-first",
            title: "2. Light First, Location Second",
            darkBackground: true,
            content: `Locations can be beautiful — but without good light, they're nothing.

When scouting, ask yourself:`,
            callout: {
              icon: "💡",
              title: "Where is the key light?",
              textLines: [
                `*"As soon as you think about light, you're thinking like a photographer."*`
              ],
              color: "#4a4a4a",
              borderOnly: true
            },
            contentAfterCallout: `Key light outdoors = the sun

…but not always.

At night — lanterns, headlights, a campfire can become your "key".

**Look for:**

• soft open shade
• strong backlight opportunities
• reflective surfaces
• architecture that blocks harsh sun
• natural "softboxes" (arches, tunnels, balconies)`,
            calloutAfterImages: {
              icon: "💡",
              title: "Pro Tip:",
              textLines: [
                `Walk around your own shadow.`,
                ``,
                `When your shadow falls *toward the couple* → that's front light.`,
                ``,
                `When it falls *sideways* → side light.`,
                ``,
                `When your shadow points *away from them* → backlight.`,
                ``,
                `This helps beginners "see" direction instantly.`
              ],
              backgroundColor: "#4a3728"
            },
            transitionTo: null,
          },
          {
            id: "backgrounds",
            title: "3. Backgrounds Matter More Than You Think",
            darkBackground: true,
            content: `A clean background elevates even an average moment.

A messy background destroys even a perfect emotion.

**When scouting, check for:**

• trash bins
• parked cars
• random cables
• high-contrast distractions
• uneven lighting behind the couple
• clashing colors`,
            callout: {
              title: "Look for:",
              textLines: [
                "• ✔ neutral walls",
                "• ✔ foliage",
                "• ✔ distance behind the couple (for depth)",
                "• ✔ textured surfaces",
                "• ✔ symmetry in architecture"
              ],
              color: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "lens-mindset",
            title: "4. Lens Mindset — choose your storytelling tools",
            darkBackground: true,
            content: `Before the wedding day, decide:

• **24mm** → dance floor, chaos, energy
• **35mm** → storytelling, movement, scenes with guests
• **50mm** → intimacy, portraits, walking shots
• **85mm** → emotional moments (vows, ceremony close-ups)

Don't wait for the wedding to "guess" what lens to use.

Decide your *intent* beforehand.`,
            transitionTo: null,
          },
          {
            id: "key-scenes",
            title: "5. Pre-visualizing the key scenes",
            darkBackground: true,
            content: `These are the moments worth planning:

**1) Bride prep**

Window direction, clutter removal, natural soft reflections.

**2) Groom prep**

Low, moody light often works best.

**3) First look**

• Backlight → gorgeous emotion
• Side light → cinematic
• Shade → predictable and clean

**4) Ceremony**

• Where to stand during vows?
• Where will the best reactions be?
• Where is the cleanest line of sight?

**5) Couple session**

Find 3–5 pre-selected spots

(with different backgrounds & different light directions).

**6) Sunset / golden hour**

Scout the horizon.

Trees, buildings, hills — they all affect sunset light.`,
            transitionTo: null,
          },
          {
            id: "light-tests",
            title: "6. Light Tests — your secret weapon",
            darkBackground: true,
            content: `5 minutes of testing = 50 minutes saved later.

**Test:**

• front light
• backlight flares
• diffused shade
• direct sun contrast
• how the venue bounces light

Take 10–15 test shots at each promising location.

Review them.

Adjust your plan.

**This is what professionals do — always.**`,
            transitionTo: null,
          },
          {
            id: "emotions",
            title: "7. Pre-visualizing emotions, not just photos",
            darkBackground: true,
            content: `Ask yourself:

• "Where will the most emotional moment happen?"
• "Where will laughter happen naturally?"
• "Where will the couple be relaxed?"

**The best emotion appears:**

• when people walk
• when they wait
• when they transition
• when they're not "posing"`,
            galleryImages: [
              {
                src: "/training/scouting/b7685579-a9c6-4f1f-a748-548894b3ad12.png",
                alt: "Pre-visualizing emotions",
                span: 3,
                colStart: 4
              }
            ],
            calloutAfterImages: {
              icon: "😎",
              textLines: [
                `Scout **transitional spaces** — corridors, stairs, corners, doorways.`,
                ``,
                `They are often the most cinematic.`
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "storyboard",
            title: "8. Create a mini mental storyboard",
            darkBackground: true,
            content: `You don't need a physical shot list.

But you *do* need a mental one:

• wide establishing shot
• medium storytelling shot
• close detailed shot
• emotional moment
• candid moment
• movement shot

A "cycle" like this keeps your album dynamic and complete.`,
            transitionTo: null,
          },
        ],
      },
      {
        id: "second-shooter",
        title: "SECOND SHOOTER — Coordination, Complementary Coverage, Style Consistency",
        hook: `Not "two photographers shooting," but one vision with two cameras.`,
        sections: [
          {
            id: "second-shooter-intro",
            title: "Introduction",
            darkBackground: true,
            callout: {
              icon: "✨",
              title: "THE ART OF WORKING WITH A SECOND SHOOTER",
              textLines: [
                `Not "two photographers shooting," but **one vision with two cameras**.`,
                ``,
                `Your second shooter isn't your assistant — they are an extension of your storytelling. They fill the gaps your eyes can't see. When coordinated well, the result feels **bigger, richer, more cinematic**.`
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "role-division",
            title: "1. Role Division — the secret to clean storytelling",
            darkBackground: true,
            contentLeftImageRight: {
              content: `A second shooter needs **clear responsibility zones**. Otherwise you get duplicates… or worse — conflicting angles.`,
              src: "/training/second-shooter/Screenshot_20251204-010601.png",
              alt: "Role division"
            },
            contentAfterCallout: `**Primary Photographer (You):**

• emotions
• main storyline
• couple-focused portraits
• key shots (rings, first kiss, vows, first dance)

**Second Shooter:**

• reactions
• behind-the-scenes
• parents & important guests
• wide establishing shots
• details while you shoot action
• opposite angle of key moments`,
            calloutAfterImages: {
              icon: "🎬",
              textLines: [
                `Think of it as shooting a movie from two camera angles.`
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "ss-communication",
            title: "2. Communication — the foundation of everything",
            darkBackground: true,
            content: `The second shooter can't guess what's important to you. You must set expectations *before* the wedding day.`,
            callout: {
              title: "Pre-wedding briefing:",
              textLines: [
                `• The couple's priorities`,
                `• VIP guests (family, best friends, emotional people)`,
                `• Timeline`,
                `• Lighting challenges`,
                `• Style references`,
                `• Must-have moments`,
                `• Phrases you use with couples`,
                `• Your do's & don'ts`
              ],
              backgroundColor: "#5c3d3d"
            },
            contentAfterCallout: `**During the day:**

Use:

• small hand gestures
• eye contact
• short signals
• brief huddles between transitions`,
            calloutAfterImages: {
              icon: "🤫",
              textLines: [
                `Never communicate loudly.`
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "style-consistency",
            title: "3. Style Consistency — how to shoot like one person",
            darkBackground: true,
            content: `This is where many teams fail. To maintain a unified gallery:

**Match:**

• camera profiles
• white balance style (Kelvin, not Auto)
• contrast approach
• focal lengths
• exposure logic

**Shared Vision:**

Show your second shooter:

• your best galleries
• your style pillars (soft light, bold contrast, candid emotion?)
• your "NO" list (e.g., no excessive tilt, no HDR look)`,
            callout: {
              icon: "👁️",
              textLines: [
                `People imitate the vision they understand.`
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "angles",
            title: "4. Angles: Don't duplicate — complement",
            darkBackground: true,
            content: `The smartest second shooter behaves like this:

If you shoot **tight**, they shoot **wide**.

If you shoot **front**, they shoot **side** or **back**.

If you shoot **portraits**, they capture **family reactions**.

If you shoot **details**, they shoot **context**.`,
            callout: {
              title: "Classic complementary examples:",
              textLines: [
                `**During the First Look:**`,
                `• Main photographer: close faces, emotion`,
                `• Second: over-the-shoulder reaction, wide environment`,
                ``,
                `**During the Kiss:**`,
                `• Main: tight emotional shot`,
                `• Second: wide + reactions of guests`,
                ``,
                `**During the Party:**`,
                `• Main: close chaos`,
                `• Second: architecture, lights, mood, silhouettes`
              ],
              color: "#5c3d3d"
            },
            blockquote: `Two angles = cinematic storytelling.`,
            galleryImagesAfter: [
              {
                src: "/training/second-shooter/Screenshot_20251204-025002.png",
                alt: "Complementary angles",
                span: 3,
                colStart: 4
              }
            ],
            transitionTo: null,
          },
          {
            id: "movement",
            title: "5. Movement: Never block each other",
            darkBackground: true,
            content: `**Rules of movement:**

• Don't cross each other's frame
• Stay opposite when covering key moments
• Switch sides during transitions
• Avoid mirroring each other's angle
• Respect each other's space`,
            callout: {
              title: `The "180° Wedding Rule"`,
              textLines: [
                `At big moments (vows, first dance, first kiss):`,
                ``,
                `• You cover 0°–90°`,
                `• Second shooter covers 180°`,
                ``,
                `No overlap.`,
                `No duplicates.`,
                `Full story.`
              ],
              backgroundColor: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "reactivity",
            title: "6. Reactivity — your second shooter should be the hunter",
            darkBackground: true,
            content: `The second shooter's superpower: **fast reaction + emotional hunting**

They should constantly scan:

• grandparents
• kids
• friends crying
• hidden laughs
• touching hands
• small gestures you miss`,
            blockquote: `Your job: the main story. Their job: the human texture between the moments.`,
            blockquoteImage: {
              src: "/training/second-shooter/1322b74d-624b-42da-b86f-90cf2c391ff8.png",
              alt: "Second shooter reactivity"
            },
            transitionTo: null,
          },
          {
            id: "ss-kit",
            title: "7. The Second Shooter Kit — practical tips",
            darkBackground: true,
            content: `**Best lenses:**

• 35mm (versatile storytelling)
• 50mm (emotion)
• 85mm (reactions from distance)
• 24mm (wide ambiance)

**Must-have items:**

✔ extra batteries

✔ spare SD cards

✔ minimal gear (don't overcomplicate)

✔ water`,
            callout: {
              icon: "🎒",
              textLines: [
                `Heavy gear = slow second shooter = missed moments.`
              ],
              backgroundColor: "#4a4a4a"
            },
            transitionTo: null,
          },
          {
            id: "human-side",
            title: "8. The Human Side — loyalty & trust",
            darkBackground: true,
            content: `Your second shooter represents *you*. Their behavior, kindness, energy — all reflects on your brand.

They should:

• smile
• blend in
• respect guests
• stay confident but discreet
• support you emotionally`,
            callout: {
              icon: "💯",
              textLines: [
                `**A good second shooter reduces your stress by 50%.**`,
                ``,
                `**A bad one increases it by 200%.**`,
                ``,
                `Choose wisely.`
              ],
              backgroundColor: "#5c3d3d"
            },
            transitionTo: null,
          },
          {
            id: "final-golden-rule",
            title: "9. Final Golden Rule",
            darkBackground: true,
            callout: {
              icon: "✨",
              textLines: [
                `*"If the main photographer shoots the 'expected' moment, the second shooter captures the 'unexpected' magic."*`,
                ``,
                `**That's the entire art.**`
              ],
              color: "#5c3d3d"
            },
            transitionTo: null,
          },
        ],
      },
    ],
    quiz: {
      title: "LOGISTIC QUIZ",
      description: "Test your knowledge of planning, preparation, communication, and professional workflow on the wedding day.",
      questions: [
        {
          id: 1,
          question: "What is the MOST important reason to arrive early to the venue?",
          options: ["To test every possible lens you own", "To read the light, find clean backgrounds, and pre-visualize scenes", "To choose the best seat for the ceremony", "To chat with guests"],
          correct: 1
        },
        {
          id: 2,
          question: "What's the purpose of scouting BEFORE the wedding day?",
          options: ["To memorize exact poses", "To negotiate tips with venue staff", "To understand light conditions, layout, and possible obstacles", "To find the best mirror selfies"],
          correct: 2
        },
        {
          id: 3,
          question: "What should be prepared the night before the wedding?",
          options: ["Only the camera body", "Batteries, cards, lenses, flash, backups, cleaned sensors", "Clothes only", "Just lenses and camera straps"],
          correct: 1
        },
        {
          id: 4,
          question: "Which outfit principle is recommended for wedding photographers?",
          options: ["As trendy and attention-grabbing as possible", "Neutral, elegant, silent fabrics, comfortable shoes", "Bright white to match the bride", "Something that \"stands out\""],
          correct: 1
        },
        {
          id: 5,
          question: "When communicating with the couple, what matters MOST?",
          options: ["Sending long philosophical texts", "Telling them how to redesign their wedding timeline", "Sharing memes", "Building trust, clarity, and emotional safety"],
          correct: 3
        },
        {
          id: 6,
          question: "Why should you avoid showing stress on a wedding day?",
          options: ["It ruins your photos", "It damages the couple's experience; your energy shapes the room", "Guests will laugh", "Stress makes lenses fog up"],
          correct: 1
        },
        {
          id: 7,
          question: "What is the FIRST rule of working with a second shooter?",
          options: ["Define roles clearly: angles, timings, lenses, moments", "Let them shoot freely with no coordination", "Ask them to copy your photos exactly", "Only give them tasks you don't like"],
          correct: 0
        },
        {
          id: 8,
          question: "What should a second shooter AVOID?",
          options: ["Shooting from different heights", "Using the same focal length as the lead during key moments", "Capturing alternative angles", "Being invisible"],
          correct: 1
        },
        {
          id: 9,
          question: "Backup strategy during the event should include:",
          options: ["Dual card slot recording + in-day card swapping", "One SD card to keep everything simple", "Backup after the wedding only", "Trusting the universe"],
          correct: 0
        },
        {
          id: 10,
          question: "Why is pre-visualization important?",
          options: ["Saves time and helps anticipate light + emotions", "Eliminates the need for creativity", "Ensures you copy Pinterest boards exactly", "Helps your camera autofocus faster"],
          correct: 0
        },
        {
          id: 11,
          question: "What is the BEST way to move during the ceremony?",
          options: ["As fast as possible to catch action", "Quiet, slow, invisible movements; respect the moment", "Constantly cross the aisle for variety", "Wave to the guests"],
          correct: 1
        },
        {
          id: 12,
          question: "When is it acceptable to interrupt the couple?",
          options: ["During the ceremony", "During emotional family moments", "When safety is at stake or you're about to miss a key shot", "Never"],
          correct: 2
        },
        {
          id: 13,
          question: "Why do photographers wear all-black or neutral colors?",
          options: ["Tradition", "To look slimmer in BTS reels", "Because white is reserved for the bride", "To avoid color cast reflections and stay invisible"],
          correct: 3
        },
        {
          id: 14,
          question: "What is essential when planning the wedding timeline with the couple?",
          options: ["Planning minute-to-minute rigidity", "Keeping everything extremely tight", "Adding 30–60 min buffers for delays, weather, and transitions", "Letting the couple guess the timing"],
          correct: 2
        },
        {
          id: 15,
          question: "What's the main purpose of clean backgrounds during scouting?",
          options: ["So editing becomes easier", "To avoid distractions and highlight the couple", "Because walls are pretty", "For posting BTS"],
          correct: 1
        },
        {
          id: 16,
          question: "What is the priority during getting-ready coverage?",
          options: ["Photographing every object in the room", "Using the widest lens possible", "Finding pockets of clean light and decluttered space", "Making the room look bigger"],
          correct: 2
        },
        {
          id: 17,
          question: "What should you do if the ceremony starts earlier than planned?",
          options: ["Panic", "Adapt instantly — adjust lens choice, light, and position", "Ask them to stop", "Leave"],
          correct: 1
        },
        {
          id: 18,
          question: "Why are backup batteries critical?",
          options: ["To lend them to guests", "Because cameras look cooler with many batteries", "Flash + cold venues drain power fast", "For decoration"],
          correct: 2
        },
        {
          id: 19,
          question: "A second shooter should ideally use lenses that…",
          options: ["Match the lead exactly", "Complement the lead (e.g., telephoto vs wide)", "Are vintage only", "Are all 85mm"],
          correct: 1
        },
        {
          id: 20,
          question: "What is the golden rule of photographer mindset?",
          options: ["Fake confidence loudly", "Calm, grounded, observant, emotionally present", "Talk non-stop", "Constantly check your phone"],
          correct: 1
        },
        {
          id: 21,
          question: "What's the main danger of poor communication with the couple?",
          options: ["Expectations don't match final results", "They choose another photographer next time", "They ask too many questions", "You must take more photos"],
          correct: 0
        },
        {
          id: 22,
          question: "What is essential immediately after the wedding day?",
          options: ["Sleeping for 14 hours", "Posting sneak peeks before backing up", "Cleaning lenses only", "Backing up files in 2–3 separate locations"],
          correct: 3
        },
        {
          id: 23,
          question: "Why should you walk the venue BEFORE guests arrive?",
          options: ["To have photos without people", "To practice running", "To test flash, spot problem lighting, choose safe paths and hiding spots", "To memorize exits"],
          correct: 2
        },
        {
          id: 24,
          question: "During family formals, the MOST important logistic skill is:",
          options: ["Yelling loudly", "Efficient group management + clear posing guidance", "Using only wide angles", "Shooting extremely fast with no direction"],
          correct: 1
        },
        {
          id: 25,
          question: "The ultimate logistic priority of a wedding photographer is:",
          options: ["Perfect gear", "Matching the decor", "Taking as many photos as possible", "Smooth workflow, calm presence, anticipation, and reliability"],
          correct: 3
        },
      ]
    },
  },
};

export default function PhotographyFundamentals() {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Access control state
  const [hasAccess, setHasAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);

  // Lightbox state for viewing images as gallery
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // URL-based navigation
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive page state from URL params
  const urlCategory = searchParams.get("category");
  const urlModule = searchParams.get("module");

  // Determine current page based on URL
  const page = urlModule ? "module" : urlCategory ? "category" : "home";

  // Find current category and module from URL
  const currentCategory = urlCategory ? trainingStructure[urlCategory] : null;
  const currentModule = useMemo(() => {
    if (!urlModule || !currentCategory) return null;
    return currentCategory.modules?.find(m => m.id === urlModule) || null;
  }, [urlModule, currentCategory]);

  // Helper to collect all images from current module
  const collectModuleImages = useCallback((module) => {
    if (!module) return [];
    const images = [];

    // Header image
    if (module.headerImage?.src) images.push(module.headerImage.src);

    // Cover image (for page-style modules)
    if (module.coverImage) images.push(module.coverImage);

    // Sections
    if (module.sections) {
      module.sections.forEach(section => {
        if (section.imageAboveContent) images.push(section.imageAboveContent);
        if (section.heroImage?.src) images.push(section.heroImage.src);
        if (section.sideByeSideImage?.src) images.push(section.sideByeSideImage.src);
        if (section.contentLeftImageRight?.src) images.push(section.contentLeftImageRight.src);
        if (section.calloutLeftImageRight?.image?.src) images.push(section.calloutLeftImageRight.image.src);
        if (section.image?.src) images.push(section.image.src);
        if (section.galleryImages) {
          section.galleryImages.forEach(img => {
            if (img.src) images.push(img.src);
            if (img.stackWith?.src) images.push(img.stackWith.src);
          });
        }
        if (section.galleryImagesAfter) {
          section.galleryImagesAfter.forEach(img => {
            if (img.src) images.push(img.src);
          });
        }
        if (section.additionalImages) {
          section.additionalImages.forEach(img => {
            if (img.src) images.push(img.src);
            if (img.stackWith?.src) images.push(img.stackWith.src);
          });
        }
        if (section.closingImage) images.push(section.closingImage);
      });
    }

    // Reference images
    if (module.referenceImages) {
      module.referenceImages.forEach(img => {
        if (img.src) images.push(img.src);
      });
    }

    // Legacy content images
    if (module.images) {
      Object.values(module.images).forEach(img => {
        if (img.src) images.push(img.src);
      });
    }

    return images;
  }, []);

  // Open lightbox with gallery
  const openLightbox = useCallback((clickedImageSrc) => {
    const images = collectModuleImages(currentModule);
    const index = images.indexOf(clickedImageSrc);
    if (index >= 0) {
      setLightboxImages(images);
      setLightboxIndex(index);
    } else {
      // Fallback: just show the clicked image
      setLightboxImages([clickedImageSrc]);
      setLightboxIndex(0);
    }
  }, [currentModule, collectModuleImages]);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setLightboxIndex(-1);
    setLightboxImages([]);
  }, []);

  // Navigation functions that update URL
  const navigateToHome = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const navigateToCategory = useCallback((categoryId) => {
    setSearchParams({ category: categoryId });
  }, [setSearchParams]);

  const navigateToModule = useCallback((categoryId, moduleId) => {
    setSearchParams({ category: categoryId, module: moduleId });
  }, [setSearchParams]);

  const navigateToNextModule = useCallback((categoryId, currentModuleId) => {
    const category = trainingStructure[categoryId];
    if (!category) return;

    const currentIndex = category.modules.findIndex(m => m.id === currentModuleId);
    const nextModule = category.modules[currentIndex + 1];

    // Scroll to top first
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (nextModule) {
      setSearchParams({ category: categoryId, module: nextModule.id });
    } else {
      // No more modules in this category, go back to category list
      setSearchParams({ category: categoryId });
    }
  }, [setSearchParams]);

  // Section state for modules with sections
  const [expandedSections, setExpandedSections] = useState({});
  const [sectionProgress, setSectionProgress] = useState({}); // { "moduleId:sectionId": true }
  const [lastCompletedSection, setLastCompletedSection] = useState(null); // Track for transition messages
  const [showConfetti, setShowConfetti] = useState(false);

  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResults, setQuizResults] = useState({}); // { trackId: { passed, score, maxScore, percentage } }

  // Certification state
  const [certification, setCertification] = useState(null);
  const [justCertified, setJustCertified] = useState(false);
  const [showCertificationCelebration, setShowCertificationCelebration] = useState(false);
  const [applyingBadge, setApplyingBadge] = useState(false);

  // Progress tracking with database
  const [completed, setCompleted] = useState({});
  const [progressLoading, setProgressLoading] = useState(true);

  // Check course access on mount and when user changes
  useEffect(() => {
    const checkAccess = async () => {
      setAccessLoading(true);
      setAccessError(null);

      try {
        const response = await fetch('/api/training/check-access', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user?.id || null,
            email: user?.email || profile?.email || null,
            courseId: 'photography-fundamentals'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to check access');
        }

        const data = await response.json();
        setHasAccess(data.hasAccess);
      } catch (error) {
        console.error('Error checking training access:', error);
        setAccessError(error.message);
        setHasAccess(false);
      } finally {
        setAccessLoading(false);
      }
    };

    // Wait for auth to finish loading before checking access
    if (!authLoading) {
      checkAccess();
    }
  }, [user, profile, authLoading]);

  // Load progress from database on mount (when user is authenticated and has access)
  useEffect(() => {
    const loadProgress = async () => {
      // Only load from DB if user is authenticated and has access
      if (!user?.id || !hasAccess) {
        setProgressLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/training/progress?userId=${user.id}`, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
          throw new Error('Failed to load progress');
        }

        const data = await response.json();

        // Set module completion state
        setCompleted(data.modules || {});

        // Set section progress state
        setSectionProgress(data.sections || {});

        // Set quiz results
        setQuizResults(data.quizzes || {});

        // Set certification status
        setCertification(data.certification || null);
      } catch (error) {
        console.error('Error loading progress from database:', error);
        // Fallback to localStorage for backwards compatibility
        const savedProgress = localStorage.getItem("lpTrainingProgress");
        const savedSectionProgress = localStorage.getItem("lpSectionProgress");
        if (savedProgress) {
          try {
            setCompleted(JSON.parse(savedProgress));
          } catch (e) {
            console.error("Failed to load progress from localStorage:", e);
          }
        }
        if (savedSectionProgress) {
          try {
            setSectionProgress(JSON.parse(savedSectionProgress));
          } catch (e) {
            console.error("Failed to load section progress from localStorage:", e);
          }
        }
      } finally {
        setProgressLoading(false);
      }
    };

    // Wait for auth and access check to complete before loading progress
    if (!authLoading && !accessLoading) {
      loadProgress();
    }
  }, [user?.id, hasAccess, authLoading, accessLoading]);

  // Also save to localStorage as backup (for offline/fallback)
  useEffect(() => {
    if (Object.keys(completed).length > 0) {
      localStorage.setItem("lpTrainingProgress", JSON.stringify(completed));
    }
  }, [completed]);

  // Also save section progress to localStorage as backup
  useEffect(() => {
    if (Object.keys(sectionProgress).length > 0) {
      localStorage.setItem("lpSectionProgress", JSON.stringify(sectionProgress));
    }
  }, [sectionProgress]);

  // Auto-expand first incomplete section when module loads from URL
  useEffect(() => {
    if (currentModule?.sections && Object.keys(expandedSections).length === 0) {
      const firstIncomplete = currentModule.sections.find(
        (s) => !sectionProgress[`${currentModule.id}:${s.id}`]
      );
      if (firstIncomplete) {
        setExpandedSections({ [`${currentModule.id}:${firstIncomplete.id}`]: true });
      } else if (currentModule.sections.length > 0) {
        // All complete, expand first section
        setExpandedSections({ [`${currentModule.id}:${currentModule.sections[0].id}`]: true });
      }
    }
  }, [currentModule, sectionProgress]);

  // Check if user is viewing a trial module
  const isViewingTrialModule = urlModule === FREE_TRIAL_MODULE_ID;

  // Determine if content should be accessible
  // - Has purchased access, OR
  // - Viewing the free trial module
  const canAccessContent = hasAccess || isViewingTrialModule;

  const percentComplete = () => {
    const total = Object.values(trainingStructure).reduce(
      (acc, cat) => acc + cat.modules.length,
      0
    );
    const done = Object.keys(completed).filter((k) => completed[k]).length;
    return total === 0 ? 0 : Math.round((done / total) * 100);
  };

  const categoryPercent = (catKey) => {
    const cat = trainingStructure[catKey];
    if (!cat) return 0;
    const lessons = cat.modules.filter((m) => m.displayType !== "page");
    const done = lessons.filter((m) => completed[m.id]).length;
    return lessons.length === 0 ? 0 : Math.round((done / lessons.length) * 100);
  };

  const openCategory = (key) => {
    navigateToCategory(key);
  };

  const openModule = (module, categoryId) => {
    navigateToModule(categoryId || urlCategory, module.id);

    // If module has sections, auto-expand first incomplete section
    if (module.sections) {
      const firstIncomplete = module.sections.find(
        (s) => !sectionProgress[`${module.id}:${s.id}`]
      );
      if (firstIncomplete) {
        setExpandedSections({ [`${module.id}:${firstIncomplete.id}`]: true });
      } else {
        // All complete, expand first section
        setExpandedSections({ [`${module.id}:${module.sections[0].id}`]: true });
      }
    }
  };

  const markComplete = async (id, categoryId) => {
    setCompleted((prev) => ({ ...prev, [id]: true }));

    // Save to database if authenticated
    if (user?.id) {
      try {
        await fetch('/api/training/progress/module', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            moduleId: id,
            categoryId: categoryId || urlCategory,
            courseId: 'photography-fundamentals'
          })
        });
      } catch (error) {
        console.error('Error saving module progress:', error);
      }
    }
  };

  // Mark a section as complete
  const markSectionComplete = async (moduleId, sectionId) => {
    const key = `${moduleId}:${sectionId}`;
    setSectionProgress((prev) => ({ ...prev, [key]: true }));

    // Save to database if authenticated
    if (user?.id) {
      try {
        await fetch('/api/training/progress/section', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            moduleId,
            sectionId,
            courseId: 'photography-fundamentals'
          })
        });
      } catch (error) {
        console.error('Error saving section progress:', error);
      }
    }

    // Show transition message for this section
    setLastCompletedSection(key);

    // Check if all sections are complete
    if (currentModule?.sections) {
      const allComplete = currentModule.sections.every(
        (s) => s.id === sectionId || sectionProgress[`${moduleId}:${s.id}`]
      );
      // Only auto-complete if there's no proTip or referenceImages to view
      const hasMoreContent = currentModule.proTip || (currentModule.referenceImages && currentModule.referenceImages.length > 0);
      if (allComplete && !hasMoreContent) {
        markComplete(moduleId, urlCategory);
        // Trigger confetti for lesson completion
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }

      // Auto-expand next incomplete section after a brief delay (for transition to show)
      const currentIndex = currentModule.sections.findIndex((s) => s.id === sectionId);
      const nextSection = currentModule.sections[currentIndex + 1];
      if (nextSection && !sectionProgress[`${moduleId}:${nextSection.id}`]) {
        setTimeout(() => {
          setExpandedSections((prev) => ({
            ...prev,
            [`${moduleId}:${sectionId}`]: false,
            [`${moduleId}:${nextSection.id}`]: true,
          }));
          // Scroll to next section after animation completes
          setTimeout(() => {
            const nextSectionElement = document.getElementById(`section-${nextSection.id}`);
            if (nextSectionElement) {
              const yOffset = -150; // Offset to account for sticky header and show title
              const y = nextSectionElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }
          }, 350);
          // Clear transition message after auto-expand
          setTimeout(() => setLastCompletedSection(null), 500);
        }, 1500);
      }
    }
  };

  // Toggle section expansion
  const toggleSection = (moduleId, sectionId) => {
    const key = `${moduleId}:${sectionId}`;
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Apply badge for existing photographers
  const handleApplyBadge = async () => {
    setApplyingBadge(true);
    try {
      const response = await fetch('/api/training/apply-badge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          courseId: 'photography-fundamentals'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to apply badge');
      }

      // Update certification state to reflect badge applied
      setCertification(prev => ({
        ...prev,
        appliedToPhotographerAt: new Date().toISOString(),
        hasLnpBadge: true
      }));

      // Show success celebration
      setShowCertificationCelebration(true);
    } catch (error) {
      console.error('Error applying badge:', error);
      alert(error.message || 'Failed to apply badge. Please try again.');
    } finally {
      setApplyingBadge(false);
    }
  };

  // Submit quiz to API
  const handleQuizSubmit = async (trackId, questions) => {
    setQuizSubmitting(true);

    try {
      const response = await fetch('/api/training/quiz/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          courseId: 'photography-fundamentals',
          categoryId: trackId,
          answers: quizAnswers,
          questions: questions
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit quiz');
      }

      const result = await response.json();

      // Update quiz results state
      setQuizResults(prev => ({
        ...prev,
        [trackId]: {
          passed: result.passed,
          score: result.score,
          maxScore: result.maxScore,
          percentage: result.percentage
        }
      }));

      // Update certification if returned
      if (result.certification) {
        setCertification(result.certification);
      }

      // Check if user just became certified
      if (result.justCertified) {
        setJustCertified(true);
        setShowCertificationCelebration(true);
        // Show confetti for certification
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      } else if (result.passed) {
        // Show confetti for passing quiz (but not certification level)
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }

      setQuizSubmitted(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      // Fallback to local calculation
      setQuizSubmitted(true);
    } finally {
      setQuizSubmitting(false);
    }
  };

  // Get section completion count for a module
  const getSectionProgress = (module) => {
    if (!module.sections) return { completed: 0, total: 0 };
    const completedCount = module.sections.filter(
      (s) => sectionProgress[`${module.id}:${s.id}`]
    ).length;
    return { completed: completedCount, total: module.sections.length };
  };

  // Check if a module is locked (sequential gating within tracks)
  // Module is locked if the previous module in the same track is not complete
  const isModuleLocked = (categoryKey, moduleId) => {
    const category = trainingStructure[categoryKey];
    if (!category) return false;

    // Filter out "page" type modules (like Resources) - they don't count for gating
    const lessons = category.modules.filter((m) => m.displayType !== "page");
    const moduleIndex = lessons.findIndex((m) => m.id === moduleId);

    // First module is never locked
    if (moduleIndex <= 0) return false;

    // Check if previous module is complete
    const prevModule = lessons[moduleIndex - 1];
    return !completed[prevModule.id];
  };

  // Check if quiz is unlocked (all modules in track must be complete)
  const isQuizUnlocked = (categoryKey) => {
    const category = trainingStructure[categoryKey];
    if (!category) return false;

    // Filter out "page" type modules - only lessons count
    const lessons = category.modules.filter((m) => m.displayType !== "page");

    // All lessons must be complete
    return lessons.every((m) => completed[m.id]);
  };

  // Get count of remaining modules needed to unlock quiz
  const getModulesUntilQuiz = (categoryKey) => {
    const category = trainingStructure[categoryKey];
    if (!category) return 0;

    const lessons = category.modules.filter((m) => m.displayType !== "page");
    const completedCount = lessons.filter((m) => completed[m.id]).length;
    return lessons.length - completedCount;
  };

  // Get module description (first section content or legacy content)
  const getModulePreview = (module) => {
    if (module.sections && module.sections.length > 0) {
      return module.sections[0].content?.slice(0, 100) || "";
    }
    return module.content?.slice(0, 100) || "";
  };

  // Loading state while checking access or loading progress
  if (authLoading || accessLoading || (hasAccess && progressLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fef2f4] rounded-full mb-4">
            <div className="w-8 h-8 border-3 border-[#fe395f] border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-[#606b7c]">Loading training course...</p>
        </motion.div>
      </div>
    );
  }

  // Access gate - show purchase prompt if no access (unless viewing trial module)
  if (!canAccessContent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fef2f4] rounded-full mb-4">
                <Lock className="h-8 w-8 text-[#fe395f]" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Photography Fundamentals
              </h1>
              <p className="text-[#606b7c] mb-4">
                Get lifetime access to 36 in-depth modules covering technical, creative, and logistic skills for wedding photography.
              </p>
              <div className="text-3xl font-bold text-gray-900 mb-2">$167</div>
              <p className="text-sm text-[#606b7c]">One-time purchase · Lifetime access</p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/training/photography-fundamentals/purchase')}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90"
              >
                Get Full Access
              </Button>

              <Button
                variant="outline"
                onClick={() => navigateToModule('technical', FREE_TRIAL_MODULE_ID)}
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                Try Free Module
              </Button>

              {user && (
                <p className="text-xs text-center text-[#606b7c] mt-4">
                  Signed in as {user.email}
                </p>
              )}

              {!user && (
                <p className="text-xs text-center text-[#606b7c] mt-4">
                  Already purchased?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-[#fe395f] hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              )}
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Check if in trial mode (viewing free module without full access)
  const isTrialMode = isViewingTrialModule && !hasAccess;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Lightbox for viewing images */}
      <AnimatePresence>
        {lightboxIndex >= 0 && lightboxImages.length > 0 && (
          <Lightbox
            images={lightboxImages}
            currentIndex={lightboxIndex}
            onIndexChange={setLightboxIndex}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>

      {/* Certification Celebration Modal */}
      <AnimatePresence>
        {showCertificationCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setShowCertificationCelebration(false)}
          >
            <Confetti show={true} />
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#ffd700] to-[#ff8c00] rounded-full mb-6 shadow-lg"
              >
                <Sparkles className="w-12 h-12 text-white" />
              </motion.div>

              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                You're L+P Certified!
              </h2>

              <p className="text-lg text-[#606b7c] mb-6">
                Congratulations! You've passed all three quizzes and earned your Love & Photos certification.
              </p>

              {certification?.isPhotographer ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">
                    Your L+P Choice badge is now displayed on your photographer profile!
                  </p>
                </div>
              ) : (
                <div className="bg-[#fef2f4] border border-[#fe395f]/20 rounded-xl p-4 mb-6">
                  <Gift className="w-8 h-8 text-[#fe395f] mx-auto mb-2" />
                  <p className="text-[#606b7c]">
                    Create a photographer profile to display your L+P Choice badge and get discovered by couples.
                  </p>
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setShowCertificationCelebration(false)}
                  className="border-gray-300"
                >
                  Continue Learning
                </Button>
                {!certification?.isPhotographer && (
                  <Button
                    onClick={() => {
                      setShowCertificationCelebration(false);
                      navigate('/onboarding');
                    }}
                    className="bg-[#fe395f] text-white hover:bg-[#e62e4f]"
                  >
                    Create Profile
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trial Mode Banner */}
      {isTrialMode && (
        <div className="bg-gradient-to-r from-[#fe395f] to-[#ff6b89] text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-sm font-medium">
              You're viewing a free preview. Get full access to all 36 modules.
            </p>
            <Button
              size="sm"
              onClick={() => navigate('/training/photography-fundamentals/purchase')}
              className="bg-white text-[#fe395f] hover:bg-gray-100 text-sm"
            >
              Get Full Access — $167
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">
                Photography Fundamentals
              </h1>
              <p className="mt-0.5 sm:mt-1 text-sm sm:text-base text-[#606b7c]">
                Master the technical, creative, and logistical skills for wedding photography
              </p>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-white rounded-lg border border-gray-200 px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-2 sm:gap-3">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#fe395f]" />
                <div>
                  <div className="text-xs text-[#606b7c]">Progress</div>
                  <div className="text-base sm:text-lg font-bold text-gray-900">
                    {percentComplete()}%
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/training/photography-fundamentals/purchase')}
                className="text-[#fe395f] border-[#fe395f] hover:bg-[#fef2f4]"
              >
                <Gift className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Gift</span>
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 sm:mt-4">
            <div className="h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#fe395f] to-[#ff6b89]"
                initial={{ width: 0 }}
                animate={{ width: `${percentComplete()}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <AnimatePresence mode="wait">
          {/* HOME PAGE - Category Selection */}
          {page === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Certification Status Card */}
              {(certification?.isCertified || Object.keys(quizResults).length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 sm:mb-6"
                >
                  <Card className={`border-2 ${
                    certification?.isCertified
                      ? "border-[#ffd700] bg-gradient-to-br from-amber-50 to-white"
                      : "border-gray-200"
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          certification?.isCertified
                            ? "bg-gradient-to-br from-[#ffd700] to-[#ff8c00]"
                            : "bg-gray-100"
                        }`}>
                          {certification?.isCertified ? (
                            <Sparkles className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                          ) : (
                            <BookOpen className="w-5 h-5 sm:w-7 sm:h-7 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-base sm:text-lg font-bold ${
                            certification?.isCertified ? "text-amber-700" : "text-gray-900"
                          }`}>
                            {certification?.isCertified
                              ? "L+P Certified!"
                              : "Certification Progress"
                            }
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1">
                            {['technical', 'creative', 'logistic'].map((track) => {
                              const passed = quizResults[track]?.passed;
                              return (
                                <div key={track} className="flex items-center gap-1 text-xs sm:text-sm">
                                  {passed ? (
                                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                                  ) : (
                                    <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border-2 border-gray-300" />
                                  )}
                                  <span className={passed ? "text-green-600" : "text-gray-500"}>
                                    {track.charAt(0).toUpperCase() + track.slice(1)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      {certification?.isCertified && !certification?.appliedToPhotographerAt && (
                        certification?.isPhotographer ? (
                          <Button
                            size="sm"
                            onClick={handleApplyBadge}
                            disabled={applyingBadge}
                            className="bg-[#fe395f] text-white hover:bg-[#e62e4f] w-full sm:w-auto"
                          >
                            {applyingBadge ? 'Applying...' : 'Apply Badge'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => navigate('/talent/apply')}
                            className="bg-[#fe395f] text-white hover:bg-[#e62e4f] w-full sm:w-auto"
                          >
                            Claim Badge
                          </Button>
                        )
                      )}
                    </div>
                  </Card>
                </motion.div>
              )}

              {/* Category Cards Grid */}
              <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
              {Object.entries(trainingStructure).map(([key, cat]) => {
                const Icon = cat.icon;
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Card
                      hover
                      className="h-full cursor-pointer border-gray-200"
                      onClick={() => openCategory(key)}
                    >
                      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                        <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-[#fef2f4] to-white rounded-xl flex items-center justify-center border border-[#fe395f]/20">
                          <span className="text-xl sm:text-2xl">{cat.emoji}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-0.5 sm:mb-1">
                            {cat.title}
                          </h2>
                          <p className="text-xs sm:text-sm text-[#606b7c] italic">
                            {cat.description}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 sm:mt-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-[#606b7c]">
                            {cat.modules.filter((m) => completed[m.id] && m.displayType !== "page").length} /{" "}
                            {cat.modules.filter((m) => m.displayType !== "page").length} lessons completed
                          </span>
                          <span className="text-xs font-bold text-[#fe395f]">
                            {categoryPercent(key)}%
                          </span>
                        </div>
                        <div className="w-full bg-[#fef2f4] rounded-full h-1.5 sm:h-2">
                          <motion.div
                            className="bg-[#fe395f] h-1.5 sm:h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${categoryPercent(key)}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>

                      <Button
                        className="w-full mt-3 sm:mt-4 bg-[#fe395f] hover:bg-[#fe395f]/90 text-sm sm:text-base"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCategory(key);
                        }}
                      >
                        View {cat.modules.filter((m) => m.displayType !== "page").length} Lessons
                      </Button>
                    </Card>
                  </motion.div>
                );
              })}
              </div>
            </motion.div>
          )}

          {/* CATEGORY PAGE - Module List */}
          {page === "category" && currentCategory && (
            <motion.div
              key="category"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Button
                variant="ghost"
                onClick={() => navigateToHome()}
                className="mb-4 sm:mb-6 text-[#606b7c] hover:text-[#fe395f] text-sm sm:text-base"
              >
                <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> Back to Categories
              </Button>

              <div className="mb-5 sm:mb-8">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <span className="text-2xl sm:text-3xl">
                    {currentCategory.emoji}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {currentCategory.title}
                  </h2>
                </div>
                <p className="text-base sm:text-lg text-[#606b7c] italic">
                  {currentCategory.description}
                </p>
              </div>

              <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                {currentCategory.modules.filter(mod => mod.displayType !== "page").map((mod, index) => {
                  const progress = getSectionProgress(mod);
                  const moduleLocked = isModuleLocked(urlCategory, mod.id);

                  return (
                    <motion.div
                      key={mod.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        hover={!moduleLocked}
                        className={`border-gray-200 ${
                          moduleLocked
                            ? "opacity-60 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        onClick={() => !moduleLocked && openModule(mod)}
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center border ${
                            moduleLocked
                              ? "bg-gray-100 border-gray-200"
                              : "bg-gradient-to-br from-[#fef2f4] to-white border-[#fe395f]/20"
                          }`}>
                            {moduleLocked ? (
                              <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                            ) : (
                              <CheckCircle
                                className={`h-5 w-5 sm:h-6 sm:w-6 ${
                                  completed[mod.id]
                                    ? "text-[#fe395f]"
                                    : "text-[#606b7c]"
                                }`}
                              />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                              <h3 className={`font-semibold text-sm sm:text-base ${moduleLocked ? "text-gray-400" : "text-gray-900"}`}>
                                {mod.title}
                              </h3>
                              {mod.estimatedMinutes && (
                                <span className={`flex items-center gap-1 text-xs px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap ${
                                  moduleLocked ? "text-gray-400 bg-gray-100" : "text-[#606b7c] bg-gray-100"
                                }`}>
                                  <Clock className="w-3 h-3" />
                                  {mod.estimatedMinutes} min
                                </span>
                              )}
                            </div>
                            <p className={`text-xs sm:text-sm ${moduleLocked ? "text-gray-400" : "text-[#606b7c]"}`}>
                              {moduleLocked ? "Complete the previous lesson to unlock" : `${getModulePreview(mod)}...`}
                            </p>

                            {/* Section progress indicator */}
                            {!moduleLocked && progress.total > 0 && (
                              <div className="mt-2 flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-[#fe395f] rounded-full transition-all"
                                    style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs text-[#606b7c]">
                                  {progress.completed}/{progress.total}
                                </span>
                              </div>
                            )}

                            {completed[mod.id] && (
                              <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#fe395f]">
                                <CheckCircle className="w-3 h-3" />
                                Completed
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              {/* Resources/Reference Pages - styled differently */}
              {currentCategory.modules.filter(mod => mod.displayType === "page").map((mod, index) => (
                <motion.div
                  key={mod.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-6"
                >
                  <Card
                    hover
                    className="cursor-pointer border-gray-300 bg-gradient-to-br from-gray-50 to-white"
                    onClick={() => openModule(mod)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#2a2a2a] rounded-xl flex items-center justify-center">
                        <BookOpen className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900 text-lg">
                            {mod.title}
                          </h3>
                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                            Reference
                          </span>
                        </div>
                        <p className="text-sm text-[#606b7c] mt-1">
                          Films, books, links, and photographers for inspiration
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Card>
                </motion.div>
              ))}

              {/* Quiz Section - only show for tracks that have a quiz */}
              {currentCategory.quiz && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-8"
                >
                  {(() => {
                    const quizUnlocked = isQuizUnlocked(urlCategory);
                    const modulesRemaining = getModulesUntilQuiz(urlCategory);
                    const trackQuizResult = quizResults[urlCategory];
                    const hasPassed = trackQuizResult?.passed;

                    return (
                      <Card className={`border-gray-200 ${
                        quizUnlocked
                          ? hasPassed
                            ? "bg-gradient-to-br from-green-50 to-white border-green-200"
                            : "bg-gradient-to-br from-[#fef2f4] to-white"
                          : "bg-gray-50 opacity-75"
                      }`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            quizUnlocked
                              ? hasPassed
                                ? "bg-green-500"
                                : "bg-[#fe395f]"
                              : "bg-gray-300"
                          }`}>
                            {quizUnlocked ? (
                              hasPassed ? (
                                <CheckCircle className="w-6 h-6 text-white" />
                              ) : (
                                <Sparkles className="w-6 h-6 text-white" />
                              )
                            ) : (
                              <Lock className="w-6 h-6 text-white" />
                            )}
                          </div>
                          <div>
                            <h3 className={`text-xl font-bold ${quizUnlocked ? "text-gray-900" : "text-gray-500"}`}>
                              {currentCategory.quiz.title}
                            </h3>
                            <p className={`text-sm ${quizUnlocked ? "text-[#606b7c]" : "text-gray-400"}`}>
                              {hasPassed
                                ? `Passed with ${trackQuizResult.percentage}%`
                                : `${currentCategory.quiz.questions.length} questions`}
                            </p>
                          </div>
                        </div>

                        {!quizUnlocked ? (
                          <div>
                            <p className="text-gray-500 mb-4">
                              Complete all {modulesRemaining} remaining lesson{modulesRemaining !== 1 ? 's' : ''} to unlock this quiz.
                            </p>
                            <Button disabled className="bg-gray-300 text-gray-500 cursor-not-allowed">
                              <Lock className="w-4 h-4 mr-2" />
                              Quiz Locked
                            </Button>
                          </div>
                        ) : !showQuiz ? (
                          <div>
                            <p className="text-[#606b7c] mb-4">{currentCategory.quiz.description}</p>
                            <Button
                              onClick={() => {
                                setShowQuiz(true);
                                setQuizAnswers({});
                                setQuizSubmitted(false);
                                setCurrentQuizQuestion(0);
                              }}
                              className={hasPassed
                                ? "bg-green-500 text-white hover:bg-green-600"
                                : "bg-[#fe395f] text-white hover:bg-[#e62e4f]"
                              }
                            >
                              {hasPassed ? 'Retake Quiz' : 'Start Quiz'}
                            </Button>
                          </div>
                        ) : !quizSubmitted ? (
                      <div className="space-y-3 sm:space-y-4">
                        {/* Progress indicator */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-base sm:text-lg font-bold text-[#fe395f]">
                            {currentQuizQuestion + 1} / {currentCategory.quiz.questions.length}
                          </span>
                          <div className="flex-1 mx-2 sm:mx-4 h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#fe395f] rounded-full transition-all duration-300"
                              style={{ width: `${((currentQuizQuestion + 1) / currentCategory.quiz.questions.length) * 100}%` }}
                            />
                          </div>
                        </div>

                        {/* Current question */}
                        {(() => {
                          const q = currentCategory.quiz.questions[currentQuizQuestion];
                          if (!q) return null; // Safety guard for out-of-bounds
                          return (
                            <motion.div
                              key={q.id}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
                            >
                              <p className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                                {q.question}
                              </p>
                              <div className="space-y-2 sm:space-y-3">
                                {q.options.map((option, oIndex) => {
                                  const isSelected = quizAnswers[q.id] === oIndex;
                                  return (
                                    <button
                                      key={oIndex}
                                      onClick={() => {
                                        setQuizAnswers(prev => ({ ...prev, [q.id]: oIndex }));
                                        // Auto-advance after a short delay
                                        setTimeout(() => {
                                          if (currentQuizQuestion < currentCategory.quiz.questions.length - 1) {
                                            setCurrentQuizQuestion(prev => prev + 1);
                                          }
                                        }, 300);
                                      }}
                                      className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-all ${
                                        isSelected
                                          ? "border-[#fe395f] bg-[#fef2f4]"
                                          : "border-gray-200 hover:border-[#fe395f] hover:bg-[#fef2f4]"
                                      }`}
                                    >
                                      <span className="font-bold text-[#fe395f] mr-2 sm:mr-3">
                                        {String.fromCharCode(65 + oIndex)}.
                                      </span>
                                      <span className="text-sm sm:text-base text-gray-800">{option}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          );
                        })()}

                        {/* Navigation */}
                        <div className="flex justify-between items-center pt-3 sm:pt-4">
                          <Button
                            variant="ghost"
                            onClick={() => setCurrentQuizQuestion(prev => Math.max(0, prev - 1))}
                            disabled={currentQuizQuestion === 0}
                            className="text-[#606b7c] disabled:opacity-30 text-sm sm:text-base px-2 sm:px-4"
                          >
                            <span className="hidden sm:inline">← Previous</span>
                            <span className="sm:hidden">←</span>
                          </Button>

                          {currentQuizQuestion === currentCategory.quiz.questions.length - 1 ? (
                            <Button
                              onClick={() => handleQuizSubmit(urlCategory, currentCategory.quiz.questions)}
                              disabled={quizSubmitting || !currentCategory.quiz.questions[currentQuizQuestion] || quizAnswers[currentCategory.quiz.questions[currentQuizQuestion].id] === undefined}
                              className="bg-[#fe395f] text-white hover:bg-[#e62e4f] disabled:opacity-50 text-sm sm:text-base"
                            >
                              {quizSubmitting ? 'Submitting...' : 'Submit Quiz'}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => setCurrentQuizQuestion(prev => prev + 1)}
                              disabled={!currentCategory.quiz.questions[currentQuizQuestion] || quizAnswers[currentCategory.quiz.questions[currentQuizQuestion].id] === undefined}
                              className="bg-[#fe395f] text-white hover:bg-[#e62e4f] disabled:opacity-50 text-sm sm:text-base"
                            >
                              <span className="hidden sm:inline">Next →</span>
                              <span className="sm:hidden">→</span>
                            </Button>
                          )}
                        </div>

                        <Button
                          variant="ghost"
                          onClick={() => setShowQuiz(false)}
                          className="w-full text-[#606b7c] mt-1 sm:mt-2 text-sm sm:text-base"
                        >
                          Cancel Quiz
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                        {(() => {
                          // Use API results if available, otherwise calculate locally
                          const apiResult = quizResults[urlCategory];
                          const localScore = currentCategory.quiz.questions.filter(q => quizAnswers[q.id] === q.correct).length;
                          const score = apiResult?.score ?? localScore;
                          const maxScore = apiResult?.maxScore ?? currentCategory.quiz.questions.length;
                          const passed = apiResult?.passed ?? (score >= maxScore * 0.8);
                          const percentage = apiResult?.percentage ?? Math.round((score / maxScore) * 100);

                          return (
                            <div className="text-center">
                              {/* Score display */}
                              <div className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full mb-3 sm:mb-4 ${
                                passed ? 'bg-green-100' : 'bg-amber-100'
                              }`}>
                                {passed ? (
                                  <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
                                ) : (
                                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-amber-600" />
                                )}
                              </div>

                              <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                                {score} / {maxScore}
                              </h4>
                              <p className="text-base sm:text-lg text-[#606b7c] mb-2">
                                {percentage}% correct
                              </p>

                              {passed ? (
                                <div className="mb-4 sm:mb-6">
                                  <p className="text-base sm:text-lg text-green-600 font-semibold mb-2">
                                    🎉 Congratulations! You passed!
                                  </p>
                                  <p className="text-sm sm:text-base text-[#606b7c]">
                                    You've mastered the {currentCategory.title.toLowerCase()} fundamentals.
                                  </p>
                                </div>
                              ) : (
                                <div className="mb-4 sm:mb-6">
                                  <p className="text-base sm:text-lg text-amber-600 font-semibold mb-2">
                                    Keep learning! You need 80% to pass.
                                  </p>
                                  <p className="text-sm sm:text-base text-[#606b7c]">
                                    Review the modules and try again. You can retake the quiz as many times as you need.
                                  </p>
                                </div>
                              )}

                              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                                <Button
                                  onClick={() => {
                                    setShowQuiz(false);
                                    setQuizAnswers({});
                                    setQuizSubmitted(false);
                                    setCurrentQuizQuestion(0);
                                  }}
                                  variant={passed ? "outline" : "default"}
                                  className={`text-sm sm:text-base ${passed ? "border-gray-300" : "bg-[#fe395f] text-white hover:bg-[#e62e4f]"}`}
                                >
                                  {passed ? 'Retake Quiz' : 'Try Again'}
                                </Button>
                                {passed && (
                                  <Button
                                    onClick={() => {
                                      setShowQuiz(false);
                                      setQuizAnswers({});
                                      setQuizSubmitted(false);
                                      setCurrentQuizQuestion(0);
                                      navigateToHome();
                                    }}
                                    className="bg-[#fe395f] text-white hover:bg-[#e62e4f] text-sm sm:text-base"
                                  >
                                    Continue Learning
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                        )}
                      </Card>
                    );
                  })()}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* PAGE-STYLE MODULE - Full page layout (Resources, etc.) */}
          {page === "module" && currentModule && currentModule.displayType === "page" && (
            <motion.div
              key="page-module"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <Button
                variant="ghost"
                onClick={() => navigateToCategory(urlCategory)}
                className="mb-6 text-[#606b7c] hover:text-[#fe395f]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lessons
              </Button>

              {/* Full-width page content with gray background */}
              <div className="bg-[#2a2a2a] rounded-xl p-8 md:p-12">
                {/* Page title */}
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-12">
                  {currentModule.title}
                </h1>

                {/* Cover image */}
                {currentModule.coverImage && (
                  <div className="mb-12">
                    <img
                      src={currentModule.coverImage}
                      alt={currentModule.title}
                      className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                      onClick={() => openLightbox(currentModule.coverImage)}
                    />
                  </div>
                )}

                {/* Render all sections */}
                {currentModule.sections && currentModule.sections.map((section, sectionIndex) => (
                  <div key={section.id} className="mb-16 last:mb-0">
                    {/* Divider line above film entries */}
                    {section.content && (section.galleryImages || section.imageAboveContent) && !section.id?.startsWith('photographers') && (
                      <div className="h-px bg-[#4a4a4a] mb-8"></div>
                    )}
                    {/* Section title - only for non-film sections */}
                    {section.title && !(section.content && (section.galleryImages || section.imageAboveContent) && !section.id?.startsWith('photographers')) && (
                      <h2 className={`text-2xl md:text-3xl font-bold mb-8 ${section.id === 'books' ? 'text-[#fe395f]' : 'text-white'}`}>
                        {section.title}
                      </h2>
                    )}

                    {/* Hero image with title below */}
                    {section.galleryImages && section.galleryImages.length > 0 && !section.content && (
                      <div className="mb-8">
                        {section.galleryImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.src}
                            alt={img.alt || ""}
                            className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy"
                            onClick={() => openLightbox(img.src)}
                          />
                        ))}
                        {/* Title below image */}
                        {section.titleBelowImage && (
                          <h2 className="text-2xl md:text-3xl font-bold text-white mt-8">
                            {section.titleBelowImage}
                          </h2>
                        )}
                      </div>
                    )}

                    {/* Film entry with image and text - alternating layout */}
                    {section.content && (section.galleryImages || section.imageAboveContent) && !section.id?.startsWith('photographers') && (
                      <div className={`grid md:grid-cols-2 gap-8 items-start ${
                        sectionIndex % 2 === 0 ? '' : 'md:[&>*:first-child]:order-2'
                      }`}>
                        {/* Text column with title */}
                        <div className="text-white">
                          {/* Film title inline with top of image */}
                          {section.title && (
                            <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                              {section.title}
                            </h3>
                          )}
                          <div className="leading-relaxed">
                            {section.content.split('\n\n').map((paragraph, pIdx) => (
                              <p key={pIdx} className="mb-4 last:mb-0">
                                {paragraph.split('\n').map((line, idx) => (
                                  <span key={idx}>
                                    {parseBoldText(line)}
                                    {idx < paragraph.split('\n').length - 1 && <br />}
                                  </span>
                                ))}
                              </p>
                            ))}
                          </div>
                        </div>
                        {/* Image column */}
                        <div>
                          {section.imageAboveContent && (
                            <img
                              src={section.imageAboveContent}
                              alt={section.imageAboveContentAlt || ""}
                              className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                              onClick={() => openLightbox(section.imageAboveContent)}
                            />
                          )}
                          {section.galleryImages && section.galleryImages.map((img, idx) => (
                            <img
                              key={idx}
                              src={img.src}
                              alt={img.alt || ""}
                              className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                              onClick={() => openLightbox(img.src)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Text-only section (like Books) */}
                    {section.content && !section.galleryImages && !section.imageAboveContent && !section.externalLinks && (
                      <div className={`text-white leading-relaxed ${section.id === 'books' ? '[&_strong]:text-[#fe395f]' : ''}`}>
                        {section.content.split('\n\n').map((paragraph, pIdx) => (
                          <p key={pIdx} className="mb-4 last:mb-0">
                            {paragraph.split('\n').map((line, idx) => {
                              // Handle ### headings
                              if (line.startsWith('### ')) {
                                return (
                                  <span key={idx} className="block text-xl font-bold text-white mt-8 mb-4">
                                    {parseBoldText(line.slice(4))}
                                  </span>
                                );
                              }
                              // Handle bullet points
                              if (line.startsWith('• ')) {
                                return (
                                  <span key={idx} className="block mb-2">
                                    {parseBoldText(line)}
                                  </span>
                                );
                              }
                              return (
                                <span key={idx}>
                                  {parseBoldText(line)}
                                  {idx < paragraph.split('\n').length - 1 && <br />}
                                </span>
                              );
                            })}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* External links grid */}
                    {section.externalLinks && section.externalLinks.length > 0 && (
                      <div className="grid gap-6 md:grid-cols-2">
                        {section.externalLinks.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group"
                          >
                            <div className="rounded-xl overflow-hidden bg-[#3a3a3a] hover:bg-[#4a4a4a] transition-colors">
                              {link.image && (
                                <img
                                  src={link.image}
                                  alt={link.title}
                                  className="w-full h-48 object-cover"
                                  loading="lazy"
                                />
                              )}
                              <div className="p-4">
                                <p className="font-medium text-white group-hover:text-[#fe395f] transition-colors">
                                  {link.title}
                                </p>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Photographers section - two column layout */}
                    {section.id === "photographers" && section.content && (
                      <>
                        {/* Two column grid for photographer lists */}
                        <div className="grid md:grid-cols-2 gap-8 text-white">
                          {section.content.split('### ').filter(Boolean).map((group, gIdx) => {
                            const lines = group.trim().split('\n');
                            const heading = lines[0];
                            const items = lines.slice(1).filter(line => line.startsWith('• '));
                            return (
                              <div key={gIdx}>
                                <h3 className="text-xl font-bold text-white mb-4">{heading}</h3>
                                <ul className="space-y-2">
                                  {items.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                      <span>•</span>
                                      <span>{parseBoldText(item.slice(2))}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                        {/* Gallery images */}
                        {section.galleryImages && section.galleryImages.length > 0 && (
                          <div className="mt-8">
                            {section.galleryImages.map((img, idx) => (
                              <img
                                key={idx}
                                src={img.src}
                                alt={img.alt || ""}
                                className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                                loading="lazy"
                                onClick={() => openLightbox(img.src)}
                              />
                            ))}
                          </div>
                        )}
                        {/* Closing image - full width */}
                        {section.closingImage && (
                          <div className="mt-12">
                            <img
                              src={section.closingImage}
                              alt="Closing"
                              className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                              onClick={() => openLightbox(section.closingImage)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* MODULE PAGE - Lesson Content */}
          {page === "module" && currentModule && currentModule.displayType !== "page" && (
            <motion.div
              key="module"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-3xl mx-auto"
            >
              <Button
                variant="ghost"
                onClick={() => navigateToCategory(urlCategory)}
                className="mb-4 sm:mb-6 text-[#606b7c] hover:text-[#fe395f] text-sm sm:text-base"
              >
                <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> Back to Lessons
              </Button>

              {/* Module Header Card */}
              <Card className="border-gray-200 mb-4 sm:mb-6">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  {completed[currentModule.id] && (
                    <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-[#fef2f4] text-[#fe395f] px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Completed
                    </div>
                  )}
                  {currentModule.estimatedMinutes && (
                    <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gray-100 text-[#606b7c] px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {currentModule.estimatedMinutes} min read
                    </div>
                  )}
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {currentModule.title}
                </h2>

                {/* Hook message and Header Image */}
                {currentModule.headerLayout === "side-by-side" ? (
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6 items-start">
                    {/* Left: Hook quote - callout or blockquote style */}
                    {currentModule.hookStyle === "callout" ? (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-[#2a2a2a] rounded-xl px-4 sm:px-5 py-4 sm:py-6"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base sm:text-lg">📸</span>
                          <h3 className="text-white font-bold text-xs sm:text-sm">{currentModule.title}</h3>
                        </div>
                        {typeof currentModule.hook === 'object' && currentModule.hook?.title ? (
                          <div className="text-[#d4a574] text-xs sm:text-sm">
                            <p className="font-bold">{currentModule.hook.title}</p>
                            <p className="italic">{currentModule.hook.subtitle}</p>
                          </div>
                        ) : (
                          <p className="text-[#d4a574] italic text-xs sm:text-sm">
                            {typeof currentModule.hook === 'string' ? currentModule.hook : currentModule.hook?.quote}
                          </p>
                        )}
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="border-l-4 border-[#fe395f] pl-3 sm:pl-4"
                      >
                        <p className="text-base sm:text-lg text-black italic">
                          {typeof currentModule.hook === 'string' ? currentModule.hook : currentModule.hook?.quote}
                        </p>
                      </motion.div>
                    )}
                    {/* Right: Image */}
                    {currentModule.headerImage && (
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                      >
                        <img
                          src={currentModule.headerImage.src}
                          alt={currentModule.headerImage.alt || ""}
                          className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                          loading="lazy"
                          onClick={() => openLightbox(currentModule.headerImage.src)}
                        />
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Default layout: Hook then full-width image */}
                    {currentModule.hook && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border-l-4 border-[#fe395f] pl-4 mb-4"
                      >
                        {typeof currentModule.hook === 'string' ? (
                          <p className="text-lg text-black italic">{currentModule.hook}</p>
                        ) : (
                          <>
                            <p className="text-lg text-black font-semibold">{currentModule.hook.quote}</p>
                            {currentModule.hook.subtext && (
                              <p className="text-base text-black italic mt-1">{currentModule.hook.subtext}</p>
                            )}
                          </>
                        )}
                      </motion.div>
                    )}

                    {/* Header Image - displayed before sections */}
                    {currentModule.headerImage && (
                      <div className="mb-4">
                        <img
                          src={currentModule.headerImage.src}
                          alt={currentModule.headerImage.alt || ""}
                          className="w-full rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                          loading="lazy"
                          onClick={() => openLightbox(currentModule.headerImage.src)}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Section progress with step indicator */}
                {currentModule.sections && (
                  <div className="space-y-3">
                    {/* Step indicator dots */}
                    <div className="flex items-center justify-center gap-2">
                      {currentModule.sections.map((section, index) => {
                        const isCompleted = sectionProgress[`${currentModule.id}:${section.id}`];
                        const isCurrent = !isCompleted && currentModule.sections.slice(0, index).every(
                          (s) => sectionProgress[`${currentModule.id}:${s.id}`]
                        );
                        return (
                          <div
                            key={section.id}
                            className={`w-3 h-3 rounded-full transition-all ${
                              isCompleted
                                ? "bg-[#fe395f]"
                                : isCurrent
                                  ? "bg-[#fe395f]/50 ring-2 ring-[#fe395f] ring-offset-2"
                                  : "bg-gray-300"
                            }`}
                          />
                        );
                      })}
                    </div>

                    {/* Progress text */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#606b7c]">
                        Step {Math.min(getSectionProgress(currentModule).completed + 1, getSectionProgress(currentModule).total)} of {getSectionProgress(currentModule).total}
                      </span>
                      <span className="text-sm font-bold text-[#fe395f]">
                        {Math.round((getSectionProgress(currentModule).completed / getSectionProgress(currentModule).total) * 100)}%
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#fe395f] to-[#ff6b89]"
                        initial={{ width: 0 }}
                        animate={{
                          width: `${(getSectionProgress(currentModule).completed / getSectionProgress(currentModule).total) * 100}%`
                        }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  </div>
                )}
              </Card>

              {/* Confetti celebration */}
              <Confetti show={showConfetti} />

              {/* Sections-based content */}
              {currentModule.sections ? (
                <div className="space-y-4">
                  {currentModule.sections.map((section, index) => {
                    const sectionKey = `${currentModule.id}:${section.id}`;
                    const isCompleted = sectionProgress[sectionKey];

                    // Sequential unlock: section is locked if any previous section is incomplete
                    const isLocked = index > 0 && currentModule.sections.slice(0, index).some(
                      (s) => !sectionProgress[`${currentModule.id}:${s.id}`]
                    );

                    // Current section: first incomplete section that's not locked
                    const isCurrent = !isCompleted && !isLocked;

                    // Show transition if this was the last completed section
                    const showTransition = lastCompletedSection === sectionKey && section.transitionTo;

                    // Pass proTip only to the last section
                    const isLastSection = index === currentModule.sections.length - 1;

                    return (
                      <div key={section.id} id={`section-${section.id}`}>
                        <SectionCard
                          section={section}
                          isCompleted={isCompleted}
                          isExpanded={expandedSections[sectionKey]}
                          onComplete={() => markSectionComplete(currentModule.id, section.id)}
                          onToggle={() => toggleSection(currentModule.id, section.id)}
                          isLocked={isLocked}
                          isCurrent={isCurrent}
                          stepNumber={index + 1}
                          totalSteps={currentModule.sections.length}
                          showTransition={showTransition}
                          onImageClick={openLightbox}
                          proTip={isLastSection ? currentModule.proTip : null}
                        />
                      </div>
                    );
                  })}

                  {/* Reference Images Section */}
                  {currentModule.referenceImages && currentModule.referenceImages.length > 0 && (
                    <div className="mt-8 bg-[#2a2a2a] rounded-xl p-6">
                      <div className={`grid gap-4 ${
                          currentModule.referenceColumns === 6 ? 'md:grid-cols-6' :
                          currentModule.referenceColumns === 4 ? 'md:grid-cols-4' :
                          currentModule.referenceColumns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                        }`}>
                        {currentModule.referenceImages.map((img, idx) => (
                          <div key={idx} className={
                            img.span === 6 ? 'md:col-span-6' :
                            img.span === 3 ? 'md:col-span-3' :
                            img.span === 2 ? 'md:col-span-2' : ''
                          }>
                            <img
                              src={img.src}
                              alt={img.alt || ""}
                              className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                              onClick={() => openLightbox(img.src)}
                            />
                            {img.caption && (
                              <p className="text-sm text-white mt-2 text-center">
                                {parseBoldText(img.caption)}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Complete Lesson Card - shows when all sections done but module not complete */}
                      {!completed[currentModule.id] && currentModule.sections && currentModule.sections.every(
                        (s) => sectionProgress[`${currentModule.id}:${s.id}`]
                      ) && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="mt-6 bg-gradient-to-r from-[#fe395f] to-[#ff6b89] rounded-xl p-8 text-center text-white shadow-xl"
                        >
                          <Sparkles className="w-16 h-16 mx-auto mb-4" />
                          <h3 className="text-2xl font-bold mb-2">Ready to Complete!</h3>
                          <p className="opacity-90 text-lg mb-4">
                            You've finished all the content. Click below to complete this lesson.
                          </p>
                          <Button
                            variant="outline"
                            onClick={() => {
                              markComplete(currentModule.id);
                              setShowConfetti(true);
                              setTimeout(() => setShowConfetti(false), 3000);
                            }}
                            className="bg-white/20 border-white/40 text-white hover:bg-white/30"
                          >
                            Complete Lesson
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Completion message */}
                  {completed[currentModule.id] && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-gradient-to-r from-[#fe395f] to-[#ff6b89] rounded-xl p-8 text-center text-white shadow-xl"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      >
                        <Sparkles className="w-16 h-16 mx-auto mb-4" />
                      </motion.div>
                      <h3 className="text-2xl font-bold mb-2">Lesson Complete!</h3>
                      <p className="opacity-90 text-lg mb-4">
                        {currentModule.completionMessage || `You've completed ${currentModule.title}. Great work!`}
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => navigateToNextModule(urlCategory, currentModule.id)}
                        className="bg-white/20 border-white/40 text-white hover:bg-white/30"
                      >
                        Continue to Next Lesson
                      </Button>
                    </motion.div>
                  )}
                </div>
              ) : (
                /* Legacy content for modules without sections */
                <Card className="border-gray-200">
                  <div className="mb-6">
                    <ContentRenderer
                      content={currentModule.content}
                      images={currentModule.images}
                      onImageClick={openLightbox}
                    />
                  </div>

                  <Button
                    onClick={() => markComplete(currentModule.id)}
                    disabled={completed[currentModule.id]}
                    className={`w-full py-3 text-lg ${
                      completed[currentModule.id]
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-[#fe395f] hover:bg-[#fe395f]/90"
                    }`}
                  >
                    {completed[currentModule.id] ? "✓ Completed" : "Mark as Complete"}
                  </Button>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-12">
        <div className="border-t border-gray-200 pt-8 text-center text-sm text-[#606b7c]">
          <p>
            <strong className="text-gray-900">Photography Fundamentals Training</strong> — Love & Photos
          </p>
          <p className="mt-2">
            Master the skills to deliver exceptional wedding photography experiences
          </p>
        </div>
      </footer>
    </div>
  );
}
